import { MerchantApiVisibilitySwitch } from '../components/admin/MerchantApiVisibilitySwitch';
import { BankStatements } from '../components/BankStatements';
import { MerchantApiKeysDownload } from '../components/admin/MerchantApiKeysDownload';
import { MerchantAmountAdjustments, AmountAdjustmentRules, validateAmountAdjustments } from '../components/admin/MerchantAmountAdjustments';
import { TraderSettlementPanel } from '../components/admin/TraderSettlementPanel';
import { CustomerAntifraudPanel } from '../components/admin/CustomerAntifraudPanel';
import { MerchantAdvancePanel } from '../components/admin/MerchantAdvancePanel';
import { MerchantDirectionSwitches, MerchantDirectionField } from '../components/admin/MerchantDirectionSwitches';
import { TraderDepositsPanel } from '../components/admin/TraderDepositsPanel';
import { AdminDeviceEvents } from '../components/admin/AdminDeviceEvents';
import { WithdrawalTransferPanel } from '../components/admin/WithdrawalTransferPanel';
import { ThemeToggle } from '../components/ThemeToggle';
import { bankDisplayName } from '../lib/kgsBanks';
import { RecalculationPanel } from '../components/admin/RecalculationPanel';
import { adminTransactionExportRow, adminTransactionExportColumns, formatAmount, formatUTCDateTime, getMerchantTransactionId, getTransactionRequisiteType, formatRequisites } from '../lib/adminTransactionExport';
import { buildTransactionExportSheet } from '../lib/transactionExport';
import { ConstantRatesPanel } from '../components/admin/ConstantRatesPanel';
import { ExternalPlatformStatistics } from '../components/admin/ExternalPlatformStatistics';
import { SettlementPanel } from '../components/admin/SettlementPanel';
import { getMerchantAvailableBalance } from '../lib/merchantAmounts';
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    AdminCurrencyRate,
    adminAPI,
    REQUISITE_TYPE_OPTIONS,
    RequisiteType,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { cabinetPath, cabinetUrl } from '../lib/domains';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Pagination } from '../components/ui/pagination';
import {
    LogOut, Users, ShoppingBag, ShieldCheck, Settings, Save, Globe,
    BarChart3, History, TrendingUp, DollarSign, Download, ArrowUpCircle,
    ArrowDownCircle, UserPlus, Link2, Copy, Clock, CheckCircle, XCircle,
    Loader2, CalendarDays, ArrowRightLeft, RefreshCw, Search, ChevronDown,
    ChevronUp, CreditCard, FileText, Route, ArrowUpDown, KeyRound, LayoutDashboard,
    Plus, Trash2, Wallet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import { useExchangeRate } from '../hooks/useExchangeRate';
import { useAdminMerchants } from '../hooks/useAdminMerchants';
import { TelegramAppealChats, TelegramAppealDeliveries } from '../components/admin/TelegramAppealChats';
import { MerchantCurrencySelector } from '../components/admin/MerchantCurrencySelector';
import { MerchantRoutingPanel } from '../components/admin/MerchantRoutingPanel';
import { RoutingTemplatesPanel } from '../components/admin/RoutingTemplatesPanel';
import type { FiatCurrency as MerchantFiatCurrency } from '../lib/typicalV2';

type CommissionTier = {
    min_amount: number;
    max_amount: number | null;
    commission_rate: number;
    max_amount_inclusive?: boolean;
};
type CommissionTierMap = Partial<Record<RequisiteType, CommissionTier[]>>;

const EMPTY_TIERS: CommissionTier[] = [];

const EXTERNAL_ADAPTER_REQUISITE_TYPES: Record<string, RequisiteType[]> = {
    noros: ['kzt_transgran_gel', 'kzt_transgran_tjs'],
    sarex: ['kzt_transgran_gel', 'kzt_transgran_tjs'],
    flintpays: ['card', 'sbp', 'mobile_kgs'],
    youpay: ['qr_vietnam_sber'],
    youpay_kgs: ['sbp_kgs', 'card', 'elqr'],
    fynzah: ['qr_vietnam_sber', 'qr_vietnam_vtb'],
    gotham: ['qr_vietnam_sber', 'qr_vietnam_vtb', 'psb_ecom'],
    bnpay: ['mobile_tjs', 'kzt_transgran_gel', 'kzt_transgran_tjs'],
    swpay_rub: ['rub_transgran_uzs_emission_tbank', 'rub_transgran_uzs_emission_vtb', 'rub_transgran_uzs_emission_sber'],
};

const SWPAY_REQUISITE_TYPES_BY_CURRENCY: Record<'RUB' | 'KGS' | 'KZT', RequisiteType[]> = {
    RUB: ['transgran_card_rub_arm', 'transgran_mobile_rub_arm'],
    KGS: ['card', 'sbp_kgs'],
    KZT: [],
};

const FYNZAH_PRODUCTION_BASE_URL = 'https://paayzen.com';

const externalAdapterRequisiteTypes = (
    adapterType: string,
    fiatCurrency: 'RUB' | 'KGS' | 'KZT' = 'RUB',
): RequisiteType[] => {
    if (adapterType === 'bnpay') return fiatCurrency === 'KZT' ? ['kzt_transgran_gel', 'kzt_transgran_tjs'] : fiatCurrency === 'RUB' ? ['mobile_tjs'] : [];
    if (adapterType === 'swpay') return SWPAY_REQUISITE_TYPES_BY_CURRENCY[fiatCurrency];
    return EXTERNAL_ADAPTER_REQUISITE_TYPES[adapterType]
        || REQUISITE_TYPE_OPTIONS.filter((item) => item.value !== 'elqr').map((item) => item.value);
};

const withSWPayPaymentMapping = <T extends Record<string, string>>(
    mapping: T,
    fiatCurrency: 'RUB' | 'KGS' | 'KZT',
): T => (fiatCurrency === 'KGS'
    ? { ...mapping, card: 'Card', sbp_kgs: 'ruFpsPhone' }
    : { ...mapping, transgran_card_rub_arm: 'Card', transgran_mobile_rub_arm: 'ruFpsPhone' }) as T;

const traderCommissionMethods = (
    fiatCurrency: MerchantFiatCurrency,
    operation: 'deposit' | 'withdraw',
): RequisiteType[] => {
    if (fiatCurrency === 'KZT') return [];
    if (fiatCurrency === 'KGS' && operation === 'deposit') {
        return ['card', 'sbp', 'elqr'];
    }
    return ['card', 'sbp'];
};

const traderCommissionMethodLabel = (method: RequisiteType, fiatCurrency: MerchantFiatCurrency) => {
    if (method === 'sbp' && fiatCurrency === 'KGS') return 'Перевод по телефону (KGS)';
    return REQUISITE_TYPE_OPTIONS.find((item) => item.value === method)?.label || method;
};

const normalizeCommissionTiers = (tiers?: CommissionTier[]): CommissionTier[] =>
    (tiers || []).map(tier => ({
        min_amount: Number(tier.min_amount),
        max_amount: tier.max_amount === null || tier.max_amount === undefined ? null : Number(tier.max_amount),
        commission_rate: Number(tier.commission_rate),
        max_amount_inclusive: Boolean(tier.max_amount_inclusive),
    }));

const normalizeCommissionTierMap = (value?: CommissionTierMap): CommissionTierMap =>
    Object.fromEntries(
        Object.entries(value || {}).map(([method, tiers]) => [
            method,
            normalizeCommissionTiers(tiers),
        ])
    ) as CommissionTierMap;

const validatePartialCommissionTiers = (tiers?: CommissionTier[], prefix = ''): string | null => {
    const normalized = normalizeCommissionTiers(tiers).sort((a, b) => a.min_amount - b.min_amount);
    let previous: CommissionTier | undefined;
    for (let index = 0; index < normalized.length; index += 1) {
        const tier = normalized[index];
        if (!Number.isFinite(tier.min_amount) || !Number.isFinite(tier.commission_rate)) return `${prefix}Заполните все значения диапазонов`;
        if (tier.commission_rate < 0 || tier.commission_rate > 100) return `${prefix}Комиссия должна быть от 0 до 100`;
        if (tier.max_amount !== null && tier.min_amount >= tier.max_amount) return `${prefix}Начало диапазона должно быть меньше конца`;
        if (tier.max_amount === null && index !== normalized.length - 1) return `${prefix}∞ может быть только у последнего диапазона`;
        if (previous) {
            const overlaps = previous.max_amount === null
                || tier.min_amount < previous.max_amount
                || (tier.min_amount === previous.max_amount && previous.max_amount_inclusive);
            if (overlaps) return `${prefix}Диапазоны не должны пересекаться`;
        }
        previous = tier;
    }
    return null;
};

const validateCommissionTierMap = (value?: CommissionTierMap): string | null => {
    for (const [method, tiers] of Object.entries(value || {})) {
        const error = validatePartialCommissionTiers(tiers, `${method}: `);
        if (error) return error;
    }
    return null;
};

const validateMethodCommissionTiersWithinAmountRange = (
    value: CommissionTierMap | undefined,
    minAmount: number | null,
    maxAmount: number | null,
    operationLabel: string,
): string | null => {
    if (minAmount === null || maxAmount === null) return null;
    for (const [method, tiers] of Object.entries(value || {})) {
        for (const tier of normalizeCommissionTiers(tiers)) {
            if (tier.min_amount < minAmount || tier.max_amount === null || tier.max_amount > maxAmount) {
                return `${method}: диапазон комиссии должен быть внутри лимитов ${operationLabel} ${minAmount}–${maxAmount}`;
            }
        }
    }
    return null;
};

const validateCommissionTiers = (tiers?: CommissionTier[]): string | null => {
    const normalized = normalizeCommissionTiers(tiers);
    if (normalized.length === 0) return null;
    if (normalized[0].min_amount !== 0) return 'Первый диапазон должен начинаться с 0';
    for (let index = 0; index < normalized.length; index += 1) {
        const tier = normalized[index];
        if (!Number.isFinite(tier.min_amount) || !Number.isFinite(tier.commission_rate)) return 'Заполните все значения диапазонов';
        if (tier.commission_rate < 0 || tier.commission_rate > 100) return 'Комиссия должна быть от 0 до 100';
        if (index > 0 && tier.min_amount !== normalized[index - 1].max_amount) return 'Диапазоны должны идти без разрывов';
        if (tier.max_amount !== null && tier.min_amount >= tier.max_amount) return 'Начало диапазона должно быть меньше конца';
        if (tier.max_amount !== null && tier.max_amount_inclusive) return 'В общей лесенке верхняя граница не включается';
        if (tier.max_amount === null && index !== normalized.length - 1) return '∞ может быть только у последнего диапазона';
    }
    return normalized[normalized.length - 1].max_amount === null ? null : 'Последний диапазон должен быть до ∞';
};

const validateOptionalAmountRange = (
    minAmount: number | null,
    maxAmount: number | null,
    label: string,
): string | null => {
    if ((minAmount === null) !== (maxAmount === null)) return `Лимиты ${label} задаются парой`;
    if (minAmount !== null && maxAmount !== null && minAmount > maxAmount) return `Минимальный лимит ${label} больше максимального`;
    return null;
};

const addCommissionTier = (tiers?: CommissionTier[], partial = false): CommissionTier[] => {
    const normalized = normalizeCommissionTiers(tiers);
    if (normalized.length === 0) {
        return [{ min_amount: 0, max_amount: null, commission_rate: 0, max_amount_inclusive: false }];
    }
    const last = normalized[normalized.length - 1];
    if (last.max_amount === null) {
        const splitAt = last.min_amount + 1000;
        return [
            ...normalized.slice(0, -1),
            { ...last, max_amount: splitAt, max_amount_inclusive: false },
            { min_amount: splitAt, max_amount: null, commission_rate: last.commission_rate, max_amount_inclusive: false },
        ];
    }
    const nextMin = last.max_amount + (partial && last.max_amount_inclusive ? 0.01 : 0);
    return [...normalized, { min_amount: nextMin, max_amount: null, commission_rate: last.commission_rate, max_amount_inclusive: false }];
};

const CommissionTierEditor: React.FC<{
    title: string;
    tiers?: CommissionTier[];
    onChange: (tiers: CommissionTier[]) => void;
    partial?: boolean;
    externalError?: string | null;
}> = ({ title, tiers = EMPTY_TIERS, onChange, partial = false, externalError = null }) => {
    const error = (partial ? validatePartialCommissionTiers(tiers) : validateCommissionTiers(tiers)) || externalError;
    const updateTier = (index: number, patch: Partial<CommissionTier>) => {
        onChange(tiers.map((tier, i) => i === index ? { ...tier, ...patch } : tier));
    };
    return (
        <div className="space-y-3 rounded-md border p-3">
            <div className="flex items-center justify-between gap-3">
                <Label>{title}</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => onChange(addCommissionTier(tiers, partial))}>
                    <Plus className="h-4 w-4 mr-2" /> Добавить диапазон
                </Button>
            </div>
            {tiers.length === 0 ? (
                <div className="text-xs text-slate-500 dark:text-slate-400">
                    {partial ? 'Отдельная комиссия для метода не задана.' : 'Используется базовая комиссия.'}
                </div>
            ) : (
                <div className="space-y-2">
                    {tiers.map((tier, index) => (
                        <div key={index} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 items-end">
                            <div>
                                <Label className="text-xs">От</Label>
                                <Input type="number" min="0" value={tier.min_amount} onChange={(e) => updateTier(index, { min_amount: parseFloat(e.target.value || '0') })} />
                            </div>
                            <div>
                                <Label className="text-xs">До</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    placeholder="∞"
                                    value={tier.max_amount ?? ''}
                                    onChange={(e) => updateTier(index, {
                                        max_amount: e.target.value === '' ? null : parseFloat(e.target.value),
                                        ...(partial ? { max_amount_inclusive: e.target.value !== '' } : {}),
                                    })}
                                />
                                {partial && tier.max_amount !== null && (
                                    <label className="mt-1 flex items-center gap-1 text-[11px] text-slate-500 dark:text-slate-400">
                                        <input
                                            type="checkbox"
                                            checked={Boolean(tier.max_amount_inclusive)}
                                            onChange={(e) => updateTier(index, { max_amount_inclusive: e.target.checked })}
                                        />
                                        Включая максимум
                                    </label>
                                )}
                            </div>
                            <div>
                                <Label className="text-xs">Комиссия %</Label>
                                <Input type="number" min="0" max="100" value={tier.commission_rate} onChange={(e) => updateTier(index, { commission_rate: parseFloat(e.target.value || '0') })} />
                            </div>
                            <Button type="button" variant="outline" size="icon" onClick={() => onChange(tiers.filter((_, i) => i !== index))}>
                                <Trash2 className="h-4 w-4" />
                            </Button>
                        </div>
                    ))}
                </div>
            )}
            {error && <div className="text-xs text-red-600 dark:text-red-300">{error}</div>}
        </div>
    );
};

const MethodCommissionEditors: React.FC<{
    title: string;
    methods: RequisiteType[];
    value?: CommissionTierMap;
    onChange: (value: CommissionTierMap) => void;
    methodLabel?: (method: RequisiteType) => string;
    minAmount?: number | null;
    maxAmount?: number | null;
    operationLabel?: string;
}> = ({ title, methods, value = {}, onChange, methodLabel, minAmount = null, maxAmount = null, operationLabel = '' }) => (
    <div className="space-y-3">
        <Label className="text-sm font-semibold">{title}</Label>
        <p className="text-xs text-slate-500 dark:text-slate-400">
            Задайте только нужные интервалы. За их пределами используется общая или базовая комиссия.
        </p>
        {methods.length === 0 ? (
            <p className="text-xs text-slate-500 dark:text-slate-400">Нет доступных методов.</p>
        ) : methods.map((method) => (
            <CommissionTierEditor
                key={method}
                title={`Метод: ${methodLabel?.(method) || REQUISITE_TYPE_OPTIONS.find((item) => item.value === method)?.label || method}`}
                tiers={value[method] || []}
                onChange={(tiers) => onChange({ ...value, [method]: tiers })}
                partial
                externalError={validateMethodCommissionTiersWithinAmountRange(
                    { [method]: value[method] || [] },
                    minAmount,
                    maxAmount,
                    operationLabel,
                )}
            />
        ))}
    </div>
);

export const AdminDashboardPage: React.FC = () => {
    const { user, logout } = useAdminAuth();
    const navigate = useNavigate();
    const { rate: adminExchangeRate } = useExchangeRate({ autoLoad: true });
    const adminRole = user?.admin_role ?? 'full_access';
    const canCreateInvites = adminRole === 'invite_manager' || adminRole === 'full_access';
    const canManage = adminRole === 'full_access';
    const adminRoleLabel = adminRole === 'viewer'
        ? 'Только просмотр'
        : adminRole === 'invite_manager'
            ? 'Просмотр и приглашения'
            : 'Полный доступ';

    const [globalStats, setGlobalStats] = useState<any>(null);
    const [currencyRates, setCurrencyRates] = useState<AdminCurrencyRate[]>([]);
    const [supportKPI, setSupportKPI] = useState<any[]>([]);
    const [auditLogs, setAuditLogs] = useState<any[]>([]);
    const [logsPagination, setLogsPagination] = useState({ page: 1, total: 0, pages: 1 });

    const [traders, setTraders] = useState<any[]>([]);
    const [tradersPagination, setTradersPagination] = useState({ page: 1, total: 0, pages: 1 });
    const [referralCodes, setReferralCodes] = useState<string[]>([]);
    const [selectedReferralCode, setSelectedReferralCode] = useState<string>('');

    const [externalPlatforms, setExternalPlatforms] = useState<any[]>([]);
    const [externalPlatformsPagination, setExternalPlatformsPagination] = useState({ page: 1, total: 0, pages: 1 });
    const [showExternalPlatformForm, setShowExternalPlatformForm] = useState(false);
    const [externalPlatformForm, setExternalPlatformForm] = useState({
        name: '',
        fiat_currency: 'RUB' as 'RUB' | 'KGS' | 'KZT',
        adapter_type: 'compatible_merchant_api',
        base_url: '',
        platform_token: '',
        webhook_secret: '',
        external_merchant_id: '',
        external_merchant_login: '',
        external_key_id: '',
        payment_type_mapping: { card: 'any_bank', sbp: 'sbp', sbp_kgs: 'sbp', mobile_kgs: 'mobile_commerce_rf', qr_vietnam_sber: 'sber_s', qr_vietnam_vtb: 'vtb_s', transgran_card_rub_arm: 'Card', transgran_mobile_rub_arm: 'ruFpsPhone', elqr: '' },
        debug_payload_capture: false,
        priority: 100,
        is_active: true,
        deposit_enabled: true,
        withdraw_enabled: true,
        min_amount: 1,
        max_amount: 1000000,
        requisite_types: ['card', 'sbp'] as RequisiteType[],
        insurance_balance: 1000,
        trust_deposit: 0,
        deposit_commission_rate: 9,
        withdraw_commission_rate: 4,
        deposit_commission_tiers: [] as CommissionTier[],
        withdraw_commission_tiers: [] as CommissionTier[],
        deposit_commission_tiers_by_requisite_type: {} as CommissionTierMap,
        withdraw_commission_tiers_by_requisite_type: {} as CommissionTierMap,
        rate_source: 'RAPIRA_P2P_API',
        rate_markup_percent: 6,
        webhook_url: '',
        notes: '',
    });

    const [support, setSupport] = useState<any[]>([]);
    const [supportPagination, setSupportPagination] = useState({ page: 1, total: 0, pages: 1 });

    const [platformSettings, setPlatformSettings] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const {
        merchants, setMerchants, pagination: merchantsPagination,
        filters: merchantFilters, setFilters: setMerchantFilters,
        loading: merchantsLoading, error: merchantsError, refresh: refreshMerchants,
    } = useAdminMerchants(activeTab === 'merchants');
    const [merchantSearch, setMerchantSearch] = useState('');

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            const search = merchantSearch.trim();
            setMerchantFilters(current => current.search === search
                ? current
                : { ...current, page: 1, search });
        }, 250);
        return () => window.clearTimeout(timeoutId);
    }, [merchantSearch, setMerchantFilters]);

    const [financeTab, setFinanceTab] = useState('merchants');
    const [settlementTraderId, setSettlementTraderId] = useState<string>();
    const [togglingStatus, setTogglingStatus] = useState<Set<string>>(new Set());
    const [openingTraderDashboard, setOpeningTraderDashboard] = useState(false);
    const openingTraderDashboardRef = useRef(false);
    const [traderDashboardError, setTraderDashboardError] = useState<{ id: string; message: string } | null>(null);
    const [openingMerchantDashboard, setOpeningMerchantDashboard] = useState<string | null>(null);
    const [merchantDashboardError, setMerchantDashboardError] = useState<{ id: string; message: string } | null>(null);

    const [invites, setInvites] = useState<any[]>([]);
    const [invitesPagination, setInvitesPagination] = useState({ page: 1, total: 0, pages: 1 });
    const [showInviteModal, setShowInviteModal] = useState(false);
    const [generatedInviteUrl, setGeneratedInviteUrl] = useState<string | null>(null);
    const [showPasswordResetModal, setShowPasswordResetModal] = useState(false);
    const [generatedPasswordResetUrl, setGeneratedPasswordResetUrl] = useState<string | null>(null);
    const [isCreatingInvite, setIsCreatingInvite] = useState(false);
    const [inviteError, setInviteError] = useState<string | null>(null);
    const [passwordResetRequestsCount, setPasswordResetRequestsCount] = useState(0);
    const [creatingResetLinkId, setCreatingResetLinkId] = useState<string | null>(null);
    const [resettingTwoFactorInviteId, setResettingTwoFactorInviteId] = useState<string | null>(null);

    const [inviteForm, setInviteForm] = useState({
        user_type: 'trader' as 'trader' | 'merchant' | 'support',
        integration_profile: 'current' as 'current' | 'typical_v2_2',
        fiat_currency: 'RUB' as MerchantFiatCurrency,
        email: '',
        username: '',
        deposit_commission_rate: 9,
        withdraw_commission_rate: 4,
        insurance_balance: 1000,
        trust_deposit: 0,
        referral_code: '',
        appeal_lifetime_minutes: 180,
        merchant_deposit_commission_rate: 10,
        merchant_withdraw_commission_rate: 10,
        deposit_commission_tiers: [] as CommissionTier[],
        withdraw_commission_tiers: [] as CommissionTier[],
        merchant_deposit_commission_tiers: [] as CommissionTier[],
        merchant_withdraw_commission_tiers: [] as CommissionTier[],
        deposit_commission_tiers_by_requisite_type: {} as CommissionTierMap,
        withdraw_commission_tiers_by_requisite_type: {} as CommissionTierMap,
        merchant_deposit_commission_tiers_by_requisite_type: {} as CommissionTierMap,
        merchant_withdraw_commission_tiers_by_requisite_type: {} as CommissionTierMap,
        merchant_deposit_min_amount: null as number | null,
        merchant_deposit_max_amount: null as number | null,
        merchant_withdraw_min_amount: null as number | null,
        merchant_withdraw_max_amount: null as number | null,
        requisite_types: ['card', 'sbp'] as RequisiteType[],
    });
    const [currencyCapabilities, setCurrencyCapabilities] = useState<Record<MerchantFiatCurrency, RequisiteType[]>>({ RUB: [], KGS: [], KZT: [] });
    const inviteAllowsEmptyRequisiteTypes = inviteForm.user_type === 'merchant'
        && inviteForm.integration_profile === 'typical_v2_2'
        && inviteForm.fiat_currency === 'KZT';

    const [transactions, setTransactions] = useState<any[]>([]);
    const [transactionsPagination, setTransactionsPagination] = useState({ page: 1, total: 0, pages: 1 });
    const [transactionsStatusFilter, setTransactionsStatusFilter] = useState('all');
    const [transactionsTypeFilter, setTransactionsTypeFilter] = useState('all');
    const [transactionsMerchantFilter, setTransactionsMerchantFilter] = useState('all');
    const [transactionsProviderTypeFilter, setTransactionsProviderTypeFilter] = useState<'all' | 'trader' | 'external'>('all');
    const [transactionsProviderFilter, setTransactionsProviderFilter] = useState('all');
    const [transactionFilterOptions, setTransactionFilterOptions] = useState<{
        merchants: Array<{ id: string; username: string; company_name?: string | null }>;
        providers: Array<{ id: string; name: string; type: 'trader' | 'external' }>;
    }>({ merchants: [], providers: [] });
    const [transactionFilterOptionsLoaded, setTransactionFilterOptionsLoaded] = useState(false);
    const [transactionsSearchQuery, setTransactionsSearchQuery] = useState('');
    const [debouncedTransactionsSearch, setDebouncedTransactionsSearch] = useState('');
    const [transactionsStartDate, setTransactionsStartDate] = useState('');
    const [transactionsEndDate, setTransactionsEndDate] = useState('');
    const [transactionsRequisiteType, setTransactionsRequisiteType] = useState<RequisiteType | 'all'>('all');
    const [transactionsFiatCurrency, setTransactionsFiatCurrency] = useState<MerchantFiatCurrency | 'all'>('all');
    const [transactionsAmountMin, setTransactionsAmountMin] = useState('');
    const [transactionsAmountMax, setTransactionsAmountMax] = useState('');
    const [transactionsRequisitesSearch, setTransactionsRequisitesSearch] = useState('');
    const [transactionsSortBy, setTransactionsSortBy] = useState('created_at');
    const [transactionsSortOrder, setTransactionsSortOrder] = useState<'asc' | 'desc'>('desc');
    const inviteCommissionError = inviteForm.user_type === 'trader'
        ? validateCommissionTiers(inviteForm.deposit_commission_tiers) || validateCommissionTiers(inviteForm.withdraw_commission_tiers)
        || validateCommissionTierMap(inviteForm.deposit_commission_tiers_by_requisite_type) || validateCommissionTierMap(inviteForm.withdraw_commission_tiers_by_requisite_type)
        : inviteForm.user_type === 'merchant'
            ? validateCommissionTiers(inviteForm.merchant_deposit_commission_tiers) || validateCommissionTiers(inviteForm.merchant_withdraw_commission_tiers)
            || validateCommissionTierMap(inviteForm.merchant_deposit_commission_tiers_by_requisite_type) || validateCommissionTierMap(inviteForm.merchant_withdraw_commission_tiers_by_requisite_type)
            || validateOptionalAmountRange(inviteForm.merchant_deposit_min_amount, inviteForm.merchant_deposit_max_amount, 'пополнения')
            || validateOptionalAmountRange(inviteForm.merchant_withdraw_min_amount, inviteForm.merchant_withdraw_max_amount, 'вывода')
            || validateMethodCommissionTiersWithinAmountRange(inviteForm.merchant_deposit_commission_tiers_by_requisite_type, inviteForm.merchant_deposit_min_amount, inviteForm.merchant_deposit_max_amount, 'пополнения')
            || validateMethodCommissionTiersWithinAmountRange(inviteForm.merchant_withdraw_commission_tiers_by_requisite_type, inviteForm.merchant_withdraw_min_amount, inviteForm.merchant_withdraw_max_amount, 'вывода')
            : null;
    const externalPlatformCommissionError = validateCommissionTiers(externalPlatformForm.deposit_commission_tiers)
        || validateCommissionTiers(externalPlatformForm.withdraw_commission_tiers)
        || validateCommissionTierMap(externalPlatformForm.deposit_commission_tiers_by_requisite_type)
        || validateCommissionTierMap(externalPlatformForm.withdraw_commission_tiers_by_requisite_type);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [transactionsError, setTransactionsError] = useState<string | null>(null);
    const transactionsLoadingRef = useRef(false);
    const transactionsRequestIdRef = useRef(0);
    const [requestedTransactionsPage, setRequestedTransactionsPage] = useState(1);
    const [transactionsPageSize, setTransactionsPageSize] = useState(20);
    const [transactionsExporting, setTransactionsExporting] = useState(false);
    const [transactionsExportProgress, setTransactionsExportProgress] = useState('');
    const [expandedTransactionIds, setExpandedTransactionIds] = useState<Set<string>>(new Set());

    const loadData = async (showFullLoader: boolean) => {
        if (showFullLoader) setIsLoading(true);
        try {
            const [tRes, , pRes, sRes, settingsData, gStats, ratesRes, sKPI, logsRes, invitesRes, refCodes, resetCount] = await Promise.all([
                adminAPI.getTraders(tradersPagination.page, 50, true, selectedReferralCode || undefined),
                refreshMerchants(),
                adminAPI.getExternalPlatforms(externalPlatformsPagination.page),
                adminAPI.getSupportUsers(supportPagination.page),
                adminAPI.getPlatformSettings(),
                adminAPI.getGlobalStats(),
                adminAPI.getCurrencyRates(),
                adminAPI.getSupportKPI(),
                adminAPI.getAuditLogs(logsPagination.page),
                adminAPI.getInvites(invitesPagination.page),
                adminAPI.getReferralCodes(),
                adminAPI.getPasswordResetRequestsCount()
            ]);

            setTraders(tRes.items);
            setTradersPagination({ page: tRes.page, total: tRes.total, pages: tRes.pages });

            setExternalPlatforms(pRes.items);
            setExternalPlatformsPagination({ page: pRes.page, total: pRes.total, pages: pRes.pages });

            setSupport(sRes.items);
            setSupportPagination({ page: sRes.page, total: sRes.total, pages: sRes.pages });

            setPlatformSettings(settingsData);
            setGlobalStats(gStats);
            setCurrencyRates(ratesRes);
            setSupportKPI(sKPI);
            setAuditLogs(logsRes.items);
            setLogsPagination({ page: logsRes.page, total: logsRes.total, pages: logsRes.pages });

            setInvites(invitesRes.items);
            setInvitesPagination({ page: invitesRes.page, total: invitesRes.total, pages: invitesRes.pages });
            setPasswordResetRequestsCount(resetCount.count);

            setReferralCodes(refCodes);
        } catch (error) {
            console.error('Failed to fetch data:', error);
        } finally {
            if (showFullLoader) setIsLoading(false);
        }
    };

    const fetchData = async () => loadData(true);
    useEffect(() => {
        let active = true;
        let pending = false;
        const interval = setInterval(async () => {
            if (pending) return;
            pending = true;
            try {
                const rates = await adminAPI.getCurrencyRates();
                if (active) setCurrencyRates(rates);
            } catch (error) {
                console.error('Failed to refresh currency rates:', error);
            } finally { pending = false; }
        }, 30000);
        return () => { active = false; clearInterval(interval); };
    }, []);

    const refreshData = async () => loadData(false);

    useEffect(() => {
        fetchData();
    }, [tradersPagination.page, externalPlatformsPagination.page, supportPagination.page, logsPagination.page, invitesPagination.page, selectedReferralCode]);

    useEffect(() => {
        if (activeTab !== 'traders') return;
        let active = true;
        let loading = false;
        const interval = setInterval(async () => {
            if (loading) return;
            loading = true;
            try {
                const response = await adminAPI.getTraders(tradersPagination.page, 50, false, selectedReferralCode || undefined);
                if (active) {
                    const earnings = new Map(response.items.map((item: any) => [item._id, item.settings?.today_commission_earned]));
                    setTraders(previous => previous.map(trader => earnings.has(trader._id) ? {
                        ...trader, settings: { ...trader.settings, today_commission_earned: earnings.get(trader._id) }
                    } : trader));
                }
            } catch (error) {
                console.error('Failed to refresh trader earnings:', error);
            } finally { loading = false; }
        }, 10000);
        return () => { active = false; clearInterval(interval); };
    }, [activeTab, tradersPagination.page, selectedReferralCode]);

    // Обработка переключения вкладки "Конверсия"
    useEffect(() => {
        if (activeTab === 'conversion') {
            navigate(cabinetPath('admin', '/conversion'));
        }
    }, [activeTab]);

    const loadTransactions = async () => {
        const requestId = ++transactionsRequestIdRef.current;
        setTransactionsError(null);
        transactionsLoadingRef.current = true;
        setTransactionsLoading(true);
        try {
            const res = await adminAPI.getTransactions(
                requestedTransactionsPage,
                transactionsPageSize,
                transactionsStatusFilter,
                transactionsTypeFilter,
                debouncedTransactionsSearch,
                {
                    merchantId: transactionsMerchantFilter !== 'all' ? transactionsMerchantFilter : undefined,
                    providerType: transactionsProviderTypeFilter !== 'all' ? transactionsProviderTypeFilter : undefined,
                    providerId: transactionsProviderFilter !== 'all'
                        ? transactionsProviderFilter
                        : undefined,
                    startDate: transactionsStartDate,
                    endDate: transactionsEndDate,
                    requisiteType: transactionsRequisiteType !== 'all' ? transactionsRequisiteType : undefined,
                    fiatCurrency: transactionsFiatCurrency !== 'all' ? transactionsFiatCurrency : undefined,
                    amountMin: transactionsAmountMin,
                    amountMax: transactionsAmountMax,
                    requisitesSearch: transactionsRequisitesSearch,
                    sortBy: transactionsSortBy,
                    sortOrder: transactionsSortOrder,
                },
            );
            if (requestId !== transactionsRequestIdRef.current) return;
            setTransactions(res.items);
            setTransactionsPagination({ page: res.page, total: res.total, pages: res.pages });
        } catch (error) {
            if (requestId !== transactionsRequestIdRef.current) return;
            setTransactionsError('Не удалось загрузить транзакции. Попробуйте обновить список.');
            setTransactions([]);
            setTransactionsPagination({ page: 1, total: 0, pages: 1 });
            console.error('Failed to fetch transactions:', error);
        } finally {
            if (requestId === transactionsRequestIdRef.current) {
                transactionsLoadingRef.current = false;
                setTransactionsLoading(false);
            }
        }
    };

    useEffect(() => {
        if (activeTab === 'transactions') {
            loadTransactions();
        }
    }, [
        activeTab,
        requestedTransactionsPage,
        transactionsPageSize,
        transactionsStatusFilter,
        transactionsTypeFilter,
        transactionsMerchantFilter,
        transactionsProviderTypeFilter,
        transactionsProviderFilter,
        debouncedTransactionsSearch,
        transactionsStartDate,
        transactionsEndDate,
        transactionsRequisiteType,
        transactionsFiatCurrency,
        transactionsAmountMin,
        transactionsAmountMax,
        transactionsRequisitesSearch,
        transactionsSortBy,
        transactionsSortOrder,
    ]);

    useEffect(() => {
        if (activeTab !== 'transactions' || transactionFilterOptionsLoaded) return;

        adminAPI.getTransactionFilterOptions()
            .then(setTransactionFilterOptions)
            .then(() => setTransactionFilterOptionsLoaded(true))
            .catch((error) => console.error('Failed to fetch transaction filter options:', error));
    }, [activeTab, transactionFilterOptionsLoaded]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedTransactionsSearch(transactionsSearchQuery.trim());
            setRequestedTransactionsPage(1);
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [transactionsSearchQuery]);

    const handleTransactionsPageChange = (page: number) => {
        if (transactionsLoadingRef.current || page === transactionsPagination.page) return;
        transactionsLoadingRef.current = true;
        setTransactionsLoading(true);
        if (page === requestedTransactionsPage) {
            void loadTransactions();
        } else {
            setRequestedTransactionsPage(page);
        }
    };

    const resetTransactionsPage = () => {
        setRequestedTransactionsPage(1);
    };

    const toggleTransactionDetails = (transactionId: string) => {
        setExpandedTransactionIds(prev => {
            const next = new Set(prev);
            if (next.has(transactionId)) {
                next.delete(transactionId);
            } else {
                next.add(transactionId);
            }
            return next;
        });
    };

    const handleTransactionSort = (sortBy: string) => {
        if (transactionsSortBy === sortBy) {
            setTransactionsSortOrder(order => order === 'asc' ? 'desc' : 'asc');
        } else {
            setTransactionsSortBy(sortBy);
            setTransactionsSortOrder('desc');
        }
        resetTransactionsPage();
    };

    const getTransactionFilters = () => ({
        merchantId: transactionsMerchantFilter !== 'all' ? transactionsMerchantFilter : undefined,
        providerType: transactionsProviderTypeFilter !== 'all' ? transactionsProviderTypeFilter : undefined,
        providerId: transactionsProviderFilter !== 'all'
            ? transactionsProviderFilter
            : undefined,
        startDate: transactionsStartDate,
        endDate: transactionsEndDate,
        requisiteType: transactionsRequisiteType !== 'all' ? transactionsRequisiteType : undefined,
        fiatCurrency: transactionsFiatCurrency !== 'all' ? transactionsFiatCurrency : undefined,
        amountMin: transactionsAmountMin,
        amountMax: transactionsAmountMax,
        requisitesSearch: transactionsRequisitesSearch,
        sortBy: transactionsSortBy,
        sortOrder: transactionsSortOrder,
    });

    const exportTransactionsToExcel = async () => {
        setTransactionsExporting(true);
        setTransactionsExportProgress('Загрузка…');
        try {
            const filters = getTransactionFilters();
            const ws = await buildTransactionExportSheet(
                (page, size) => adminAPI.getTransactions(
                    page,
                    size,
                    transactionsStatusFilter,
                    transactionsTypeFilter,
                    debouncedTransactionsSearch,
                    filters,
                ),
                adminTransactionExportRow,
                (loaded, total) => setTransactionsExportProgress(`${loaded} / ${total}`),
            );
            ws['!cols'] = adminTransactionExportColumns;
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
            XLSX.writeFile(wb, `admin_transactions_${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            console.error('Transactions Excel export failed:', error);
            alert(`Ошибка при экспорте транзакций. ${error instanceof Error ? error.message : 'Попробуйте уменьшить период и повторить выгрузку.'}`);
        } finally {
            setTransactionsExporting(false);
            setTransactionsExportProgress('');
        }
    };

    const toggleStatus = async (type: 'trader' | 'merchant' | 'support' | 'external_platform', id: string, currentStatus: boolean) => {
        if (togglingStatus.has(id)) {
            return;
        }

        setTogglingStatus(prev => new Set(prev).add(id));

        try {
            if (type === 'trader') {
                await adminAPI.updateTrader(id, { is_active: !currentStatus });
            } else if (type === 'merchant') {
                await adminAPI.updateMerchant(id, { is_active: !currentStatus });
            } else if (type === 'external_platform') {
                await adminAPI.updateExternalPlatform(id, { is_active: !currentStatus });
            } else {
                await adminAPI.updateSupport(id, { is_active: !currentStatus });
            }
            await refreshData();
        } catch (error) {
            alert('Ошибка при обновлении статуса');
        } finally {
            setTogglingStatus(prev => {
                const newSet = new Set(prev);
                newSet.delete(id);
                return newSet;
            });
        }
    };

    const updateTraderSettings = async (id: string, settings: any) => {
        try {
            await adminAPI.updateTrader(id, settings);
            alert('Настройки обновлены');
            refreshData();
        } catch (error) {
            alert('Ошибка при обновлении настроек');
        }
    };

    const openTraderSettlements = (traderId: string) => {
        setSettlementTraderId(traderId);
        setFinanceTab('traders');
        setActiveTab('settlement');
    };

    const openTraderDashboard = async (traderId: string) => {
        if (openingTraderDashboardRef.current) return;
        setTraderDashboardError(null);
        // Reserve the tab during the click, before awaiting the token request.
        const popup = window.open('about:blank', '_blank');
        if (!popup) {
            setTraderDashboardError({ id: traderId, message: 'Разрешите открытие новых вкладок и нажмите «Дашборд» ещё раз.' });
            return;
        }
        popup.opener = null;
        openingTraderDashboardRef.current = true;
        setOpeningTraderDashboard(true);
        try {
            const response = await adminAPI.createTraderImpersonationToken(traderId);
            const url = cabinetUrl('trader', `/impersonate?token=${encodeURIComponent(response.access_token)}`);
            if (!popup.closed) popup.location.replace(url);
        } catch (error: any) {
            popup.close();
            const detail = error.response?.data?.detail;
            setTraderDashboardError({ id: traderId, message: typeof detail === 'string' ? detail : 'Не удалось открыть дашборд трейдера.' });
        } finally {
            openingTraderDashboardRef.current = false;
            setOpeningTraderDashboard(false);
        }
    };

    const openMerchantDashboard = async (merchantId: string) => {
        setMerchantDashboardError(null);
        // Open synchronously with the click so browsers do not block an async popup.
        const popup = window.open('about:blank', '_blank');
        if (!popup) {
            setMerchantDashboardError({ id: merchantId, message: 'Разрешите открытие новых вкладок и нажмите «Дашборд» ещё раз.' });
            return;
        }
        popup.opener = null;
        setOpeningMerchantDashboard(merchantId);
        try {
            const response = await adminAPI.createMerchantImpersonationToken(merchantId);
            if (!popup.closed) popup.location.replace(cabinetUrl('merchant', `/impersonate#token=${encodeURIComponent(response.access_token)}`));
        } catch (error: any) {
            popup.close();
            const detail = error.response?.data?.detail;
            setMerchantDashboardError({ id: merchantId, message: typeof detail === 'string' ? detail : 'Не удалось открыть дашборд мерчанта.' });
        } finally {
            setOpeningMerchantDashboard(null);
        }
    };

    const updateMerchantFlag = async (id: string, field: MerchantDirectionField | 'hide_api_keys', enabled: boolean) => {
        await adminAPI.updateMerchant(id, { [field]: enabled });
        setMerchants(current => current.map(merchant => merchant._id === id
            ? { ...merchant, [field]: enabled } : merchant));
    };

    const updateMerchantSettings = async (id: string, settings: any) => {
        try {
            await adminAPI.updateMerchant(id, settings);
            alert('Настройки мерчанта обновлены');
            refreshData();
        } catch (error: any) {
            const detail = error.response?.data?.detail;
            const message = Array.isArray(detail)
                ? detail.map((item: any) => item?.msg || JSON.stringify(item)).join('; ')
                : detail;
            alert(message || 'Ошибка при обновлении настроек мерчанта');
        }
    };

    const createExternalPlatform = async () => {
        try {
            const payload = {
                ...externalPlatformForm,
                deposit_commission_tiers: normalizeCommissionTiers(externalPlatformForm.deposit_commission_tiers),
                withdraw_commission_tiers: normalizeCommissionTiers(externalPlatformForm.withdraw_commission_tiers),
                deposit_commission_tiers_by_requisite_type: normalizeCommissionTierMap(externalPlatformForm.deposit_commission_tiers_by_requisite_type),
                withdraw_commission_tiers_by_requisite_type: normalizeCommissionTierMap(externalPlatformForm.withdraw_commission_tiers_by_requisite_type),
                requisite_types: externalPlatformForm.requisite_types.filter((item) =>
                    externalAdapterRequisiteTypes(externalPlatformForm.adapter_type, externalPlatformForm.fiat_currency).includes(item)
                ),
                name: externalPlatformForm.name.trim(),
                base_url: externalPlatformForm.base_url.trim(),
                platform_token: externalPlatformForm.platform_token.trim() || undefined,
                webhook_secret: externalPlatformForm.webhook_secret.trim() || undefined,
                external_merchant_id: externalPlatformForm.external_merchant_id.trim() || undefined,
                external_merchant_login: externalPlatformForm.external_merchant_login.trim() || undefined,
                external_key_id: externalPlatformForm.external_key_id.trim() || undefined,
                webhook_url: externalPlatformForm.webhook_url.trim() || undefined,
                notes: externalPlatformForm.notes.trim() || undefined,
            };
            await adminAPI.createExternalPlatform(payload);
            setExternalPlatformForm({
                name: '',
                fiat_currency: 'RUB',
                adapter_type: 'compatible_merchant_api',
                base_url: '',
                platform_token: '',
                webhook_secret: '',
                external_merchant_id: '',
                external_merchant_login: '',
                external_key_id: '',
                payment_type_mapping: { card: 'any_bank', sbp: 'sbp', sbp_kgs: 'sbp', mobile_kgs: 'mobile_commerce_rf', qr_vietnam_sber: 'sber_s', qr_vietnam_vtb: 'vtb_s', transgran_card_rub_arm: 'Card', transgran_mobile_rub_arm: 'ruFpsPhone', elqr: '' },
                debug_payload_capture: false,
                priority: 100,
                is_active: true,
                deposit_enabled: true,
                withdraw_enabled: true,
                min_amount: 1,
                max_amount: 1000000,
                requisite_types: ['card', 'sbp'] as RequisiteType[],
                insurance_balance: 1000,
                trust_deposit: 0,
                deposit_commission_rate: 9,
                withdraw_commission_rate: 4,
                deposit_commission_tiers: [],
                withdraw_commission_tiers: [],
                deposit_commission_tiers_by_requisite_type: {},
                withdraw_commission_tiers_by_requisite_type: {},
                rate_source: 'RAPIRA_P2P_API',
                rate_markup_percent: 6,
                webhook_url: '',
                notes: '',
            });
            setShowExternalPlatformForm(false);
            alert('Платформа каскада создана');
            refreshData();
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Ошибка при создании платформы');
        }
    };

    useEffect(() => {
        const currency = inviteForm.fiat_currency;
        Promise.all([
            adminAPI.getExternalPlatformCapabilities('RUB'),
            adminAPI.getExternalPlatformCapabilities('KGS'),
            adminAPI.getExternalPlatformCapabilities('KZT'),
        ])
            .then(([rub, kgs, kzt]) => {
                const capabilities = { RUB: rub.all, KGS: kgs.all, KZT: kzt.all };
                setCurrencyCapabilities(capabilities);
                if (inviteForm.user_type === 'merchant' && inviteForm.integration_profile === 'typical_v2_2') {
                    setInviteForm((current) => ({
                        ...current,
                        requisite_types: current.requisite_types.filter((item) => capabilities[currency].includes(item)),
                    }));
                }
            })
            .catch(() => setCurrencyCapabilities({ RUB: [], KGS: [], KZT: [] }));
    }, [inviteForm.user_type, inviteForm.integration_profile, inviteForm.fiat_currency]);

    const updateExternalPlatformSettings = async (id: string, settings: any) => {
        try {
            await adminAPI.updateExternalPlatform(id, settings);
            alert('Настройки платформы обновлены');
            refreshData();
        } catch (err: any) {
            alert(err.response?.data?.detail || 'Ошибка при обновлении платформы');
        }
    };

    const updatePlatformSettings = async () => {
        try {
            await adminAPI.updatePlatformSettings({
                deposit_fee_usd: platformSettings.deposit_fee_usd,
                merchant_api_requests_per_5_seconds: platformSettings.merchant_api_requests_per_5_seconds,
                merchant_api_request_mixpanel_enabled: platformSettings.merchant_api_request_mixpanel_enabled,
                merchant_api_min_amount: platformSettings.merchant_api_min_amount,
                merchant_api_max_amount: platformSettings.merchant_api_max_amount,
            });
            alert('Глобальные настройки обновлены');
            refreshData();
        } catch (error: any) {
            alert(error.response?.data?.detail || 'Ошибка при обновлении глобальных настроек');
        }
    };

    const platformSettingsValid = platformSettings
        && Number.isFinite(platformSettings.deposit_fee_usd)
        && platformSettings.deposit_fee_usd >= 0
        && Number.isInteger(platformSettings.merchant_api_requests_per_5_seconds)
        && platformSettings.merchant_api_requests_per_5_seconds >= 1
        && Number.isFinite(platformSettings.merchant_api_min_amount)
        && platformSettings.merchant_api_min_amount >= 0
        && Number.isFinite(platformSettings.merchant_api_max_amount)
        && platformSettings.merchant_api_max_amount > 0
        && platformSettings.merchant_api_min_amount <= platformSettings.merchant_api_max_amount;

    const createInvite = async () => {
        setIsCreatingInvite(true);
        setInviteError(null);

        try {
            const data: any = {
                user_type: inviteForm.user_type,
                email: inviteForm.email,
                username: inviteForm.username,
            };

            if (inviteForm.user_type === 'trader') {
                data.fiat_currency = inviteForm.fiat_currency;
                data.deposit_commission_rate = inviteForm.deposit_commission_rate;
                data.withdraw_commission_rate = inviteForm.withdraw_commission_rate;
                data.deposit_commission_tiers = normalizeCommissionTiers(inviteForm.deposit_commission_tiers);
                data.withdraw_commission_tiers = normalizeCommissionTiers(inviteForm.withdraw_commission_tiers);
                data.deposit_commission_tiers_by_requisite_type = normalizeCommissionTierMap(inviteForm.deposit_commission_tiers_by_requisite_type);
                data.withdraw_commission_tiers_by_requisite_type = normalizeCommissionTierMap(inviteForm.withdraw_commission_tiers_by_requisite_type);
                data.insurance_balance = inviteForm.insurance_balance;
                data.trust_deposit = inviteForm.trust_deposit;
                if (inviteForm.referral_code.trim()) {
                    data.referral_code = inviteForm.referral_code.trim();
                }
            } else if (inviteForm.user_type === 'merchant') {
                data.integration_profile = inviteForm.integration_profile;
                data.fiat_currency = inviteForm.integration_profile === 'typical_v2_2' ? inviteForm.fiat_currency : 'RUB';
                data.appeal_lifetime_minutes = inviteForm.appeal_lifetime_minutes;
                data.merchant_deposit_commission_rate = inviteForm.merchant_deposit_commission_rate;
                data.merchant_withdraw_commission_rate = inviteForm.merchant_withdraw_commission_rate;
                data.merchant_deposit_commission_tiers = normalizeCommissionTiers(inviteForm.merchant_deposit_commission_tiers);
                data.merchant_withdraw_commission_tiers = normalizeCommissionTiers(inviteForm.merchant_withdraw_commission_tiers);
                data.merchant_deposit_commission_tiers_by_requisite_type = normalizeCommissionTierMap(inviteForm.merchant_deposit_commission_tiers_by_requisite_type);
                data.merchant_withdraw_commission_tiers_by_requisite_type = normalizeCommissionTierMap(inviteForm.merchant_withdraw_commission_tiers_by_requisite_type);
                data.merchant_deposit_min_amount = inviteForm.merchant_deposit_min_amount;
                data.merchant_deposit_max_amount = inviteForm.merchant_deposit_max_amount;
                data.merchant_withdraw_min_amount = inviteForm.merchant_withdraw_min_amount;
                data.merchant_withdraw_max_amount = inviteForm.merchant_withdraw_max_amount;
                data.requisite_types = inviteForm.requisite_types;
            }

            const result = await adminAPI.createInvite(data);
            setGeneratedInviteUrl(result.invite_url);
            setShowInviteModal(true);

            setInviteForm({
                user_type: 'trader',
                integration_profile: 'current',
                fiat_currency: 'RUB',
                email: '',
                username: '',
                deposit_commission_rate: 9,
                withdraw_commission_rate: 4,
                insurance_balance: 1000,
                trust_deposit: 0,
                referral_code: '',
                appeal_lifetime_minutes: 180,
                merchant_deposit_commission_rate: 10,
                merchant_withdraw_commission_rate: 10,
                deposit_commission_tiers: [],
                withdraw_commission_tiers: [],
                merchant_deposit_commission_tiers: [],
                merchant_withdraw_commission_tiers: [],
                deposit_commission_tiers_by_requisite_type: {},
                withdraw_commission_tiers_by_requisite_type: {},
                merchant_deposit_commission_tiers_by_requisite_type: {},
                merchant_withdraw_commission_tiers_by_requisite_type: {},
                merchant_deposit_min_amount: null,
                merchant_deposit_max_amount: null,
                merchant_withdraw_min_amount: null,
                merchant_withdraw_max_amount: null,
                requisite_types: ['card', 'sbp'],
            });

            refreshData();
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Ошибка при создании приглашения');
        } finally {
            setIsCreatingInvite(false);
        }
    };

    const createPasswordResetLink = async (inviteId: string) => {
        setCreatingResetLinkId(inviteId);
        setInviteError(null);
        const pendingRequests = invites.find((invite) => invite.id === inviteId)?.pending_password_reset_requests || 0;

        try {
            const updatedInvite = await adminAPI.createPasswordResetLink(inviteId);
            setInvites((currentInvites) =>
                currentInvites.map((invite) =>
                    invite.id === inviteId ? updatedInvite : invite
                )
            );
            setPasswordResetRequestsCount((count) =>
                Math.max(0, count - pendingRequests)
            );
            setGeneratedPasswordResetUrl(updatedInvite.password_reset_url || null);
            setShowPasswordResetModal(Boolean(updatedInvite.password_reset_url));
            refreshData();
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Ошибка при создании ссылки сброса пароля');
        } finally {
            setCreatingResetLinkId(null);
        }
    };

    const resetMerchantTwoFactor = async (invite: any) => {
        const confirmed = window.confirm(
            `Сбросить двухфакторную аутентификацию мерчанта ${invite.username}?`
        );
        if (!confirmed) return;

        setResettingTwoFactorInviteId(invite.id);
        setInviteError(null);
        try {
            await adminAPI.resetMerchantTwoFactor(invite.id);
            setInvites((currentInvites) =>
                currentInvites.map((item) =>
                    item.id === invite.id
                        ? { ...item, two_factor_enabled: false }
                        : item
                )
            );
        } catch (err: any) {
            setInviteError(err.response?.data?.detail || 'Ошибка при сбросе 2FA');
        } finally {
            setResettingTwoFactorInviteId(null);
        }
    };

    const copyToClipboard = async (text: string) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
            } else {
                const textarea = document.createElement('textarea');
                textarea.value = text;
                textarea.style.position = 'fixed';
                textarea.style.left = '-9999px';
                document.body.appendChild(textarea);
                textarea.focus();
                textarea.select();
                document.execCommand('copy');
                document.body.removeChild(textarea);
            }
            alert('Ссылка скопирована в буфер обмена');
            return true;
        } catch (error) {
            console.error('Failed to copy link:', error);
            alert('Ссылка создана, но не удалось скопировать её автоматически');
            return false;
        }
    };

    const getInviteStatusBadge = (status: string) => {
        switch (status) {
            case 'pending':
                return <Badge variant="outline" className="bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-300 border-yellow-300 dark:border-yellow-800">Ожидает</Badge>;
            case 'used':
                return <Badge variant="outline" className="bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-300 dark:border-green-800">Использовано</Badge>;
            case 'expired':
                return <Badge variant="outline" className="bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-300 dark:border-red-800">Истекло</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const getUserTypeBadge = (userType: string) => {
        switch (userType) {
            case 'trader':
                return <Badge className="bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300">Трейдер</Badge>;
            case 'merchant':
                return <Badge className="bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300">Мерчант</Badge>;
            case 'support':
                return <Badge className="bg-purple-100 dark:bg-purple-950 text-purple-700 dark:text-purple-300">Саппорт</Badge>;
            default:
                return <Badge>{userType}</Badge>;
        }
    };

    const exportTradersToExcel = () => {
        try {
            const data = traders.map(trader => {
                const settings = trader.settings || {};
                const volumeStats = trader.volume_stats || {};
                return {
                    'ID': trader._id,
                    'Username': trader.username,
                    'Email': trader.email,
                    'Статус': trader.is_active ? 'Активен' : 'Отключен',
                    'Создан': trader.created_at ? new Date(trader.created_at).toLocaleString('ru-RU') : '-',
                    'Баланс USDT': settings.balance?.toFixed(2) || 0,
                    'Страховой баланс': settings.insurance_balance?.toFixed(2) || 0,
                    'Депозит доверия': settings.trust_deposit?.toFixed(2) || 0,
                    'Ком. депозит %': settings.deposit_commission_rate || 0,
                    'Ком. вывод %': settings.withdraw_commission_rate || 0,
                    'Заработано сегодня': settings.today_commission_earned?.toFixed(2) || 0,
                    'Заработано всего': settings.total_commission_earned?.toFixed(2) || 0,
                    'Ввод сегодня (RUB)': volumeStats.deposit_today || 0,
                    'Ввод за месяц (RUB)': volumeStats.deposit_month || 0,
                    'Вывод сегодня (RUB)': volumeStats.withdraw_today || 0,
                    'Вывод за месяц (RUB)': volumeStats.withdraw_month || 0,
                };
            });

            const ws = XLSX.utils.json_to_sheet(data);
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Traders');

            ws['!cols'] = [
                { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 12 },
                { wch: 20 }, { wch: 12 }, { wch: 15 }, { wch: 12 },
                { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 18 },
                { wch: 18 }, { wch: 18 }, { wch: 18 },
            ];

            const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `traders_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Excel export failed:', error);
            alert('Ошибка при экспорте в Excel');
        }
    };

    if (isLoading) {
        return <div className="p-8 text-center">Загрузка...</div>;
    }

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 md:p-8">
            <header className="flex flex-wrap gap-3 justify-between items-center mb-8">
                <div>
                    <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">Админ-панель</h1>
                    <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
                        <span>Добро пожаловать, {user?.username}</span>
                        <Badge variant="outline">{adminRoleLabel}</Badge>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={logout} className="flex gap-2">
                        <LogOut className="h-4 w-4" /> Выйти
                    </Button>
                    <ThemeToggle />
                </div>
            </header>

            <Tabs value={activeTab} onValueChange={(value) => {
                setActiveTab(value);
                if (value === 'conversion') {
                    navigate(cabinetPath('admin', '/conversion'));
                }
            }} className="space-y-4">
                <TabsList className="flex flex-wrap h-auto w-full max-w-7xl">
                    <TabsTrigger value="overview" className="flex gap-2">
                        <BarChart3 className="h-4 w-4" /> Обзор
                    </TabsTrigger>
                    <TabsTrigger value="invites" className="flex gap-2">
                        <UserPlus className="h-4 w-4" /> Приглашения
                        {passwordResetRequestsCount > 0 && (
                            <Badge className="ml-1 bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800">
                                {passwordResetRequestsCount}
                            </Badge>
                        )}
                    </TabsTrigger>
                    <TabsTrigger value="traders" className="flex gap-2">
                        <Users className="h-4 w-4" /> Трейдеры
                    </TabsTrigger>
                    <TabsTrigger value="merchants" className="flex gap-2">
                        <ShoppingBag className="h-4 w-4" /> Мерчанты
                    </TabsTrigger>
                    <TabsTrigger value="routing" className="flex gap-2"><Route className="h-4 w-4" />Распределение</TabsTrigger>
                    <TabsTrigger value="external-platforms" className="flex gap-2">
                        <Route className="h-4 w-4" /> Платформы
                    </TabsTrigger>
                    <TabsTrigger value="transactions" className="flex gap-2">
                        <ArrowRightLeft className="h-4 w-4" /> Транзакции
                    </TabsTrigger>
                    <TabsTrigger value="device-events" className="flex gap-2">
                        <FileText className="h-4 w-4" /> Пуши
                    </TabsTrigger>
                    {canManage && <TabsTrigger value="recalculation" className="flex gap-2">
                        <RefreshCw className="h-4 w-4" /> Перерасчёт
                    </TabsTrigger>}
                    {canManage && <TabsTrigger value="withdrawal-transfer" className="flex gap-2"><ArrowRightLeft className="h-4 w-4" /> Перенос выплат</TabsTrigger>}
                    <TabsTrigger value="trader-deposits" className="flex gap-2"><Wallet className="h-4 w-4" /> Депозиты трейдеров</TabsTrigger>
                    <TabsTrigger value="bank-statements">Банковские выписки</TabsTrigger>
                    <TabsTrigger value="support" className="flex gap-2">
                        <ShieldCheck className="h-4 w-4" /> Саппорт
                    </TabsTrigger>
                    <TabsTrigger value="settlement" className="flex gap-2">
                        <DollarSign className="h-4 w-4" /> Финансы
                    </TabsTrigger>
                    <TabsTrigger value="constant-rates" className="flex gap-2"><ArrowRightLeft className="h-4 w-4" /> Постоянные курсы</TabsTrigger>
                    <TabsTrigger value="settings" className="flex gap-2">
                        <Settings className="h-4 w-4" /> Настройки
                    </TabsTrigger>
                    <TabsTrigger value="logs" className="flex gap-2">
                        <History className="h-4 w-4" /> Логи
                    </TabsTrigger>
                    <TabsTrigger value="conversion" className="flex gap-2">
                        <TrendingUp className="h-4 w-4" /> Конверсия
                    </TabsTrigger>
                    <TabsTrigger value="antifraud" className="flex gap-2">
                        <ShieldCheck className="h-4 w-4" /> Исключения антифрода
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="antifraud"><CustomerAntifraudPanel readOnly={!canManage} /></TabsContent>
                <TabsContent value="bank-statements"><BankStatements audience="admin" canManage={canManage} /></TabsContent>

                <TabsContent value="settlement">
                    <Tabs value={financeTab} onValueChange={setFinanceTab}>
                        <TabsList>
                            <TabsTrigger value="merchants">Мерчанты</TabsTrigger>
                            <TabsTrigger value="platforms">Платформы</TabsTrigger>
                            <TabsTrigger value="traders">Трейдеры</TabsTrigger>
                        </TabsList>
                        <TabsContent value="merchants">
                            <SettlementPanel canManage={canManage} onCompleted={updated => {
                                setMerchants(items => items.map(merchant => merchant._id === updated.id
                                    ? {
                                        ...merchant, settlement_balance: updated.settlement_balance,
                                        available_balance_usdt: updated.available_balance
                                    }
                                    : merchant));
                            }} />
                        </TabsContent>
                        <TabsContent value="platforms">
                            <SettlementPanel kind="platform" canManage={canManage} />
                        </TabsContent>
                        <TabsContent value="traders">
                            <TraderSettlementPanel canManage={canManage} initialTraderId={settlementTraderId} />
                        </TabsContent>
                    </Tabs>
                </TabsContent>
                <TabsContent value="overview">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            title="Общий оборот (USDT)"
                            value={globalStats?.total_volume_usdt?.toFixed(2)}
                            icon={<TrendingUp className="h-5 w-5 text-cyan-500" />}
                            suffix=" USDT"
                        />
                        <StatCard
                            title="Прибыль платформы"
                            value={globalStats?.total_platform_profit_usdt?.toFixed(2)}
                            icon={<DollarSign className="h-5 w-5 text-green-500" />}
                            suffix=" USDT"
                        />
                        <StatCard
                            title="Комиссии трейдеров"
                            value={globalStats?.total_trader_commissions_usdt?.toFixed(2)}
                            icon={<Users className="h-5 w-5 text-indigo-500" />}
                            suffix=" USDT"
                        />
                        <StatCard
                            title="Балансы трейдеров"
                            value={globalStats?.total_trader_balances_usdt?.toFixed(2)}
                            icon={<Users className="h-5 w-5 text-purple-500" />}
                            suffix=" USDT"
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                        <CurrencyRatesCard rates={currencyRates} onRefresh={refreshData} />

                        <Card>
                            <CardHeader>
                                <CardTitle>Операционные показатели</CardTitle>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-500 dark:text-slate-400">Активные ордера:</span>
                                        <span className="font-bold">{globalStats?.active_orders_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-500 dark:text-slate-400">Платежи в обработке:</span>
                                        <span className="font-bold text-orange-600 dark:text-orange-300">{globalStats?.pending_payments_count}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-slate-500 dark:text-slate-400">Соотношение Dep/With:</span>
                                        <span className="font-bold">{globalStats?.deposit_withdraw_ratio?.toFixed(2)}</span>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between">
                                <CardTitle>KPI Саппорта (Апелляции)</CardTitle>
                                <Badge variant="outline">Top Performance</Badge>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-4">
                                    {supportKPI.length > 0 ? supportKPI.map((kpi) => (
                                        <div key={kpi.support_id} className="flex justify-between items-center">
                                            <div>
                                                <p className="font-medium">{kpi.username}</p>
                                                <p className="text-xs text-slate-400">Решено: {kpi.total_resolved}</p>
                                            </div>
                                            <div className="flex gap-2 text-xs">
                                                <span className="text-blue-600 dark:text-blue-300">M: {kpi.favor_merchant}</span>
                                                <span className="text-purple-600 dark:text-purple-300">T: {kpi.favor_trader}</span>
                                            </div>
                                        </div>
                                    )) : (
                                        <p className="text-slate-400 text-center py-4 text-sm">Нет данных по апелляциям</p>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </TabsContent>

                <TabsContent value="invites">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Create Invite Form */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    <UserPlus className="h-5 w-5" /> Создать приглашение
                                </CardTitle>
                                <CardDescription>
                                    Создайте ссылку для регистрации нового пользователя
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                <fieldset disabled={!canCreateInvites} className="space-y-4">
                                    {!canCreateInvites && (
                                        <div className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900 p-3 text-sm text-slate-600 dark:text-slate-300">
                                            Ваша роль разрешает только просмотр приглашений.
                                        </div>
                                    )}
                                    {/* User Type Selection */}
                                    <div className="space-y-2">
                                        <Label>Тип пользователя</Label>
                                        <div className="flex gap-2">
                                            <Button
                                                type="button"
                                                variant={inviteForm.user_type === 'trader' ? 'default' : 'outline'}
                                                className={inviteForm.user_type === 'trader' ? 'bg-blue-600' : ''}
                                                onClick={() => setInviteForm({
                                                    ...inviteForm,
                                                    user_type: 'trader',
                                                    fiat_currency: inviteForm.fiat_currency === 'KZT' ? 'RUB' : inviteForm.fiat_currency,
                                                })}
                                            >
                                                <Users className="h-4 w-4 mr-2" /> Трейдер
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={inviteForm.user_type === 'merchant' ? 'default' : 'outline'}
                                                className={inviteForm.user_type === 'merchant' ? 'bg-green-600' : ''}
                                                onClick={() => setInviteForm({ ...inviteForm, user_type: 'merchant' })}
                                            >
                                                <ShoppingBag className="h-4 w-4 mr-2" /> Мерчант
                                            </Button>
                                            <Button
                                                type="button"
                                                variant={inviteForm.user_type === 'support' ? 'default' : 'outline'}
                                                className={inviteForm.user_type === 'support' ? 'bg-purple-600' : ''}
                                                onClick={() => setInviteForm({ ...inviteForm, user_type: 'support' })}
                                            >
                                                <ShieldCheck className="h-4 w-4 mr-2" /> Саппорт
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Common Fields */}
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="space-y-2">
                                            <Label htmlFor="invite-username">Имя пользователя</Label>
                                            <Input
                                                id="invite-username"
                                                placeholder="username"
                                                value={inviteForm.username}
                                                onChange={(e) => setInviteForm({ ...inviteForm, username: e.target.value })}
                                            />
                                        </div>
                                        <div className="space-y-2">
                                            <Label htmlFor="invite-email">Email</Label>
                                            <Input
                                                id="invite-email"
                                                type="email"
                                                placeholder="email@example.com"
                                                value={inviteForm.email}
                                                onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                                            />
                                        </div>
                                    </div>

                                    {/* Trader-specific Fields */}
                                    {inviteForm.user_type === 'trader' && (
                                        <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                                            <p className="text-sm font-medium text-blue-700 dark:text-blue-300">Настройки трейдера</p>
                                            <div className="space-y-2">
                                                <Label>Валютная пара</Label>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={inviteForm.fiat_currency === 'RUB' ? 'default' : 'outline'}
                                                        onClick={() => setInviteForm({ ...inviteForm, fiat_currency: 'RUB' })}
                                                    >
                                                        RUB / USDT
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={inviteForm.fiat_currency === 'KGS' ? 'default' : 'outline'}
                                                        onClick={() => setInviteForm({ ...inviteForm, fiat_currency: 'KGS' })}
                                                    >
                                                        KGS / USDT
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-blue-600 dark:text-blue-300">
                                                    Пара фиксируется после регистрации и определяет доступные ордера.
                                                </p>
                                            </div>
                                            <div className="grid grid-cols-3 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="deposit-rate">Комиссия пополнения (%)</Label>
                                                    <Input
                                                        id="deposit-rate"
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                        value={inviteForm.deposit_commission_rate}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, deposit_commission_rate: parseFloat(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="withdraw-rate">Комиссия вывода (%)</Label>
                                                    <Input
                                                        id="withdraw-rate"
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                        value={inviteForm.withdraw_commission_rate}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, withdraw_commission_rate: parseFloat(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="insurance">Страховой депозит (USDT)</Label>
                                                    <Input
                                                        id="insurance"
                                                        type="number"
                                                        min="0"
                                                        value={inviteForm.insurance_balance}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, insurance_balance: parseFloat(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="trust-deposit">Депозит доверия (USDT)</Label>
                                                    <Input
                                                        id="trust-deposit"
                                                        type="number"
                                                        min="0"
                                                        value={inviteForm.trust_deposit}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, trust_deposit: parseFloat(e.target.value) })}
                                                    />
                                                </div>
                                            </div>
                                            <CommissionTierEditor
                                                title="Общая комиссия пополнения по сумме"
                                                tiers={inviteForm.deposit_commission_tiers}
                                                onChange={(deposit_commission_tiers) => setInviteForm({ ...inviteForm, deposit_commission_tiers })}
                                            />
                                            <CommissionTierEditor
                                                title="Общая комиссия вывода по сумме"
                                                tiers={inviteForm.withdraw_commission_tiers}
                                                onChange={(withdraw_commission_tiers) => setInviteForm({ ...inviteForm, withdraw_commission_tiers })}
                                            />
                                            <MethodCommissionEditors
                                                title="Комиссии пополнения по методам"
                                                methods={traderCommissionMethods(inviteForm.fiat_currency, 'deposit')}
                                                value={inviteForm.deposit_commission_tiers_by_requisite_type}
                                                onChange={(deposit_commission_tiers_by_requisite_type) => setInviteForm({ ...inviteForm, deposit_commission_tiers_by_requisite_type })}
                                                methodLabel={(method) => traderCommissionMethodLabel(method, inviteForm.fiat_currency)}
                                            />
                                            <MethodCommissionEditors
                                                title="Комиссии вывода по методам"
                                                methods={traderCommissionMethods(inviteForm.fiat_currency, 'withdraw')}
                                                value={inviteForm.withdraw_commission_tiers_by_requisite_type}
                                                onChange={(withdraw_commission_tiers_by_requisite_type) => setInviteForm({ ...inviteForm, withdraw_commission_tiers_by_requisite_type })}
                                                methodLabel={(method) => traderCommissionMethodLabel(method, inviteForm.fiat_currency)}
                                            />
                                            <div className="space-y-2">
                                                <Label htmlFor="referral-code">Реферальный код (опционально)</Label>
                                                <Input
                                                    id="referral-code"
                                                    placeholder="Например: partner1"
                                                    value={inviteForm.referral_code}
                                                    onChange={(e) => setInviteForm({ ...inviteForm, referral_code: e.target.value })}
                                                />
                                                <p className="text-xs text-blue-600 dark:text-blue-300">Код того, кто привел этого трейдера</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Merchant-specific Fields */}
                                    {inviteForm.user_type === 'merchant' && (
                                        <div className="space-y-4 p-4 bg-green-50 dark:bg-green-950 rounded-lg">
                                            <p className="text-sm font-medium text-green-700 dark:text-green-300">Настройки мерчанта</p>
                                            <div className="space-y-2">
                                                <Label>Тип интеграции</Label>
                                                <div className="flex gap-2">
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={inviteForm.integration_profile === 'current' ? 'default' : 'outline'}
                                                        onClick={() => setInviteForm({ ...inviteForm, integration_profile: 'current', fiat_currency: 'RUB', requisite_types: ['card', 'sbp'] })}
                                                    >
                                                        Текущий
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant={inviteForm.integration_profile === 'typical_v2_2' ? 'default' : 'outline'}
                                                        onClick={() => setInviteForm({ ...inviteForm, integration_profile: 'typical_v2_2', fiat_currency: 'KGS', requisite_types: [] })}
                                                    >
                                                        Типовой v2.2
                                                    </Button>
                                                </div>
                                                <p className="text-xs text-green-600 dark:text-green-300">
                                                    Профиль фиксируется после регистрации мерчанта.
                                                </p>
                                            </div>
                                            {inviteForm.integration_profile === 'typical_v2_2' && (
                                                <MerchantCurrencySelector
                                                    value={inviteForm.fiat_currency}
                                                    onChange={(fiat_currency) => setInviteForm({ ...inviteForm, fiat_currency, requisite_types: [] })}
                                                />
                                            )}
                                            <div className="rounded-md border bg-white dark:bg-slate-800 p-3">
                                                <Label className="text-sm font-semibold">Индивидуальные лимиты ({inviteForm.integration_profile === 'typical_v2_2' ? inviteForm.fiat_currency : 'RUB'})</Label>
                                                <p className="mb-3 mt-1 text-xs text-slate-500 dark:text-slate-400">Оставьте оба поля операции пустыми, чтобы использовать глобальные лимиты.</p>
                                                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                                    {([
                                                        ['merchant_deposit_min_amount', 'Пополнение от'],
                                                        ['merchant_deposit_max_amount', 'Пополнение до'],
                                                        ['merchant_withdraw_min_amount', 'Вывод от'],
                                                        ['merchant_withdraw_max_amount', 'Вывод до'],
                                                    ] as const).map(([field, label]) => (
                                                        <div key={field} className="space-y-1">
                                                            <Label className="text-xs">{label}</Label>
                                                            <Input
                                                                type="number"
                                                                min="0"
                                                                value={inviteForm[field] ?? ''}
                                                                onChange={(e) => setInviteForm({
                                                                    ...inviteForm,
                                                                    [field]: e.target.value === '' ? null : parseFloat(e.target.value),
                                                                })}
                                                            />
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="space-y-2">
                                                    <Label htmlFor="merchant-deposit-commission">Комиссия пополнения %</Label>
                                                    <Input
                                                        id="merchant-deposit-commission"
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                        value={inviteForm.merchant_deposit_commission_rate}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, merchant_deposit_commission_rate: parseFloat(e.target.value) })}
                                                    />
                                                </div>
                                                <div className="space-y-2">
                                                    <Label htmlFor="merchant-withdraw-commission">Комиссия вывода %</Label>
                                                    <Input
                                                        id="merchant-withdraw-commission"
                                                        type="number"
                                                        step="0.1"
                                                        min="0"
                                                        max="100"
                                                        value={inviteForm.merchant_withdraw_commission_rate}
                                                        onChange={(e) => setInviteForm({ ...inviteForm, merchant_withdraw_commission_rate: parseFloat(e.target.value) })}
                                                    />
                                                </div>
                                            </div>
                                            <CommissionTierEditor
                                                title="Общая комиссия пополнения по сумме"
                                                tiers={inviteForm.merchant_deposit_commission_tiers}
                                                onChange={(merchant_deposit_commission_tiers) => setInviteForm({ ...inviteForm, merchant_deposit_commission_tiers })}
                                            />
                                            <CommissionTierEditor
                                                title="Общая комиссия вывода по сумме"
                                                tiers={inviteForm.merchant_withdraw_commission_tiers}
                                                onChange={(merchant_withdraw_commission_tiers) => setInviteForm({ ...inviteForm, merchant_withdraw_commission_tiers })}
                                            />
                                            <MethodCommissionEditors
                                                title="Комиссии пополнения по методам"
                                                methods={inviteForm.requisite_types}
                                                value={inviteForm.merchant_deposit_commission_tiers_by_requisite_type}
                                                onChange={(merchant_deposit_commission_tiers_by_requisite_type) => setInviteForm({ ...inviteForm, merchant_deposit_commission_tiers_by_requisite_type })}
                                                minAmount={inviteForm.merchant_deposit_min_amount}
                                                maxAmount={inviteForm.merchant_deposit_max_amount}
                                                operationLabel="пополнения"
                                            />
                                            <MethodCommissionEditors
                                                title="Комиссии вывода по методам"
                                                methods={inviteForm.requisite_types.filter((item) => !['psb_ecom', 'sbp_qr', 'qr_vietnam_sber', 'qr_vietnam_vtb', 'transgran_card_rub_arm', 'transgran_mobile_rub_arm', 'kzt_transgran_gel', 'kzt_transgran_idr', 'kzt_transgran_tjs', 'kzt_transgran_uzs', 'elqr'].includes(item))}
                                                value={inviteForm.merchant_withdraw_commission_tiers_by_requisite_type}
                                                onChange={(merchant_withdraw_commission_tiers_by_requisite_type) => setInviteForm({ ...inviteForm, merchant_withdraw_commission_tiers_by_requisite_type })}
                                                minAmount={inviteForm.merchant_withdraw_min_amount}
                                                maxAmount={inviteForm.merchant_withdraw_max_amount}
                                                operationLabel="вывода"
                                            />
                                            <RequisiteTypeSelector
                                                value={inviteForm.requisite_types}
                                                onChange={(requisite_types) => setInviteForm({ ...inviteForm, requisite_types })}
                                                options={currencyCapabilities[inviteForm.fiat_currency]}
                                                allowEmpty={inviteAllowsEmptyRequisiteTypes}
                                                emptyMessage={inviteAllowsEmptyRequisiteTypes ? 'Для KZT методы пока не настроены' : undefined}
                                            />
                                            <div className="space-y-2">
                                                <Label htmlFor="appeal-minutes">Минуты на апелляцию</Label>
                                                <Input
                                                    id="appeal-minutes"
                                                    type="number"
                                                    min="1"
                                                    value={inviteForm.appeal_lifetime_minutes}
                                                    onChange={(e) => setInviteForm({ ...inviteForm, appeal_lifetime_minutes: parseInt(e.target.value) })}
                                                />
                                                <p className="text-xs text-green-600 dark:text-green-300">Время, которое дается на подачу апелляции</p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Support-specific note */}
                                    {inviteForm.user_type === 'support' && (
                                        <div className="p-4 bg-purple-50 dark:bg-purple-950 rounded-lg">
                                            <p className="text-sm text-purple-700 dark:text-purple-300">
                                                Для саппорта требуется только имя и email.
                                                Остальные настройки не применяются.
                                            </p>
                                        </div>
                                    )}

                                    {inviteError && (
                                        <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
                                            {inviteError}
                                        </div>
                                    )}

                                    <Button
                                        onClick={createInvite}
                                        className="w-full"
                                        disabled={isCreatingInvite || !!inviteCommissionError || !inviteForm.username || !inviteForm.email || (inviteForm.user_type === 'merchant' && inviteForm.requisite_types.length === 0 && !inviteAllowsEmptyRequisiteTypes)}
                                    >
                                        {isCreatingInvite ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Создание...
                                            </>
                                        ) : (
                                            <>
                                                <Link2 className="h-4 w-4 mr-2" />
                                                Создать ссылку на регистрацию
                                            </>
                                        )}
                                    </Button>
                                </fieldset>
                            </CardContent>
                        </Card>

                        {/* Invites List */}
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-2">
                                    История приглашений
                                    {passwordResetRequestsCount > 0 && (
                                        <Badge className="bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800">
                                            Сброс пароля ×{passwordResetRequestsCount}
                                        </Badge>
                                    )}
                                </CardTitle>
                                <CardDescription>Список созданных приглашений</CardDescription>
                            </CardHeader>
                            <CardContent>
                                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                                    {invites.length === 0 ? (
                                        <p className="text-center text-slate-400 py-8">
                                            Нет созданных приглашений
                                        </p>
                                    ) : (
                                        invites.map((invite) => (
                                            <div key={invite.id} className="p-3 border rounded-lg space-y-2">
                                                <div className="flex justify-between items-start">
                                                    <div>
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{invite.username}</span>
                                                            {getUserTypeBadge(invite.user_type)}
                                                            {invite.user_type === 'trader' && (
                                                                <Badge variant="outline">
                                                                    {(invite.fiat_currency || 'RUB')} / USDT
                                                                </Badge>
                                                            )}
                                                            {invite.user_type === 'merchant' && (
                                                                <>
                                                                    <Badge variant="outline">
                                                                        {invite.integration_profile === 'typical_v2_2' ? 'Типовой v2.2' : 'Текущий'}
                                                                    </Badge>
                                                                    <Badge variant="outline">{invite.fiat_currency || (invite.integration_profile === 'typical_v2_2' ? 'KGS' : 'RUB')}</Badge>
                                                                </>
                                                            )}
                                                            {(invite.pending_password_reset_requests || 0) > 0 && (
                                                                <Badge className="bg-orange-100 dark:bg-orange-950 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800">
                                                                    Запрошен сброс ×{invite.pending_password_reset_requests}
                                                                </Badge>
                                                            )}
                                                            {invite.two_factor_enabled && (
                                                                <Badge className="bg-green-100 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-800">
                                                                    2FA включена
                                                                </Badge>
                                                            )}
                                                        </div>
                                                        <p className="text-sm text-slate-500 dark:text-slate-400">{invite.email}</p>
                                                    </div>
                                                    {getInviteStatusBadge(invite.status)}
                                                </div>
                                                <div className="flex items-center justify-between text-xs text-slate-400">
                                                    <div className="flex items-center gap-3">
                                                        <span className="flex items-center gap-1" title="Дата создания">
                                                            <Clock className="h-3 w-3" />
                                                            Создан: {new Date(invite.created_at).toLocaleString('ru-RU')}
                                                        </span>
                                                        <span className="flex items-center gap-1" title="Дата истечения">
                                                            <XCircle className="h-3 w-3" />
                                                            Истекает: {new Date(invite.expires_at).toLocaleString('ru-RU')}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        {invite.status === 'used' && ['trader', 'merchant', 'support'].includes(invite.user_type) && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-6 text-xs"
                                                                onClick={() => createPasswordResetLink(invite.id)}
                                                                disabled={!canManage || creatingResetLinkId === invite.id}
                                                            >
                                                                {creatingResetLinkId === invite.id ? (
                                                                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                                ) : (
                                                                    <KeyRound className="h-3 w-3 mr-1" />
                                                                )}
                                                                Создать ссылку сброса
                                                            </Button>
                                                        )}
                                                        {invite.status === 'used'
                                                            && invite.user_type === 'merchant'
                                                            && invite.two_factor_enabled && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    className="h-6 text-xs text-red-600 dark:text-red-300 hover:text-red-700 dark:hover:text-red-300"
                                                                    onClick={() => resetMerchantTwoFactor(invite)}
                                                                    disabled={!canManage || resettingTwoFactorInviteId === invite.id}
                                                                >
                                                                    {resettingTwoFactorInviteId === invite.id ? (
                                                                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                                                    ) : (
                                                                        <ShieldCheck className="h-3 w-3 mr-1" />
                                                                    )}
                                                                    Сбросить 2FA
                                                                </Button>
                                                            )}
                                                        {invite.password_reset_url && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-6 text-xs"
                                                                onClick={() => {
                                                                    setGeneratedPasswordResetUrl(invite.password_reset_url);
                                                                    setShowPasswordResetModal(true);
                                                                }}
                                                            >
                                                                <Copy className="h-3 w-3 mr-1" /> Ссылка сброса
                                                            </Button>
                                                        )}
                                                        {invite.status === 'pending' && invite.invite_url && (
                                                            <Button
                                                                size="sm"
                                                                variant="ghost"
                                                                className="h-6 text-xs"
                                                                onClick={() => copyToClipboard(invite.invite_url)}
                                                            >
                                                                <Copy className="h-3 w-3 mr-1" /> Копировать
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                {invitesPagination.pages > 1 && (
                                    <div className="mt-4 border-t pt-4">
                                        <Pagination
                                            currentPage={invitesPagination.page}
                                            totalPages={invitesPagination.pages}
                                            onPageChange={(page) => setInvitesPagination({ ...invitesPagination, page })}
                                            totalItems={invitesPagination.total}
                                            pageSize={50}
                                        />
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* Modal for Generated Link */}
                    {showInviteModal && generatedInviteUrl && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <Card className="max-w-lg w-full mx-4">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-green-100 dark:bg-green-950 rounded-full flex items-center justify-center">
                                            <CheckCircle className="h-6 w-6 text-green-600 dark:text-green-300" />
                                        </div>
                                        <div>
                                            <CardTitle>Приглашение создано!</CardTitle>
                                            <CardDescription>Скопируйте ссылку и отправьте пользователю</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Ссылка для регистрации:</p>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 text-sm break-all bg-white dark:bg-slate-800 p-2 rounded border">
                                                {generatedInviteUrl}
                                            </code>
                                            <Button
                                                size="sm"
                                                onClick={() => copyToClipboard(generatedInviteUrl)}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 p-3 rounded-lg">
                                        <Clock className="h-4 w-4" />
                                        <span>Ссылка действительна 24 часа</span>
                                    </div>
                                    <Button
                                        className="w-full"
                                        onClick={() => {
                                            setShowInviteModal(false);
                                            setGeneratedInviteUrl(null);
                                        }}
                                    >
                                        Закрыть
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {showPasswordResetModal && generatedPasswordResetUrl && (
                        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                            <Card className="max-w-lg w-full mx-4">
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <div className="w-12 h-12 bg-orange-100 dark:bg-orange-950 rounded-full flex items-center justify-center">
                                            <KeyRound className="h-6 w-6 text-orange-600 dark:text-orange-300" />
                                        </div>
                                        <div>
                                            <CardTitle>Ссылка для сброса пароля</CardTitle>
                                            <CardDescription>Передайте ссылку пользователю вручную</CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                                        <p className="text-xs text-slate-500 dark:text-slate-400 mb-1">Ссылка для сброса пароля:</p>
                                        <div className="flex items-center gap-2">
                                            <code className="flex-1 text-sm break-all bg-white dark:bg-slate-800 p-2 rounded border">
                                                {generatedPasswordResetUrl}
                                            </code>
                                            <Button
                                                size="sm"
                                                onClick={() => copyToClipboard(generatedPasswordResetUrl)}
                                            >
                                                <Copy className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-950 p-3 rounded-lg">
                                        <Clock className="h-4 w-4" />
                                        <span>Ссылка действительна 24 часа</span>
                                    </div>
                                    <Button
                                        className="w-full"
                                        onClick={() => {
                                            setShowPasswordResetModal(false);
                                            setGeneratedPasswordResetUrl(null);
                                        }}
                                    >
                                        Закрыть
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="traders">
                    <div className="mb-4 flex justify-between items-center">
                        <div className="flex items-center gap-4">
                            <Label htmlFor="referral-filter" className="whitespace-nowrap">Фильтр по рефералу:</Label>
                            <select
                                id="referral-filter"
                                value={selectedReferralCode}
                                onChange={(e) => {
                                    setSelectedReferralCode(e.target.value);
                                    setTradersPagination({ ...tradersPagination, page: 1 });
                                }}
                                className="h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                            >
                                <option value="">Все трейдеры</option>
                                {referralCodes.map((code) => (
                                    <option key={code} value={code}>{code}</option>
                                ))}
                            </select>
                            {selectedReferralCode && (
                                <Badge variant="outline" className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300">
                                    Фильтр: {selectedReferralCode}
                                </Badge>
                            )}
                        </div>
                        <Button
                            variant="outline"
                            onClick={exportTradersToExcel}
                            disabled={traders.length === 0}
                            className="flex gap-2 bg-green-600 hover:bg-green-700 text-white border-green-600"
                        >
                            <Download className="h-4 w-4" /> Экспорт в Excel
                        </Button>
                    </div>
                    <div className="grid gap-4">
                        {traders.map((trader) => (
                            <TraderCard
                                key={trader._id}
                                trader={trader}
                                onToggle={() => toggleStatus('trader', trader._id, trader.is_active)}
                                onUpdate={(settings) => updateTraderSettings(trader._id, settings)}
                                onOpenDashboard={() => openTraderDashboard(trader._id)}
                                isOpeningDashboard={openingTraderDashboard}
                                dashboardError={traderDashboardError && traderDashboardError.id === trader._id ? traderDashboardError.message : null}
                                onOpenSettlements={() => openTraderSettlements(trader._id)}
                                isToggling={togglingStatus.has(trader._id)}
                                currencyRates={currencyRates}
                                readOnly={!canManage}
                            />
                        ))}
                    </div>
                    {tradersPagination.pages > 1 && (
                        <div className="mt-8">
                            <Pagination
                                currentPage={tradersPagination.page}
                                totalPages={tradersPagination.pages}
                                onPageChange={(page) => setTradersPagination({ ...tradersPagination, page })}
                                totalItems={tradersPagination.total}
                                pageSize={50}
                            />
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="merchants">
                    <div className="mb-4 flex flex-wrap items-end gap-3">
                        <div className="min-w-[16rem] flex-1 space-y-2">
                            <Label htmlFor="merchant-search">Поиск мерчанта</Label>
                            <Input id="merchant-search" type="search" placeholder="Логин или полный ID"
                                maxLength={200} value={merchantSearch} onChange={event => setMerchantSearch(event.target.value)} />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="merchant-currency-filter">Валюта</Label>
                            <select id="merchant-currency-filter" value={merchantFilters.currency}
                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                onChange={event => setMerchantFilters(current => ({
                                    ...current, page: 1, currency: event.target.value as MerchantFiatCurrency | '',
                                }))}>
                                <option value="">Все валюты</option>
                                <option value="RUB">RUB</option>
                                <option value="KGS">KGS</option>
                                <option value="KZT">KZT</option>
                            </select>
                        </div>
                        {(merchantSearch || merchantFilters.search || merchantFilters.currency) && (
                            <Button type="button" variant="outline" onClick={() => {
                                setMerchantSearch('');
                                setMerchantFilters({ page: 1, search: '', currency: '' });
                            }}>Сбросить</Button>
                        )}
                    </div>
                    {merchantsLoading ? (
                        <p role="status" className="py-8 text-center text-slate-500">Загрузка мерчантов…</p>
                    ) : merchantsError ? (
                        <div role="alert" className="flex items-center gap-3 py-4">
                            <span>{merchantsError}</span>
                            <Button variant="outline" onClick={() => void refreshMerchants()}>Повторить</Button>
                        </div>
                    ) : (
                        <>
                            <p className="mb-3 text-sm text-slate-500">Найдено: {merchantsPagination.total}</p>
                            {merchants.length === 0 && <p className="py-8 text-center text-slate-500">Мерчанты не найдены. Измените поиск или валюту.</p>}
                            <div className="grid gap-4">
                                {merchants.map((merchant) => (
                                    <MerchantCard
                                        key={merchant._id}
                                        merchant={merchant}
                                        onOpenDashboard={() => openMerchantDashboard(merchant._id)}
                                        isOpeningDashboard={openingMerchantDashboard !== null}
                                        dashboardError={merchantDashboardError && merchantDashboardError.id === merchant._id ? merchantDashboardError.message : null}
                                        onToggle={() => toggleStatus('merchant', merchant._id, merchant.is_active)}
                                        onUpdate={(settings) => updateMerchantSettings(merchant._id, settings)}
                                        onDirectionChange={(field, enabled) => updateMerchantFlag(merchant._id, field, enabled)}
                                        onApiVisibilityChange={hidden => updateMerchantFlag(merchant._id, 'hide_api_keys', hidden)}
                                        isToggling={togglingStatus.has(merchant._id)}
                                        exchangeRate={adminExchangeRate}
                                        currencyRates={currencyRates}
                                        currencyCapabilities={currencyCapabilities}
                                        readOnly={!canManage}
                                    />
                                ))}
                            </div>
                            {merchantsPagination.pages > 1 && (
                                <div className="mt-8">
                                    <Pagination
                                        currentPage={merchantsPagination.page}
                                        totalPages={merchantsPagination.pages}
                                        onPageChange={(page) => setMerchantFilters(current => ({ ...current, page }))}
                                        totalItems={merchantsPagination.total}
                                        pageSize={50}
                                    />
                                </div>
                            )}
                        </>
                    )}
                </TabsContent>

                <TabsContent value="routing"><RoutingTemplatesPanel readOnly={!canManage} /></TabsContent>

                <TabsContent value="external-platforms">
                    <div className="space-y-4">
                        <div className="flex justify-between items-center">
                            <div>
                                <h2 className="text-xl font-semibold">Платформы каскада</h2>
                                <p className="text-sm text-slate-500 dark:text-slate-400">Внешние источники ликвидности, которые принимают заявки после локальных трейдеров.</p>
                            </div>
                            <Button onClick={() => setShowExternalPlatformForm(value => !value)} className="flex gap-2" disabled={!canManage}>
                                <Route className="h-4 w-4" />
                                {showExternalPlatformForm ? 'Скрыть форму' : 'Добавить платформу'}
                            </Button>
                        </div>

                        {showExternalPlatformForm && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Новая платформа</CardTitle>
                                    <CardDescription>Создайте сущность каскада и задайте адаптер. Для другого API позже добавляется новый adapter_type.</CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-5">
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="space-y-2">
                                            <Label>Название</Label>
                                            <Input value={externalPlatformForm.name} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, name: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Тип адаптера</Label>
                                            <select
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                value={externalPlatformForm.adapter_type}
                                                onChange={(e) => setExternalPlatformForm({
                                                    ...externalPlatformForm,
                                                    adapter_type: e.target.value,
                                                    min_amount: e.target.value === 'bnpay' ? 500 : externalPlatformForm.min_amount,
                                                    max_amount: e.target.value === 'bnpay' ? 9999 : externalPlatformForm.max_amount,
                                                    deposit_commission_rate: e.target.value === 'bnpay' ? 12 : externalPlatformForm.deposit_commission_rate,
                                                    base_url: e.target.value === 'gotham' ? 'https://gt-union.org/api/v1' : e.target.value === 'sarex' ? 'https://api.sarex.live' : e.target.value === 'bnpay' ? 'https://s2s.bnpay.world' : e.target.value === 'noros' ? 'https://rest.noros.org/api/v1' : e.target.value === 'fynzah' && !externalPlatformForm.base_url.trim()
                                                        ? FYNZAH_PRODUCTION_BASE_URL
                                                        : externalPlatformForm.base_url,
                                                    fiat_currency: ['noros', 'sarex'].includes(e.target.value) ? 'KZT' : e.target.value === 'youpay_kgs' ? 'KGS' : ['youpay', 'fynzah', 'swpay_rub', 'bnpay', 'gotham'].includes(e.target.value) ? 'RUB' : externalPlatformForm.fiat_currency,
                                                    rate_source: ['noros', 'sarex', 'youpay_kgs'].includes(e.target.value) ? 'BYBIT_P2P_API' : ['youpay', 'fynzah', 'swpay_rub', 'bnpay', 'gotham'].includes(e.target.value) ? 'RAPIRA_P2P_API' : externalPlatformForm.rate_source,
                                                    withdraw_enabled: e.target.value === 'youpay_kgs'
                                                        ? true
                                                        : ['flintpays', 'youpay', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(e.target.value)
                                                            ? false
                                                            : externalPlatformForm.withdraw_enabled,
                                                    requisite_types: externalAdapterRequisiteTypes(e.target.value, ['bnpay', 'swpay_rub', 'gotham'].includes(e.target.value) ? 'RUB' : externalPlatformForm.fiat_currency),
                                                    is_active: ['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(e.target.value) ? false : externalPlatformForm.is_active,
                                                    payment_type_mapping: e.target.value === 'swpay'
                                                        ? withSWPayPaymentMapping(externalPlatformForm.payment_type_mapping, externalPlatformForm.fiat_currency)
                                                        : e.target.value === 'fynzah'
                                                            ? { ...externalPlatformForm.payment_type_mapping, qr_vietnam_sber: 'sber_s', qr_vietnam_vtb: 'vtb_s' }
                                                            : externalPlatformForm.payment_type_mapping,
                                                })}
                                            >
                                                <option value="compatible_merchant_api">compatible_merchant_api</option>
                                                <option value="flintpays">flintpays</option>
                                                <option value="youpay">youpay</option>
                                                <option value="youpay_kgs">youpay_kgs</option>
                                                <option value="fynzah">fynzah</option>
                                                <option value="bnpay">Bnpay · RUB / KZT → TJS</option>
                                                <option value="swpay">swpay</option>
                                                <option value="swpay_rub">SWPay RUB → UZS</option>
                                                <option value="gotham">Gotham Trade</option>
                                                <option value="noros">NOROS</option>
                                                <option value="sarex">Sarex · KZT → GEL / TJS</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Фиатная валюта</Label>
                                            <select
                                                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                                value={externalPlatformForm.fiat_currency}
                                                onChange={(e) => {
                                                    const fiatCurrency = e.target.value as 'RUB' | 'KGS' | 'KZT';
                                                    setExternalPlatformForm({
                                                        ...externalPlatformForm,
                                                        fiat_currency: fiatCurrency,
                                                        ...(externalPlatformForm.adapter_type === 'bnpay' ? {
                                                            min_amount: fiatCurrency === 'KZT' ? 1000 : 500,
                                                            max_amount: fiatCurrency === 'KZT' ? 250000 : 9999,
                                                            deposit_commission_rate: fiatCurrency === 'KZT' ? 5 : 12,
                                                            rate_markup_percent: fiatCurrency === 'KZT' ? 0 : 6,
                                                        } : {}),
                                                        rate_source: fiatCurrency !== 'RUB' ? 'BYBIT_P2P_API' : 'RAPIRA_P2P_API',
                                                        withdraw_enabled: externalPlatformForm.adapter_type === 'swpay'
                                                            ? fiatCurrency === 'KGS' && externalPlatformForm.withdraw_enabled
                                                            : externalPlatformForm.withdraw_enabled,
                                                        requisite_types: ['swpay', 'bnpay'].includes(externalPlatformForm.adapter_type)
                                                            ? externalAdapterRequisiteTypes(externalPlatformForm.adapter_type, fiatCurrency)
                                                            : externalPlatformForm.requisite_types,
                                                        payment_type_mapping: externalPlatformForm.adapter_type === 'swpay'
                                                            ? withSWPayPaymentMapping(externalPlatformForm.payment_type_mapping, fiatCurrency)
                                                            : externalPlatformForm.payment_type_mapping,
                                                    });
                                                }}
                                                disabled={['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'noros', 'sarex', 'gotham'].includes(externalPlatformForm.adapter_type)}
                                            >
                                                <option value="RUB">RUB</option>
                                                <option value="KGS" disabled={externalPlatformForm.adapter_type === 'bnpay'}>KGS</option>
                                                <option value="KZT">KZT</option>
                                            </select>
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Base URL</Label>
                                            <Input placeholder="https://partner.example.com" value={externalPlatformForm.base_url} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, base_url: e.target.value })} />
                                        </div>
                                    </div>
                                    {externalPlatformForm.fiat_currency === 'KGS' && (
                                        <div className="rounded-md border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950 p-3 text-sm text-orange-800 dark:text-orange-200">
                                            {externalPlatformForm.adapter_type === 'swpay'
                                                ? 'Для SWPay/KGS используйте отдельную платформу каскада (например, swpay_kgs). Методы системы: card и sbp_kgs.'
                                                : 'Для YouPay/KGS используйте отдельную платформу каскада с Merchant ID 605. Методы системы: sbp_kgs и card.'}
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="space-y-2 md:col-span-2">
                                            <Label>{['youpay', 'youpay_kgs'].includes(externalPlatformForm.adapter_type) ? 'YouPay API key' : externalPlatformForm.adapter_type === 'bnpay' ? 'Bnpay API key' : externalPlatformForm.adapter_type === 'sarex' ? 'Sarex Bearer token' : externalPlatformForm.adapter_type === 'fynzah' ? 'Fynzah Bearer token' : ['swpay', 'swpay_rub'].includes(externalPlatformForm.adapter_type) ? 'SWPay Key Secret' : externalPlatformForm.adapter_type === 'noros' ? 'NOROS api_key' : 'Токен платформы'}</Label>
                                            <Input type="password" value={externalPlatformForm.platform_token} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, platform_token: e.target.value })} />
                                        </div>
                                        {!['youpay', 'youpay_kgs'].includes(externalPlatformForm.adapter_type) && (
                                            <div className="space-y-2 md:col-span-2">
                                                <Label>{externalPlatformForm.adapter_type === 'bnpay' ? 'Bnpay Secret' : externalPlatformForm.adapter_type === 'sarex' ? 'Sarex Secret (HMAC)' : externalPlatformForm.adapter_type === 'fynzah' ? 'Fynzah Secret key' : 'Webhook secret'}</Label>
                                                <Input type="password" value={externalPlatformForm.webhook_secret} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, webhook_secret: e.target.value })} />
                                            </div>
                                        )}
                                    </div>

                                    {externalPlatformForm.adapter_type === 'flintpays' && (
                                        <div className="space-y-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4">
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                <div className="space-y-2 md:col-span-2">
                                                    <Label>Flint Merchant UUID</Label>
                                                    <Input value={externalPlatformForm.external_merchant_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_id: e.target.value })} />
                                                </div>
                                                {(['card', 'sbp', 'mobile_kgs'] as const).map((type) => (
                                                    <div className="space-y-2" key={type}>
                                                        <Label>payment_type: {type}</Label>
                                                        <Input
                                                            value={externalPlatformForm.payment_type_mapping[type]}
                                                            onChange={(e) => setExternalPlatformForm({
                                                                ...externalPlatformForm,
                                                                payment_type_mapping: { ...externalPlatformForm.payment_type_mapping, [type]: e.target.value },
                                                            })}
                                                        />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {['youpay', 'youpay_kgs'].includes(externalPlatformForm.adapter_type) && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 p-4">
                                            <div className="space-y-2">
                                                <Label>YouPay Merchant ID</Label>
                                                <Input value={externalPlatformForm.external_merchant_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_id: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>YouPay Merchant Login</Label>
                                                <Input value={externalPlatformForm.external_merchant_login} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_login: e.target.value })} />
                                            </div>
                                        </div>
                                    )}

                                    {externalPlatformForm.adapter_type === 'gotham' && (
                                    <div className="space-y-2 rounded-md border p-4">
                                        <Label>Gotham Merchant Name</Label>
                                        <Input value={externalPlatformForm.external_merchant_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_id: e.target.value })} />
                                        <p className="text-xs text-slate-500">Токен платформы: X-Merchant-Token. Webhook secret: Callback Token.</p>
                                    </div>
                                )}
                                {externalPlatformForm.adapter_type === 'sarex' && (
                                        <div className="space-y-2 rounded-md border p-4">
                                            <Label>Sarex Merchant ID</Label>
                                            <Input value={externalPlatformForm.external_merchant_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_id: e.target.value })} />
                                            <p className="text-xs text-slate-500">KZT → GEL: KZT_P2P_TG · KZT → TJS: KZT_P2P_TGTJ · только приём платежей</p>
                                        </div>
                                    )}
                                    {externalPlatformForm.adapter_type === 'bnpay' && (
                                        <div className="space-y-2 rounded-md border p-4">
                                            <Label>Bnpay Shop ID</Label>
                                            <Input value={externalPlatformForm.external_merchant_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_id: e.target.value })} />
                                        </div>
                                    )}
                                    {externalPlatformForm.adapter_type === 'fynzah' && (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950 p-4">
                                            <div className="space-y-2">
                                                <Label>Fynzah Merchant ID</Label>
                                                <Input value={externalPlatformForm.external_merchant_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_merchant_id: e.target.value })} />
                                            </div>
                                            <div className="space-y-2">
                                                <Label>Маршруты Fynzah</Label>
                                                <div className="rounded-md border bg-white dark:bg-slate-800 px-3 py-2 text-sm leading-6">
                                                    <div><code>qr_vietnam_sber</code> → <code>sber_s</code></div>
                                                    <div><code>qr_vietnam_vtb</code> → <code>vtb_s</code></div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {['swpay', 'swpay_rub'].includes(externalPlatformForm.adapter_type) && (
                                        <div className="space-y-4 rounded-md border border-violet-200 bg-violet-50 p-4">
                                            <div className="space-y-2">
                                                <Label>SWPay Key ID</Label>
                                                <Input value={externalPlatformForm.external_key_id} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, external_key_id: e.target.value })} />
                                            </div>
                                            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                {externalPlatformForm.adapter_type === 'swpay_rub' && <p className="col-span-full text-sm">Т-Банк → tinkoff · ВТБ → vtb · Сбер → sberbank. Направление закрепляется за профилем SWPay.</p>}
                                                {(externalPlatformForm.adapter_type === 'swpay' ? externalAdapterRequisiteTypes('swpay', externalPlatformForm.fiat_currency) : []).map((type) => (
                                                    <div className="space-y-2" key={type}>
                                                        <Label>PaymentMethod: {type}</Label>
                                                        <Input value={(externalPlatformForm.payment_type_mapping as Partial<Record<RequisiteType, string>>)[type] || ''} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, payment_type_mapping: { ...externalPlatformForm.payment_type_mapping, [type]: e.target.value } })} />
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <Label>Webhook URL</Label>
                                        <Input
                                            placeholder={['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(externalPlatformForm.adapter_type) ? 'Сформируется автоматически после создания' : 'https://api.example.com/api/external-platforms/callback/{platform_id}'}
                                            value={externalPlatformForm.webhook_url}
                                            onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, webhook_url: e.target.value })}
                                            readOnly={['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(externalPlatformForm.adapter_type)}
                                        />
                                    </div>

                                    <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-4">
                                        <label className="flex items-center gap-3 text-sm font-medium text-amber-900 dark:text-amber-200">
                                            <Switch
                                                checked={externalPlatformForm.debug_payload_capture}
                                                onCheckedChange={(checked) => setExternalPlatformForm({ ...externalPlatformForm, debug_payload_capture: checked })}
                                            />
                                            Сохранять полный журнал интеграции без срока удаления
                                        </label>
                                        <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
                                            Для этой платформы будут сохраняться полные запросы, ответы, ошибки и входящие webhook.
                                        </p>
                                    </div>

                                    <RequisiteTypeSelector
                                        value={externalPlatformForm.requisite_types}
                                        onChange={(requisite_types) => setExternalPlatformForm({ ...externalPlatformForm, requisite_types })}
                                        options={externalAdapterRequisiteTypes(externalPlatformForm.adapter_type, externalPlatformForm.fiat_currency)}
                                    />

                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                                        <div className="space-y-2">
                                            <Label>Приоритет</Label>
                                            <Input type="number" min="0" value={externalPlatformForm.priority} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, priority: parseInt(e.target.value || '0', 10) })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Мин. сумма</Label>
                                            <Input type="number" min="0" value={externalPlatformForm.min_amount} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, min_amount: parseFloat(e.target.value || '0') })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Макс. сумма</Label>
                                            <Input type="number" min="1" value={externalPlatformForm.max_amount} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, max_amount: parseFloat(e.target.value || '0') })} />
                                        </div>
                                        <label className="flex items-center gap-2 text-sm">
                                            <Switch checked={externalPlatformForm.deposit_enabled} onCheckedChange={(checked) => setExternalPlatformForm({ ...externalPlatformForm, deposit_enabled: checked })} />
                                            Deposit
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <Switch disabled={['flintpays', 'youpay', 'fynzah', 'swpay_rub', 'bnpay', 'noros', 'sarex', 'gotham'].includes(externalPlatformForm.adapter_type) || (externalPlatformForm.adapter_type === 'swpay' && externalPlatformForm.fiat_currency !== 'KGS')} checked={externalPlatformForm.withdraw_enabled} onCheckedChange={(checked) => setExternalPlatformForm({ ...externalPlatformForm, withdraw_enabled: checked })} />
                                            Withdraw
                                        </label>
                                        <label className="flex items-center gap-2 text-sm">
                                            <Switch checked={externalPlatformForm.is_active} onCheckedChange={(checked) => setExternalPlatformForm({ ...externalPlatformForm, is_active: checked })} />
                                            Активна
                                        </label>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                                        <div className="space-y-2">
                                            <Label>Страховой депозит</Label>
                                            <Input type="number" min="0" value={externalPlatformForm.insurance_balance} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, insurance_balance: parseFloat(e.target.value || '0') })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Депозит доверия</Label>
                                            <Input type="number" min="0" value={externalPlatformForm.trust_deposit} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, trust_deposit: parseFloat(e.target.value || '0') })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Ком. ввода (%)</Label>
                                            <Input type="number" min="0" max="100" value={externalPlatformForm.deposit_commission_rate} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, deposit_commission_rate: parseFloat(e.target.value || '0') })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Ком. вывода (%)</Label>
                                            <Input type="number" min="0" max="100" value={externalPlatformForm.withdraw_commission_rate} onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, withdraw_commission_rate: parseFloat(e.target.value || '0') })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>Наценка к курсу (%)</Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                min="0"
                                                value={externalPlatformForm.rate_markup_percent}
                                                onChange={(e) => setExternalPlatformForm({ ...externalPlatformForm, rate_markup_percent: parseFloat(e.target.value || '0') })}
                                            />
                                        </div>
                                        <Button onClick={createExternalPlatform} disabled={!!externalPlatformCommissionError || !externalPlatformForm.name.trim() || !externalPlatformForm.base_url.trim() || !externalPlatformForm.platform_token.trim() || externalPlatformForm.requisite_types.length === 0 || (externalPlatformForm.adapter_type === 'flintpays' && !externalPlatformForm.external_merchant_id.trim()) || (['youpay', 'youpay_kgs'].includes(externalPlatformForm.adapter_type) && externalPlatformForm.is_active && (!externalPlatformForm.external_merchant_id.trim() || !externalPlatformForm.external_merchant_login.trim())) || (['fynzah', 'bnpay', 'sarex', 'gotham'].includes(externalPlatformForm.adapter_type) && externalPlatformForm.is_active && (!externalPlatformForm.external_merchant_id.trim() || !externalPlatformForm.webhook_secret.trim())) || (['swpay', 'swpay_rub'].includes(externalPlatformForm.adapter_type) && !externalPlatformForm.external_key_id.trim())}>
                                            <Save className="h-4 w-4 mr-2" /> Создать
                                        </Button>
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <CommissionTierEditor
                                            title="Общая комиссия пополнения по сумме"
                                            tiers={externalPlatformForm.deposit_commission_tiers}
                                            onChange={(deposit_commission_tiers) => setExternalPlatformForm({ ...externalPlatformForm, deposit_commission_tiers })}
                                        />
                                        <CommissionTierEditor
                                            title="Общая комиссия вывода по сумме"
                                            tiers={externalPlatformForm.withdraw_commission_tiers}
                                            onChange={(withdraw_commission_tiers) => setExternalPlatformForm({ ...externalPlatformForm, withdraw_commission_tiers })}
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                        <MethodCommissionEditors
                                            title="Комиссии пополнения по методам"
                                            methods={externalPlatformForm.requisite_types}
                                            value={externalPlatformForm.deposit_commission_tiers_by_requisite_type}
                                            onChange={(deposit_commission_tiers_by_requisite_type) => setExternalPlatformForm({ ...externalPlatformForm, deposit_commission_tiers_by_requisite_type })}
                                        />
                                        <MethodCommissionEditors
                                            title="Комиссии вывода по методам"
                                            methods={externalPlatformForm.requisite_types.filter((item) => !['psb_ecom', 'sbp_qr', 'qr_vietnam_sber', 'qr_vietnam_vtb', 'transgran_card_rub_arm', 'transgran_mobile_rub_arm', 'kzt_transgran_gel', 'kzt_transgran_idr', 'kzt_transgran_tjs', 'kzt_transgran_uzs', 'elqr'].includes(item))}
                                            value={externalPlatformForm.withdraw_commission_tiers_by_requisite_type}
                                            onChange={(withdraw_commission_tiers_by_requisite_type) => setExternalPlatformForm({ ...externalPlatformForm, withdraw_commission_tiers_by_requisite_type })}
                                        />
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        <div className="grid gap-4">
                            {externalPlatforms.map((platform) => (
                                <ExternalPlatformCard
                                    key={platform._id}
                                    platform={platform}
                                    onToggle={() => toggleStatus('external_platform', platform._id, platform.is_active)}
                                    onUpdate={(settings) => updateExternalPlatformSettings(platform._id, settings)}
                                    isToggling={togglingStatus.has(platform._id)}
                                    currencyRates={currencyRates}
                                    readOnly={!canManage}
                                />
                            ))}
                        </div>
                        {externalPlatformsPagination.pages > 1 && (
                            <div className="mt-8">
                                <Pagination
                                    currentPage={externalPlatformsPagination.page}
                                    totalPages={externalPlatformsPagination.pages}
                                    onPageChange={(page) => setExternalPlatformsPagination({ ...externalPlatformsPagination, page })}
                                    totalItems={externalPlatformsPagination.total}
                                    pageSize={50}
                                />
                            </div>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="trader-deposits"><TraderDepositsPanel canManage={canManage} /></TabsContent>
                <TabsContent value="device-events"><AdminDeviceEvents /></TabsContent>
                {canManage && <TabsContent value="withdrawal-transfer"><WithdrawalTransferPanel onCompleted={() => { void loadTransactions(); }} /></TabsContent>}
                {canManage && <TabsContent value="recalculation">
                    <RecalculationPanel onCompleted={() => { void loadTransactions(); }} />
                </TabsContent>}

                <TabsContent value="transactions">
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <ArrowRightLeft className="h-5 w-5" /> Все транзакции
                                    </CardTitle>
                                    <CardDescription>Список всех транзакций на платформе</CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={exportTransactionsToExcel}
                                        disabled={transactionsExporting || transactionsLoading}
                                        className="flex items-center gap-2"
                                    >
                                        {transactionsExporting ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <Download className="h-4 w-4" />
                                        )}
                                        {transactionsExporting ? `Экспорт: ${transactionsExportProgress}` : 'Экспорт Excel'}
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadTransactions}
                                        disabled={transactionsLoading}
                                    >
                                        {transactionsLoading ? (
                                            <RefreshCw className="h-4 w-4 animate-spin" />
                                        ) : (
                                            <RefreshCw className="h-4 w-4" />
                                        )}
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-4 mt-4">
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        type="search"
                                        value={transactionsSearchQuery}
                                        onChange={(e) => setTransactionsSearchQuery(e.target.value)}
                                        placeholder="ID, полный Customer ID, сумма, мерчант, реквизиты"
                                        className="w-[460px] max-w-full pl-9"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <Label htmlFor="merchant-filter" className="whitespace-nowrap text-sm">Мерчант:</Label>
                                    <select
                                        id="merchant-filter"
                                        value={transactionsMerchantFilter}
                                        onChange={(e) => {
                                            setTransactionsMerchantFilter(e.target.value);
                                            resetTransactionsPage();
                                        }}
                                        className="h-9 max-w-64 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    >
                                        <option value="all">Все мерчанты</option>
                                        {transactionFilterOptions.merchants.map((merchant) => (
                                            <option key={merchant.id} value={merchant.id}>
                                                {merchant.username}{merchant.company_name ? ` · ${merchant.company_name}` : ''}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Label htmlFor="provider-type-filter" className="whitespace-nowrap text-sm">Провайдер:</Label>
                                    <select
                                        id="provider-type-filter"
                                        value={transactionsProviderTypeFilter}
                                        onChange={(e) => {
                                            setTransactionsProviderTypeFilter(e.target.value as 'all' | 'trader' | 'external');
                                            setTransactionsProviderFilter('all');
                                            resetTransactionsPage();
                                        }}
                                        className="h-9 max-w-56 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    >
                                        <option value="all">Все провайдеры</option>
                                        <option value="trader">Локальные</option>
                                        <option value="external">Внешние платформы</option>
                                    </select>
                                    {transactionsProviderTypeFilter !== 'all' && (
                                        <select
                                            id="provider-filter"
                                            aria-label="Выбор провайдера"
                                            value={transactionsProviderFilter}
                                            onChange={(e) => {
                                                setTransactionsProviderFilter(e.target.value);
                                                resetTransactionsPage();
                                            }}
                                            className="h-9 max-w-64 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        >
                                            <option value="all">
                                                {transactionsProviderTypeFilter === 'trader' ? 'Все локальные' : 'Все внешние'}
                                            </option>
                                            {transactionFilterOptions.providers
                                                .filter((provider) => provider.type === transactionsProviderTypeFilter)
                                                .map((provider) => (
                                                    <option key={provider.id} value={provider.id}>{provider.name}</option>
                                                ))}
                                        </select>
                                    )}
                                </div>

                                <div className="flex flex-col gap-1">
                                    <Label className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                                        Временной диапазон
                                    </Label>
                                    <div className="flex items-center gap-2">
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-xs text-slate-400">Начало периода</span>
                                            <Input
                                                id="date-from-filter"
                                                type="datetime-local"
                                                step="60"
                                                value={transactionsStartDate}
                                                onChange={(e) => {
                                                    setTransactionsStartDate(e.target.value);
                                                    resetTransactionsPage();
                                                }}
                                                className="h-9 w-52"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-0.5">
                                            <span className="text-xs text-slate-400">Конец периода</span>
                                            <Input
                                                id="date-to-filter"
                                                type="datetime-local"
                                                step="60"
                                                value={transactionsEndDate}
                                                onChange={(e) => {
                                                    setTransactionsEndDate(e.target.value);
                                                    resetTransactionsPage();
                                                }}
                                                className="h-9 w-52"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex gap-1">
                                    <Button
                                        variant={transactionsTypeFilter === 'all' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => {
                                            setTransactionsTypeFilter('all');
                                            setRequestedTransactionsPage(1);
                                        }}
                                    >
                                        Все
                                    </Button>
                                    <Button
                                        variant={transactionsTypeFilter === 'deposit' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => {
                                            setTransactionsTypeFilter('deposit');
                                            setRequestedTransactionsPage(1);
                                        }}
                                        className={transactionsTypeFilter === 'deposit' ? 'bg-green-600 hover:bg-green-700' : ''}
                                    >
                                        <ArrowDownCircle className="h-4 w-4 mr-1" /> Входящие
                                    </Button>
                                    <Button
                                        variant={transactionsTypeFilter === 'withdraw' ? 'default' : 'outline'}
                                        size="sm"
                                        onClick={() => {
                                            setTransactionsTypeFilter('withdraw');
                                            setRequestedTransactionsPage(1);
                                        }}
                                        className={transactionsTypeFilter === 'withdraw' ? 'bg-orange-600 hover:bg-orange-700' : ''}
                                    >
                                        <ArrowUpCircle className="h-4 w-4 mr-1" /> Исходящие
                                    </Button>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Label htmlFor="requisite-type-filter" className="whitespace-nowrap text-sm">Метод оплаты:</Label>
                                    <select
                                        id="requisite-type-filter"
                                        value={transactionsRequisiteType}
                                        onChange={(e) => {
                                            setTransactionsRequisiteType(e.target.value as RequisiteType | 'all');
                                            resetTransactionsPage();
                                        }}
                                        className="h-9 max-w-56 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    >
                                        <option value="all">Все методы</option>
                                        {REQUISITE_TYPE_OPTIONS.map((method) => (
                                            <option key={method.value} value={method.value}>{method.label}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Label htmlFor="fiat-currency-filter" className="whitespace-nowrap text-sm">Фиат:</Label>
                                    <select
                                        id="fiat-currency-filter"
                                        value={transactionsFiatCurrency}
                                        onChange={(e) => {
                                            setTransactionsFiatCurrency(e.target.value as MerchantFiatCurrency | 'all');
                                            resetTransactionsPage();
                                        }}
                                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    >
                                        <option value="all">Все валюты</option>
                                        <option value="RUB">RUB</option>
                                        <option value="KGS">KGS</option>
                                        <option value="KZT">KZT</option>
                                    </select>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Label htmlFor="amount-min-filter" className="whitespace-nowrap text-sm">Сумма fiat:</Label>
                                    <Input
                                        id="amount-min-filter"
                                        type="number"
                                        min="0"
                                        value={transactionsAmountMin}
                                        onChange={(e) => {
                                            setTransactionsAmountMin(e.target.value);
                                            resetTransactionsPage();
                                        }}
                                        placeholder="от"
                                        className="h-9 w-24"
                                    />
                                    <Input
                                        type="number"
                                        min="0"
                                        value={transactionsAmountMax}
                                        onChange={(e) => {
                                            setTransactionsAmountMax(e.target.value);
                                            resetTransactionsPage();
                                        }}
                                        placeholder="до"
                                        className="h-9 w-24"
                                    />
                                </div>

                                <div className="flex items-center gap-2">
                                    <Label htmlFor="status-filter" className="whitespace-nowrap text-sm">Статус:</Label>
                                    <select
                                        id="status-filter"
                                        value={transactionsStatusFilter}
                                        onChange={(e) => {
                                            setTransactionsStatusFilter(e.target.value);
                                            setRequestedTransactionsPage(1);
                                        }}
                                        className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                    >
                                        <option value="all">Все статусы</option>
                                        <option value="assigned">Назначен</option>
                                        <option value="pending_requisites">Ожидает реквизитов</option>
                                        <option value="receipt_uploaded">Клиент сообщил об оплате / Чек загружен</option>
                                        <option value="confirmed">Подтвержден</option>
                                        <option value="completed">Завершен</option>
                                        <option value="cancelled">Отменен</option>
                                        <option value="disputed">Спор</option>
                                        <option value="no_traders">Нет трейдеров</option>
                                    </select>
                                </div>

                                {(transactionsTypeFilter !== 'all' ||
                                    transactionsStatusFilter !== 'all' ||
                                    transactionsMerchantFilter !== 'all' ||
                                    transactionsProviderTypeFilter !== 'all' ||
                                    transactionsProviderFilter !== 'all' ||
                                    transactionsSearchQuery.trim() ||
                                    transactionsStartDate ||
                                    transactionsEndDate ||
                                    transactionsRequisiteType !== 'all' ||
                                    transactionsFiatCurrency !== 'all' ||
                                    transactionsAmountMin ||
                                    transactionsAmountMax ||
                                    transactionsRequisitesSearch.trim() ||
                                    transactionsSortBy !== 'created_at' ||
                                    transactionsSortOrder !== 'desc') && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                                setTransactionsTypeFilter('all');
                                                setTransactionsStatusFilter('all');
                                                setTransactionsMerchantFilter('all');
                                                setTransactionsProviderTypeFilter('all');
                                                setTransactionsProviderFilter('all');
                                                setTransactionsSearchQuery('');
                                                setDebouncedTransactionsSearch('');
                                                setTransactionsStartDate('');
                                                setTransactionsEndDate('');
                                                setTransactionsRequisiteType('all');
                                                setTransactionsFiatCurrency('all');
                                                setTransactionsAmountMin('');
                                                setTransactionsAmountMax('');
                                                setTransactionsRequisitesSearch('');
                                                setTransactionsSortBy('created_at');
                                                setTransactionsSortOrder('desc');
                                                resetTransactionsPage();
                                            }}
                                            className="text-slate-500 dark:text-slate-400"
                                        >
                                            Сбросить фильтры
                                        </Button>
                                    )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            {transactionsLoading ? (
                                <div className="text-center py-12">
                                    <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2 text-slate-400" />
                                    <p className="text-slate-500 dark:text-slate-400">Загрузка транзакций...</p>
                                </div>
                            ) : transactionsError ? (
                                <p role="alert" className="py-4 text-red-600 dark:text-red-400">{transactionsError}</p>
                            ) : transactions.length === 0 ? (
                                <div className="text-center py-12 text-slate-400">
                                    <ArrowRightLeft className="h-12 w-12 mx-auto mb-4 opacity-30" />
                                    <p>Транзакции не найдены</p>
                                </div>
                            ) : (
                                <AdminTransactionsTable
                                    transactions={transactions}
                                    expandedTransactionIds={expandedTransactionIds}
                                    onToggle={toggleTransactionDetails}
                                    sortBy={transactionsSortBy}
                                    sortOrder={transactionsSortOrder}
                                    onSort={handleTransactionSort}
                                />
                            )}
                            <div className="mt-6 border-t pt-4">
                                <Pagination
                                    currentPage={transactionsPagination.page}
                                    totalPages={transactionsPagination.pages}
                                    onPageChange={handleTransactionsPageChange}
                                    disabled={transactionsLoading}
                                    totalItems={transactionsPagination.total}
                                    pageSize={transactionsPageSize}
                                    onPageSizeChange={(size) => {
                                        setTransactionsPageSize(size);
                                        setRequestedTransactionsPage(1);
                                    }}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="support">
                    <Tabs defaultValue="employees">
                        <TabsList className="mb-4">
                            <TabsTrigger value="employees">Сотрудники</TabsTrigger>
                            <TabsTrigger value="merchant-chats">Чаты мерчантов</TabsTrigger>
                            <TabsTrigger value="executor-chats">Чаты исполнителей</TabsTrigger>
                        </TabsList>
                        <TabsContent value="employees">
                            <Card>
                                <CardHeader>
                                    <CardTitle>Управление поддержкой</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="divide-y mb-6">
                                        {support.map((s) => (
                                            <div key={s._id} className="py-4 flex justify-between items-center">
                                                <div>
                                                    <p className="font-medium">{s.username}</p>
                                                    <p className="text-sm text-slate-500 dark:text-slate-400">{s.email}</p>
                                                    <div className="flex items-center gap-3 mt-1">
                                                        <p className="text-xs text-slate-400">ID: {s._id}</p>
                                                        {s.created_at && (
                                                            <span className="flex items-center gap-1 text-xs text-slate-400" title="Дата регистрации">
                                                                <CalendarDays className="h-3 w-3" />
                                                                {new Date(s.created_at).toLocaleString('ru-RU')}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <Badge variant={s.is_active ? "success" : "destructive"}>
                                                        {s.is_active ? "Активен" : "Отключен"}
                                                    </Badge>
                                                    <Switch
                                                        checked={s.is_active}
                                                        onCheckedChange={() => toggleStatus('support', s._id, s.is_active)}
                                                        disabled={!canManage || togglingStatus.has(s._id)}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {supportPagination.pages > 1 && (
                                        <div className="border-t pt-6">
                                            <Pagination
                                                currentPage={supportPagination.page}
                                                totalPages={supportPagination.pages}
                                                onPageChange={(page) => setSupportPagination({ ...supportPagination, page })}
                                                totalItems={supportPagination.total}
                                                pageSize={50}
                                            />
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        </TabsContent>
                        <TabsContent value="merchant-chats"><TelegramAppealChats role="merchant" canManage={canManage} /></TabsContent>
                        <TabsContent value="executor-chats"><TelegramAppealChats role="executor" canManage={canManage} /></TabsContent>
                    </Tabs>
                    <TelegramAppealDeliveries canManage={canManage} />
                </TabsContent>

                <TabsContent value="logs">
                    <Card>
                        <CardHeader>
                            <CardTitle>Логи аудита и системы</CardTitle>
                            <CardDescription>История изменений, статусов и системных предупреждений</CardDescription>
                        </CardHeader>
                        <CardContent>
                            <div className="divide-y text-sm">
                                {auditLogs.map((log) => (
                                    <div key={log._id} className="py-3">
                                        <div className="flex justify-between items-start">
                                            <div>
                                                <span className="font-bold text-slate-700 dark:text-slate-200">{log.admin_username}</span>
                                                <span className="mx-2 text-slate-400">→</span>
                                                <Badge variant="outline" className="text-[10px] uppercase">{log.action}</Badge>
                                                <span className="ml-2 text-slate-600 dark:text-slate-300">{log.target_type} ({log.target_id?.slice(-6)})</span>
                                            </div>
                                            <span className="text-xs text-slate-400">{new Date(log.created_at).toLocaleString()}</span>
                                        </div>
                                        {log.details && (
                                            <pre className="mt-2 text-[10px] bg-slate-50 dark:bg-slate-900 p-2 rounded overflow-x-auto">
                                                {JSON.stringify(log.details, null, 2)}
                                            </pre>
                                        )}
                                    </div>
                                ))}
                            </div>
                            {logsPagination.pages > 1 && (
                                <div className="mt-6 border-t pt-4">
                                    <Pagination
                                        currentPage={logsPagination.page}
                                        totalPages={logsPagination.pages}
                                        onPageChange={(page) => setLogsPagination({ ...logsPagination, page })}
                                        totalItems={logsPagination.total}
                                        pageSize={15}
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="constant-rates" className="space-y-6">
                    <ConstantRatesPanel rates={currencyRates} readOnly={!canManage} onSaved={refreshData} />
                </TabsContent>

                <TabsContent value="settings">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Globe className="h-5 w-5" /> Глобальные настройки платформы
                            </CardTitle>
                            <CardDescription>Изменение системных параметров для всех пользователей</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="max-w-2xl space-y-6">
                                <div className="space-y-2">
                                    <Label htmlFor="deposit_fee">Комиссия за депозит USDT (фиксированная, в $)</Label>
                                    <Input
                                        id="deposit_fee"
                                        type="number"
                                        step="0.1"
                                        min="0"
                                        value={platformSettings?.deposit_fee_usd ?? ''}
                                        onChange={(e) => setPlatformSettings({ ...platformSettings, deposit_fee_usd: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                        disabled={!canManage}
                                    />
                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                        Эта комиссия вычитается из каждого входящего депозита трейдера при зачислении на баланс.
                                    </p>
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="merchant_api_rate_limit">Лимит запросов Merchant API за 5 секунд</Label>
                                    <Input
                                        id="merchant_api_rate_limit"
                                        type="number"
                                        step="1"
                                        min="1"
                                        value={platformSettings?.merchant_api_requests_per_5_seconds ?? 5}
                                        onChange={(e) => setPlatformSettings({ ...platformSettings, merchant_api_requests_per_5_seconds: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                                        disabled={!canManage}
                                    />
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Лимит применяется отдельно к каждому мерчанту. После превышения API отвечает 429.</p>
                                </div>
                                <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
                                    <div className="space-y-1">
                                        <Label htmlFor="merchant_api_mixpanel">Analytics: Merchant API Request Received</Label>
                                        <p className="text-xs text-slate-500 dark:text-slate-400">Отправка в Mixpanel и OpenPanel. Хранение события в журнале Merchant API продолжает работать.</p>
                                    </div>
                                    <Switch
                                        id="merchant_api_mixpanel"
                                        checked={platformSettings?.merchant_api_request_mixpanel_enabled ?? true}
                                        onCheckedChange={(checked) => setPlatformSettings({ ...platformSettings, merchant_api_request_mixpanel_enabled: checked })}
                                        disabled={!canManage}
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label>Допустимая сумма операции Merchant API, RUB</Label>
                                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                        <Input
                                            aria-label="Минимальная сумма Merchant API"
                                            type="number"
                                            min="0"
                                            step="1"
                                            value={platformSettings?.merchant_api_min_amount ?? 1}
                                            onChange={(e) => setPlatformSettings({ ...platformSettings, merchant_api_min_amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                            placeholder="Минимум"
                                            disabled={!canManage}
                                        />
                                        <Input
                                            aria-label="Максимальная сумма Merchant API"
                                            type="number"
                                            min="0.01"
                                            step="1"
                                            value={platformSettings?.merchant_api_max_amount ?? 1000000}
                                            onChange={(e) => setPlatformSettings({ ...platformSettings, merchant_api_max_amount: e.target.value === '' ? '' : parseFloat(e.target.value) })}
                                            placeholder="Максимум"
                                            disabled={!canManage}
                                        />
                                    </div>
                                    <p className="text-xs text-slate-500 dark:text-slate-400">Запросы вне диапазона сразу получают 404 до поиска реквизитов, курсов и расчётов.</p>
                                </div>
                                <Button
                                    onClick={updatePlatformSettings}
                                    className="bg-slate-900"
                                    disabled={!canManage || !platformSettingsValid}
                                >
                                    <Save className="h-4 w-4 mr-2" /> Сохранить настройки
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
};

const formatNumber = (value?: number | null, maximumFractionDigits = 2) => (
    typeof value === 'number' && Number.isFinite(value)
        ? value.toLocaleString('ru-RU', { maximumFractionDigits })
        : '-'
);

const formatMerchantCommission = (tx: any) => {
    if (tx.order_type === 'withdraw') {
        return formatAmount(tx.merchant_commission_usdt, 'USDT', 6);
    }

    return formatAmount(tx.merchant_commission_amount, 'RUB');
};

function olderThanFiveMinutes(value: string | null | undefined, now: number): boolean {
    if (!value) return false;
    const utc = /(?:Z|[+-]\d{2}:\d{2})$/.test(value) ? value : `${value}Z`;
    return now - new Date(utc).getTime() > 300000;
}

export const CurrencyRatesCard: React.FC<{ rates: AdminCurrencyRate[], onRefresh: () => void }> = ({ rates, onRefresh }) => {
    const [now, setNow] = useState(Date.now);
    useEffect(() => {
        const timer = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(timer);
    }, []);
    return <Card>
        <CardHeader className="flex flex-row items-center justify-between">
            <div>
                <CardTitle className="flex items-center gap-2">
                    <ArrowRightLeft className="h-5 w-5 text-emerald-600 dark:text-emerald-300" /> Курсы fiat/USDT
                </CardTitle>
                <CardDescription>Курсы RUB, KGS и KZT: P2P, XE и постоянные значения</CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={onRefresh} title="Обновить данные dashboard">
                <RefreshCw className="h-4 w-4" />
            </Button>
        </CardHeader>
        <CardContent>
            <div className="space-y-3">
                {rates.length > 0 ? rates.map((rate) => {
                    const markupPercent = rate.markup_percent ?? rate.configured_markup_percent;

                    return (
                        <div
                            key={`${rate.from_currency}:${rate.source}`}
                            className={`rounded-md border p-3 ${rate.is_default ? 'border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/50' : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'}`}
                        >
                            <div className="mb-3 flex items-start justify-between gap-3">
                                <div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="font-semibold text-slate-800 dark:text-slate-100">{rate.source_label}</span>
                                        {rate.is_default && (
                                            <Badge className="bg-emerald-600">Дефолт</Badge>
                                        )}
                                        {!rate.has_data && (
                                            <Badge variant="outline" className="border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-950 text-orange-700 dark:text-orange-300">
                                                Нет данных
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="mt-1 font-mono text-xs text-slate-400">{rate.source}</div>
                                </div>
                                <div className="text-right text-xs text-slate-400">
                                    {rate.source === 'XE_USD_KZT' ? (
                                        <>
                                            <div>Получен: {formatUTCDateTime(rate.updated_at)}</div>
                                            <div>Котировка: {formatUTCDateTime(rate.quoted_at ?? null)}</div>
                                            <div>Опрос: раз в {rate.rate_update_interval_seconds} сек.</div>
                                        </>
                                    ) : formatUTCDateTime(rate.updated_at)}
                                </div>
                            </div>

                            {rate.description && <p className="mb-2 text-sm text-slate-500">{rate.description}</p>}
                            {rate.source === 'XE_USD_KZT' && rate.has_data && (
                                <div className="mb-2 text-sm text-amber-600" role="status">
                                    {olderThanFiveMinutes(rate.updated_at, now) && (
                                        <div>Нет успешного получения более 5 минут. Расчёты продолжаются по последнему курсу.</div>
                                    )}
                                    {olderThanFiveMinutes(rate.quoted_at, now) && (
                                        <div>Котировка XE старше 5 минут.</div>
                                    )}
                                </div>
                            )}
                            <div className="grid grid-cols-3 gap-3 text-sm">
                                <div>
                                    <div className="text-xs uppercase text-slate-400">Рабочий</div>
                                    <div className="font-bold text-slate-900 dark:text-slate-100">{formatNumber(rate.rate, 4)} {rate.from_currency}</div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase text-slate-400">Базовый курс</div>
                                    <div className="font-medium text-slate-700 dark:text-slate-200">{formatNumber(rate.base_rate, 4)} {rate.from_currency}</div>
                                </div>
                                <div>
                                    <div className="text-xs uppercase text-slate-400">Наценка</div>
                                    <div className="font-medium text-slate-700 dark:text-slate-200">{formatNumber(markupPercent, 2)}%</div>
                                </div>
                            </div>
                        </div>
                    );
                }) : (
                    <div className="rounded-md border border-dashed p-4 text-center text-sm text-slate-400">
                        Курсы пока не загружены
                    </div>
                )}
            </div>
        </CardContent>
    </Card>
};

const formatDuration = (seconds?: number | null) => {
    if (typeof seconds !== 'number' || !Number.isFinite(seconds)) return '-';
    if (seconds < 60) return `${seconds} сек`;
    const minutes = Math.floor(seconds / 60);
    const restSeconds = seconds % 60;
    if (minutes < 60) return restSeconds ? `${minutes} мин ${restSeconds} сек` : `${minutes} мин`;
    const hours = Math.floor(minutes / 60);
    const restMinutes = minutes % 60;
    return restMinutes ? `${hours} ч ${restMinutes} мин` : `${hours} ч`;
};

const SortableTransactionHeader: React.FC<{
    label: string;
    sortKey: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (sortBy: string) => void;
    align?: 'left' | 'right';
    className?: string;
}> = ({ label, sortKey, sortBy, sortOrder, onSort, align = 'left', className = '' }) => {
    const active = sortBy === sortKey;

    return (
        <th
            aria-sort={active ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
            className={`py-3 px-3 font-semibold text-slate-600 dark:text-slate-300 whitespace-nowrap ${align === 'right' ? 'text-right' : 'text-left'} ${className}`}
        >
            <button
                type="button"
                onClick={() => onSort(sortKey)}
                title={`Сортировать по полю "${label}"`}
                className={`inline-flex w-full cursor-pointer select-none items-center gap-1 rounded px-1 py-0.5 ${align === 'right' ? 'justify-end' : 'justify-start'} hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-700`}
            >
                <span>{label}</span>
                <ArrowUpDown className={`h-3.5 w-3.5 ${active ? 'text-slate-900 dark:text-slate-100' : 'text-slate-300'}`} />
                {active && (
                    <span className="text-[10px] font-bold text-slate-500 dark:text-slate-400">
                        {sortOrder === 'asc' ? '↑' : '↓'}
                    </span>
                )}
            </button>
        </th>
    );
};

const AdminTransactionsTable: React.FC<{
    transactions: any[];
    expandedTransactionIds: Set<string>;
    onToggle: (transactionId: string) => void;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    onSort: (sortBy: string) => void;
}> = ({ transactions, expandedTransactionIds, onToggle, sortBy, sortOrder, onSort }) => (
    <div className="overflow-x-auto">
        <table className="w-full text-sm">
            <thead>
                <tr className="border-b-2 border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900">
                    <th className="min-w-[150px] px-3 py-3 text-left font-semibold text-slate-600 dark:text-slate-300">Мерчант</th>
                    <SortableTransactionHeader label="ID наш" sortKey="transaction_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[220px]" />
                    <SortableTransactionHeader label="Дата создания ордера" sortKey="created_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[180px]" />
                    <SortableTransactionHeader label="Дата финализации" sortKey="closed_at" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[180px]" />
                    <SortableTransactionHeader label="Метод" sortKey="requisite_type" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[150px]" />
                    <SortableTransactionHeader label="Сумма" sortKey="amount" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} align="right" className="min-w-[170px]" />
                    <SortableTransactionHeader label="Статус" sortKey="status" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[140px]" />
                    <SortableTransactionHeader label="Провайдер" sortKey="provider" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[150px]" />
                    <SortableTransactionHeader label="ID мерчанта" sortKey="merchant_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[180px]" />
                    <th className="min-w-[180px] max-w-[280px] px-3 py-3 text-left font-semibold text-slate-600 dark:text-slate-300">Customer ID</th>
                    <th className="min-w-[130px] px-3 py-3 text-left font-semibold text-slate-600 dark:text-slate-300">Тип трафика</th>
                    <SortableTransactionHeader label="ID провайдера" sortKey="provider_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[200px]" />
                    <SortableTransactionHeader label="Order ID провайдера" sortKey="external_order_id" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[220px]" />
                    <SortableTransactionHeader label="Данные реквизита" sortKey="requisites" sortBy={sortBy} sortOrder={sortOrder} onSort={onSort} className="min-w-[320px]" />
                </tr>
            </thead>
            <tbody>
                {transactions.map((tx) => {
                    const isExpanded = expandedTransactionIds.has(tx.transaction_id);
                    const isDeposit = tx.order_type === 'deposit';
                    const closedAt = tx.closed_at || tx.completed_at || tx.cancelled_at;
                    return (
                        <React.Fragment key={tx.transaction_id}>
                            <tr
                                className="border-b border-slate-100 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer text-sm"
                                onClick={() => onToggle(tx.transaction_id)}
                            >
                                <td className="py-3 px-3 align-top">
                                    <div className="flex items-start gap-2">
                                        {isExpanded ? <ChevronUp className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" /> : <ChevronDown className="h-4 w-4 mt-0.5 text-slate-400 flex-shrink-0" />}
                                        <div>
                                            <div className="font-medium text-slate-800 dark:text-slate-100">
                                                {tx.merchant_username || tx.merchant_name || '-'}
                                            </div>
                                            {tx.merchant_username && tx.merchant_name && tx.merchant_name !== tx.merchant_username && (
                                                <div className="mt-0.5 text-xs text-slate-400">{tx.merchant_name}</div>
                                            )}
                                        </div>
                                    </div>
                                </td>
                                <td className="py-3 px-3 align-top">
                                    <div className="min-w-0">
                                        <div className="font-mono text-xs text-slate-700 dark:text-slate-200 break-all">{tx.transaction_id}</div>
                                        <Badge variant="outline" className={`mt-1 text-[10px] ${isDeposit ? 'text-green-700 dark:text-green-300 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950' : 'text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950'}`}>
                                            {isDeposit ? 'Deposit' : 'Withdraw'}
                                        </Badge>
                                    </div>
                                </td>
                                <td className="py-3 px-3 align-top text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                    {formatUTCDateTime(tx.created_at)}
                                </td>
                                <td className="py-3 px-3 align-top text-xs text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                    {formatUTCDateTime(closedAt)}
                                </td>
                                <td className="py-3 px-3 align-top">
                                    <Badge variant="outline" className={`text-xs ${tx.requisite_type ? 'bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' : 'bg-red-50 dark:bg-red-950 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'}`}>
                                        {getTransactionRequisiteType(tx)}
                                    </Badge>
                                </td>
                                <td className="py-3 px-3 align-top text-right whitespace-nowrap">
                                    <div className="font-semibold text-slate-800 dark:text-slate-100">{formatAmount(tx.amount_fiat ?? tx.amount, tx.fiat_currency || tx.currency)}</div>
                                    <div className="text-xs text-slate-400">{formatAmount(tx.amount_usdt, 'USDT', 6)}</div>
                                </td>
                                <td className="py-3 px-3 align-top">
                                    <TransactionStatusBadge status={tx.status} hasReceipt={tx.has_receipt} />
                                </td>
                                <td className="py-3 px-3 align-top text-slate-700 dark:text-slate-200">
                                    {tx.provider_name || '-'}
                                </td>
                                <td className="py-3 px-3 align-top font-mono text-xs text-slate-700 dark:text-slate-200 break-all">
                                    {getMerchantTransactionId(tx)}
                                </td>
                                <td className="py-3 px-3 align-top font-mono text-xs text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                    {tx.customer_id == null
                                        ? 'Не передан'
                                        : tx.customer_id.length > 20
                                            ? `${tx.customer_id.slice(0, 20)}…`
                                            : tx.customer_id}
                                </td>
                                <td className="py-3 px-3 align-top text-xs text-slate-700 dark:text-slate-200 whitespace-nowrap">
                                    {tx.traffic_type?.toUpperCase() || 'Не передан'}
                                </td>
                                <td className="py-3 px-3 align-top font-mono text-xs text-slate-700 dark:text-slate-200 break-all">
                                    {tx.provider_id || '-'}
                                </td>
                                <td className="py-3 px-3 align-top font-mono text-xs text-slate-700 dark:text-slate-200 break-all">
                                    {tx.external_order_id || '-'}
                                </td>
                                <td className="py-3 px-3 align-top text-xs text-slate-700 dark:text-slate-200 min-w-[320px]">
                                    {formatRequisites(tx.requisites, tx.fiat_currency || tx.currency)}
                                </td>
                            </tr>
                            {isExpanded && (
                                <tr className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
                                    <td colSpan={14} className="p-4">
                                        <TransactionDetails tx={tx} />
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>
                    );
                })}
            </tbody>
        </table>
    </div>
);

const TransactionDetails: React.FC<{ tx: any }> = ({ tx }) => {
    const isDeposit = tx.order_type === 'deposit';
    const isExternal = Boolean(tx.external_platform_id);
    const requisitesTitle = isDeposit ? 'Реквизиты трейдера' : 'Реквизиты пользователя';
    const closedAt = tx.closed_at || tx.completed_at || tx.cancelled_at;
    const executorRate = isExternal ? tx.external_exchange_rate : tx.trader_exchange_rate;
    const executorRateLabel = isExternal ? 'Финальный курс каскада' : 'Финальный курс трейдера';
    const executorRateSource = isExternal ? tx.external_rate_source : tx.trader_rate_source;
    const executorRateSourceLabel = isExternal ? 'Источник курса каскада' : 'Источник курса трейдера';

    return (
        <div className="space-y-4">
            <TransactionField label="Customer ID" value={tx.customer_id ?? 'Не передан'} mono />
            <div>
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <Route className="h-4 w-4" />
                    Маршрутизация · общее время выдачи реквизита: {formatDuration(tx.routing_total_seconds)}
                </div>
                <div className="divide-y rounded-md border">
                    {(tx.routing || []).map((route: any, index: number) => (
                        <div key={`${tx.transaction_id}-route-${index}`} className="grid grid-cols-1 md:grid-cols-7 gap-2 p-3 text-xs">
                            <TransactionField label="Провайдер" value={route.provider_name || route.provider_id || '-'} />
                            <TransactionField label="ID провайдера" value={route.provider_id || '-'} mono />
                            <TransactionField label="Order ID провайдера" value={route.provider_order_id || '-'} mono />
                            <TransactionField label="ID настройки" value={route.provider_platform_id || '-'} mono />
                            <TransactionField label="Статус провайдера" value={route.status || '-'} />
                            <TransactionField label="Время выдачи" value={formatDuration(route.duration_seconds)} />
                            <TransactionField label="Выдано" value={formatUTCDateTime(route.requisites_issued_at)} />
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                    <FileText className="h-4 w-4" />
                    Логи
                </div>
                <div className="rounded-md border divide-y">
                    {(tx.logs || []).map((log: any, index: number) => (
                        <div key={`${tx.transaction_id}-log-${index}`} className="grid grid-cols-1 md:grid-cols-[220px_180px_1fr] gap-2 p-3 text-xs">
                            <span className="font-mono text-slate-500 dark:text-slate-400">{formatUTCDateTime(log.time)}</span>
                            <span className="font-medium text-slate-700 dark:text-slate-200">{log.event}</span>
                            <span className="text-slate-600 dark:text-slate-300">{log.message}</span>
                        </div>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <TransactionField label="Создано" value={formatUTCDateTime(tx.created_at)} />
                <TransactionField label="Время изменения статуса платежа" value={formatUTCDateTime(tx.status_changed_at)} />
                <TransactionField label="Время закрытия ордера" value={formatUTCDateTime(closedAt)} />
            </div>

            <div>
                <div className="mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">Метрики скорости</div>
                <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3 rounded-md border p-3 text-xs">
                    <TransactionField label="До выдачи реквизитов" value={formatDuration(tx.routing_total_seconds)} />
                    <TransactionField label="До загрузки чека" value={formatDuration(tx.time_to_receipt_seconds)} />
                    <TransactionField label="Проверка чека" value={formatDuration(tx.receipt_to_confirmation_seconds)} />
                    <TransactionField label="От подтверждения до финала" value={formatDuration(tx.confirmation_to_finalization_seconds)} />
                    <TransactionField label="От выдачи до финала" value={formatDuration(tx.requisites_to_finalization_seconds)} />
                    <TransactionField label="Полный цикл до финала" value={formatDuration(tx.total_finalization_seconds)} />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                <TransactionField label="Цена" value={formatNumber(tx.exchange_rate, 2)} />
                {tx.order_type === 'deposit' && tx.merchant_amount_adjustment_version === 1 && tx.merchant_amount != null && <>
                    <TransactionField label="Сумма мерчанта" value={formatAmount(tx.merchant_amount, tx.payer_currency, 2)} />
                    <TransactionField label="Прибавка мерчанта" value={formatAmount(tx.merchant_amount_adjustment, tx.payer_currency, 2)} />
                </>}
                <TransactionField label={tx.external_platform_id && tx.order_type === 'deposit' ? 'Сумма каскаду' : 'Оплаченная сумма'} value={formatAmount(tx.payer_amount, tx.payer_currency, tx.payer_currency === 'USDT' ? 6 : 2)} />
                <TransactionField label="Комиссия с мерчанта" value={formatMerchantCommission(tx)} />
                <TransactionField label="Комиссия с мерчанта, %" value={tx.merchant_commission_rate != null ? `${formatNumber(tx.merchant_commission_rate, 2)}%` : '-'} />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs">
                {tx.external_platform_id && <>
                    <TransactionField label="Каскад: заявленная сумма" value={formatAmount(tx.external_reported_usdt, 'USDT', 2)} />
                    <TransactionField label="Каскад: наш расчёт" value={formatAmount(tx.external_net_usdt, 'USDT', 6)} />
                </>}
                <TransactionField label="Наш финальный курс" value={formatNumber(tx.merchant_exchange_rate ?? tx.exchange_rate, 4)} />
                <TransactionField label={executorRateLabel} value={formatNumber(executorRate, 4)} />
                <TransactionField label="Списание трейдера" value={formatAmount(tx.trader_net_debit_usdt, 'USDT', 6)} />
                <TransactionField label="Прибыль площадки" value={formatAmount(tx.platform_profit_usdt, 'USDT', 6)} />
                <TransactionField label="Маржа площадки" value={tx.platform_profit_percent != null ? `${formatNumber(tx.platform_profit_percent, 3)}%` : '-'} />
                <TransactionField label="Источник нашего курса" value={tx.merchant_rate_source || '-'} />
                <TransactionField label={executorRateSourceLabel} value={executorRateSource || '-'} />
                <TransactionField label="Safety" value={tx.rate_safety_applied ? 'Применен' : 'Не применен'} />
            </div>

            {tx.requisites && (
                <div>
                    <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
                        <CreditCard className="h-4 w-4" />
                        {requisitesTitle}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-xs rounded-md border p-3">
                        <TransactionField label="Банк" value={bankDisplayName(tx.requisites.recipient_bank) || '-'} />
                        <TransactionField label="Получатель" value={tx.requisites.recipient_name || '-'} />
                        <TransactionField label="Карта" value={tx.requisites.recipient_card || '-'} mono />
                        <TransactionField label="Телефон" value={tx.requisites.phone_number || '-'} mono />
                    </div>
                </div>
            )}
        </div>
    );
};

const TransactionField: React.FC<{ label: string, value: React.ReactNode, mono?: boolean, wide?: boolean, badgeClass?: string }> = ({ label, value, mono, wide, badgeClass }) => (
    <div className={wide ? 'md:col-span-2 xl:col-span-4' : ''}>
        <div className="text-[11px] uppercase text-slate-400">{label}</div>
        {badgeClass ? (
            <Badge variant="outline" className={`mt-1 max-w-full break-all ${badgeClass}`}>
                {value}
            </Badge>
        ) : (
            <div className={`mt-1 text-slate-700 dark:text-slate-200 break-all ${mono ? 'font-mono' : 'font-medium'}`}>
                {value}
            </div>
        )}
    </div>
);

const TransactionStatusBadge: React.FC<{ status: string; hasReceipt?: boolean }> = ({ status, hasReceipt }) => {
    switch (status) {
        case 'assigned':
            return <Badge variant="secondary">Назначен</Badge>;
        case 'pending_requisites':
            return <Badge variant="outline" className="text-yellow-700 dark:text-yellow-300 border-yellow-400 bg-yellow-50 dark:bg-yellow-950">Ожидает реквизитов</Badge>;
        case 'receipt_uploaded':
            return <Badge variant="default">{hasReceipt ? 'Чек загружен' : 'Клиент сообщил об оплате'}</Badge>;
        case 'confirmed':
            return <Badge className="bg-blue-600">Подтвержден</Badge>;
        case 'completed':
            return <Badge className="bg-green-600">Завершен</Badge>;
        case 'cancelled':
            return <Badge variant="outline" className="text-red-600 dark:text-red-300 border-red-300 dark:border-red-800">Отменен</Badge>;
        case 'disputed':
            return <Badge variant="outline" className="text-orange-600 dark:text-orange-300 border-orange-300 dark:border-orange-800 bg-orange-50 dark:bg-orange-950">Спор</Badge>;
        case 'no_traders':
            return <Badge variant="outline" className="text-gray-500 dark:text-slate-400 border-gray-300 dark:border-slate-700 bg-gray-50 dark:bg-slate-900">Нет трейдеров</Badge>;
        default:
            return <Badge variant="outline">{status}</Badge>;
    }
};

const StatCard: React.FC<{ title: string, value: any, icon: React.ReactNode, suffix?: string }> = ({ title, value, icon, suffix = "" }) => (
    <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">{title}</CardTitle>
            {icon}
        </CardHeader>
        <CardContent>
            <div className="text-2xl font-bold">{value || '0'}{suffix}</div>
        </CardContent>
    </Card>
);

const ExternalPlatformCard: React.FC<{
    platform: any,
    onToggle: () => void,
    onUpdate: (settings: any) => void,
    isToggling: boolean,
    currencyRates: AdminCurrencyRate[],
    readOnly: boolean,
}> = ({ platform, onToggle, onUpdate, isToggling, currencyRates, readOnly }) => {
    const [showStatistics, setShowStatistics] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [showTechnicalSettings, setShowTechnicalSettings] = useState(false);
    const hasConfiguredCredentials = (() => {
        if (!platform.has_platform_token) return false;
        if (['youpay', 'youpay_kgs'].includes(platform.adapter_type)) {
            return Boolean(platform.external_merchant_id && platform.external_merchant_login);
        }
        if (platform.adapter_type === 'noros') return Boolean(platform.has_webhook_secret);
        if (['fynzah', 'bnpay', 'sarex', 'gotham'].includes(platform.adapter_type)) {
            return Boolean(platform.has_webhook_secret && platform.external_merchant_id);
        }
        if (['swpay', 'swpay_rub'].includes(platform.adapter_type)) {
            return Boolean(platform.external_key_id);
        }
        if (platform.adapter_type === 'flintpays') {
            return Boolean(platform.external_merchant_id);
        }
        return true;
    })();
    const [showCredentials, setShowCredentials] = useState(!hasConfiguredCredentials);
    const platformFiatCurrency = (platform.fiat_currency || 'RUB') as 'RUB' | 'KGS' | 'KZT';
    const adapterRequisiteTypes = externalAdapterRequisiteTypes(platform.adapter_type, platformFiatCurrency);
    const rawPersistedRequisiteTypes = (
        platform.requisite_types?.length ? platform.requisite_types : ['card', 'sbp']
    ) as RequisiteType[];
    const initialRequisiteTypes = rawPersistedRequisiteTypes.filter((item) =>
        adapterRequisiteTypes.includes(item)
    );
    const [settings, setSettings] = useState({
        name: platform.name || '',
        fiat_currency: (platform.fiat_currency || 'RUB') as 'RUB' | 'KGS' | 'KZT',
        adapter_type: platform.adapter_type || 'compatible_merchant_api',
        base_url: platform.base_url || '',
        platform_token: '',
        webhook_secret: '',
        external_merchant_id: platform.external_merchant_id || '',
        external_merchant_login: platform.external_merchant_login || '',
        external_key_id: platform.external_key_id || '',
        payment_type_mapping: {
            card: platform.payment_type_mapping?.card || 'any_bank',
            sbp: platform.payment_type_mapping?.sbp || 'sbp',
            sbp_kgs: platform.payment_type_mapping?.sbp_kgs || 'sbp',
            mobile_kgs: platform.payment_type_mapping?.mobile_kgs || 'mobile_commerce_rf',
            qr_vietnam_sber: platform.payment_type_mapping?.qr_vietnam_sber || 'sber_s',
            qr_vietnam_vtb: platform.payment_type_mapping?.qr_vietnam_vtb || 'vtb_s',
            transgran_card_rub_arm: platform.payment_type_mapping?.transgran_card_rub_arm || platform.payment_type_mapping?.vtb_arm || platform.payment_type_mapping?.card || 'Card',
            transgran_mobile_rub_arm: platform.payment_type_mapping?.transgran_mobile_rub_arm || platform.payment_type_mapping?.sbp || 'ruFpsPhone',
            elqr: platform.payment_type_mapping?.elqr || '',
        },
        debug_payload_capture: platform.debug_payload_capture ?? false,
        priority: platform.priority ?? 100,
        deposit_enabled: platform.deposit_enabled ?? true,
        withdraw_enabled: platform.withdraw_enabled ?? true,
        min_amount: platform.min_amount ?? 1,
        max_amount: platform.max_amount ?? 1000000,
        requisite_types: initialRequisiteTypes.length > 0
            ? initialRequisiteTypes
            : adapterRequisiteTypes,
        insurance_balance: platform.insurance_balance ?? 1000,
        trust_deposit: platform.trust_deposit ?? 0,
        deposit_commission_rate: platform.deposit_commission_rate ?? 9,
        withdraw_commission_rate: platform.withdraw_commission_rate ?? 4,
        deposit_commission_tiers: normalizeCommissionTiers(platform.deposit_commission_tiers),
        withdraw_commission_tiers: normalizeCommissionTiers(platform.withdraw_commission_tiers),
        deposit_commission_tiers_by_requisite_type: normalizeCommissionTierMap(platform.deposit_commission_tiers_by_requisite_type),
        withdraw_commission_tiers_by_requisite_type: normalizeCommissionTierMap(platform.withdraw_commission_tiers_by_requisite_type),
        rate_source: platform.rate_source ?? (platform.fiat_currency === 'KGS' ? 'BYBIT_P2P_API' : 'RAPIRA_P2P_API'),
        rate_markup_percent: platform.rate_markup_percent ?? 6,
        webhook_url: platform.webhook_url || '',
        notes: platform.notes || '',
    });
    const platformRates = currencyRates.filter((item) => item.from_currency === settings.fiat_currency);
    const selectedRate = platformRates.find((item) => item.source === settings.rate_source);
    const baseRate = selectedRate?.base_rate ?? selectedRate?.rate ?? null;
    const previewRate = baseRate != null
        ? baseRate * (1 + (Number(settings.rate_markup_percent) || 0) / 100)
        : null;
    const commissionError = validateCommissionTiers(settings.deposit_commission_tiers)
        || validateCommissionTiers(settings.withdraw_commission_tiers)
        || validateCommissionTierMap(settings.deposit_commission_tiers_by_requisite_type)
        || validateCommissionTierMap(settings.withdraw_commission_tiers_by_requisite_type);

    const save = () => {
        if (commissionError) {
            alert(commissionError);
            return;
        }
        const payload: any = { ...settings };
        payload.requisite_types = payload.requisite_types.filter((item: RequisiteType) =>
            externalAdapterRequisiteTypes(payload.adapter_type, payload.fiat_currency).includes(item)
        );
        payload.deposit_commission_tiers = normalizeCommissionTiers(payload.deposit_commission_tiers);
        payload.withdraw_commission_tiers = normalizeCommissionTiers(payload.withdraw_commission_tiers);
        payload.deposit_commission_tiers_by_requisite_type = normalizeCommissionTierMap(payload.deposit_commission_tiers_by_requisite_type);
        payload.withdraw_commission_tiers_by_requisite_type = normalizeCommissionTierMap(payload.withdraw_commission_tiers_by_requisite_type);
        if (!payload.platform_token.trim()) delete payload.platform_token;
        if (!payload.webhook_secret.trim()) delete payload.webhook_secret;
        payload.external_merchant_id = payload.external_merchant_id.trim() || undefined;
        payload.external_merchant_login = payload.external_merchant_login.trim() || undefined;
        payload.external_key_id = payload.external_key_id.trim() || undefined;
        payload.payment_type_mapping = Object.fromEntries(
            Object.entries(payload.payment_type_mapping).map(([key, value]) => [key, String(value).trim()])
        );
        onUpdate(payload);
    };

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                        <Route className="h-5 w-5" />
                        {platform.name}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-2 flex-wrap">
                        <span>{platform.adapter_type}</span>
                        <Badge variant="outline">{platform.fiat_currency || 'RUB'}</Badge>
                        <Badge variant="outline">Priority {platform.priority}</Badge>
                    </CardDescription>
                </div>
                <div className="flex items-center gap-4">
                    <Button type="button" variant="outline" size="sm" aria-expanded={showStatistics} onClick={() => setShowStatistics(!showStatistics)}>
                        <BarChart3 className="mr-2 h-4 w-4" />Статистика
                    </Button>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                            if (isExpanded) {
                                setShowTechnicalSettings(false);
                                setShowCredentials(false);
                            }
                            setIsExpanded(!isExpanded);
                        }}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Скрыть настройки каскада' : 'Показать настройки каскада'}
                        title={isExpanded ? 'Скрыть настройки' : 'Показать настройки'}
                    >
                        {isExpanded
                            ? <ChevronUp className="h-5 w-5 text-slate-500 dark:text-slate-400" />
                            : <ChevronDown className="h-5 w-5 text-slate-500 dark:text-slate-400" />}
                    </Button>
                    <Badge variant={platform.is_active ? "success" : "destructive"}>
                        {platform.is_active ? "Активна" : "Отключена"}
                    </Badge>
                    <Switch checked={platform.is_active} onCheckedChange={onToggle} disabled={readOnly || isToggling} />
                </div>
            </CardHeader>
            {showStatistics && <CardContent><ExternalPlatformStatistics platformId={platform._id} /></CardContent>}
            {isExpanded && (
                <fieldset disabled={readOnly} className={readOnly ? 'opacity-90' : undefined}>
                    <CardContent className="space-y-6">
                        <div className="flex items-center justify-between gap-4 rounded-md border bg-slate-50 dark:bg-slate-900 p-4">
                            <div>
                                <Label>Технические настройки</Label>
                                <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                    Base URL, webhook, доступы, маппинг и методы скрыты.
                                </p>
                            </div>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                    if (showTechnicalSettings) setShowCredentials(false);
                                    setShowTechnicalSettings(!showTechnicalSettings);
                                }}
                            >
                                {showTechnicalSettings ? 'Скрыть технические настройки' : 'Показать технические настройки'}
                            </Button>
                        </div>

                        {showTechnicalSettings && (
                            <>
                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                    <div className="space-y-2">
                                        <Label>Название</Label>
                                        <Input value={settings.name} onChange={(e) => setSettings({ ...settings, name: e.target.value })} />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Adapter type</Label>
                                        <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            value={settings.adapter_type}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                adapter_type: e.target.value,
                                                ...(['sarex', 'swpay_rub', 'gotham'].includes(e.target.value) ? { is_active: false } : {}),
                                                base_url: e.target.value === 'gotham' ? 'https://gt-union.org/api/v1' : e.target.value === 'sarex' ? 'https://api.sarex.live' : e.target.value === 'bnpay' ? 'https://s2s.bnpay.world' : e.target.value === 'noros' ? 'https://rest.noros.org/api/v1' : e.target.value === 'fynzah' && !settings.base_url.trim()
                                                    ? FYNZAH_PRODUCTION_BASE_URL
                                                    : settings.base_url,
                                                fiat_currency: ['noros', 'sarex'].includes(e.target.value) ? 'KZT' : e.target.value === 'youpay_kgs' ? 'KGS' : ['youpay', 'fynzah', 'swpay_rub', 'bnpay', 'gotham'].includes(e.target.value) ? 'RUB' : settings.fiat_currency,
                                                rate_source: ['noros', 'sarex', 'youpay_kgs'].includes(e.target.value) ? 'BYBIT_P2P_API' : ['youpay', 'fynzah', 'swpay_rub', 'bnpay', 'gotham'].includes(e.target.value) ? 'RAPIRA_P2P_API' : settings.rate_source,
                                                withdraw_enabled: e.target.value === 'youpay_kgs'
                                                    ? true
                                                    : ['flintpays', 'youpay', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(e.target.value)
                                                        ? false
                                                        : settings.withdraw_enabled,
                                                requisite_types: externalAdapterRequisiteTypes(e.target.value, ['bnpay', 'swpay_rub', 'gotham'].includes(e.target.value) ? 'RUB' : settings.fiat_currency),
                                                payment_type_mapping: e.target.value === 'swpay'
                                                    ? withSWPayPaymentMapping(settings.payment_type_mapping, settings.fiat_currency)
                                                    : e.target.value === 'fynzah'
                                                        ? { ...settings.payment_type_mapping, qr_vietnam_sber: 'sber_s', qr_vietnam_vtb: 'vtb_s' }
                                                        : settings.payment_type_mapping,
                                            })}
                                        >
                                            <option value="compatible_merchant_api">compatible_merchant_api</option>
                                            <option value="flintpays">flintpays</option>
                                            <option value="youpay">youpay</option>
                                            <option value="youpay_kgs">youpay_kgs</option>
                                            <option value="fynzah">fynzah</option>
                                            <option value="bnpay">Bnpay · RUB / KZT → TJS</option>
                                            <option value="swpay">swpay</option>
                                            <option value="swpay_rub">SWPay RUB → UZS</option>
                                            <option value="gotham">Gotham Trade</option>
                                                <option value="noros">NOROS</option>
                                            <option value="sarex">Sarex · KZT → GEL / TJS</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Фиатная валюта</Label>
                                        <select
                                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                            value={settings.fiat_currency}
                                            onChange={(e) => {
                                                const fiatCurrency = e.target.value as 'RUB' | 'KGS' | 'KZT';
                                                setSettings({
                                                    ...settings,
                                                    fiat_currency: fiatCurrency,
                                                    ...(settings.adapter_type === 'bnpay' ? {
                                                        min_amount: fiatCurrency === 'KZT' ? 1000 : 500,
                                                        max_amount: fiatCurrency === 'KZT' ? 250000 : 9999,
                                                        deposit_commission_rate: fiatCurrency === 'KZT' ? 5 : 12,
                                                        rate_markup_percent: fiatCurrency === 'KZT' ? 0 : 6,
                                                    } : {}),
                                                    rate_source: fiatCurrency !== 'RUB' ? 'BYBIT_P2P_API' : 'RAPIRA_P2P_API',
                                                    withdraw_enabled: settings.adapter_type === 'swpay'
                                                        ? fiatCurrency === 'KGS' && settings.withdraw_enabled
                                                        : settings.withdraw_enabled,
                                                    requisite_types: ['swpay', 'bnpay'].includes(settings.adapter_type)
                                                        ? externalAdapterRequisiteTypes(settings.adapter_type, fiatCurrency)
                                                        : settings.requisite_types,
                                                    payment_type_mapping: settings.adapter_type === 'swpay'
                                                        ? withSWPayPaymentMapping(settings.payment_type_mapping, fiatCurrency)
                                                        : settings.payment_type_mapping,
                                                });
                                            }}
                                            disabled={['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'noros', 'sarex', 'gotham'].includes(settings.adapter_type)}
                                        >
                                            <option value="RUB">RUB</option>
                                            <option value="KGS" disabled={settings.adapter_type === 'bnpay'}>KGS</option>
                                            <option value="KZT">KZT</option>
                                        </select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Base URL</Label>
                                        <Input value={settings.base_url} onChange={(e) => setSettings({ ...settings, base_url: e.target.value })} />
                                    </div>
                                </div>

                                <div className="flex items-center justify-between gap-4 rounded-md border bg-slate-50 dark:bg-slate-900 p-3">
                                    <div>
                                        <Label>Доступы к платформе</Label>
                                        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                                            {hasConfiguredCredentials ? 'Настроены и скрыты.' : 'Ещё не настроены.'}
                                        </p>
                                    </div>
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setShowCredentials((visible) => !visible)}
                                    >
                                        {showCredentials ? 'Скрыть' : 'Заменить доступы'}
                                    </Button>
                                </div>

                                {showCredentials && (
                                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                        <div className="space-y-2 md:col-span-2">
                                            <Label>{['youpay', 'youpay_kgs'].includes(settings.adapter_type) ? 'Новый YouPay API key' : settings.adapter_type === 'bnpay' ? 'Новый Bnpay API key' : settings.adapter_type === 'sarex' ? 'Новый Sarex Bearer token' : settings.adapter_type === 'fynzah' ? 'Новый Fynzah Bearer token' : ['swpay', 'swpay_rub'].includes(settings.adapter_type) ? 'Новый SWPay Key Secret' : settings.adapter_type === 'noros' ? 'Новый NOROS api_key' : 'Новый токен платформы'}</Label>
                                            <Input type="password" value={settings.platform_token} placeholder="Оставьте пустым, чтобы не менять" onChange={(e) => setSettings({ ...settings, platform_token: e.target.value })} />
                                        </div>
                                        {!['youpay', 'youpay_kgs'].includes(settings.adapter_type) && (
                                            <div className="space-y-2 md:col-span-2">
                                                <Label>{settings.adapter_type === 'bnpay' ? 'Новый Bnpay Secret' : settings.adapter_type === 'sarex' ? 'Новый Sarex Secret (HMAC)' : settings.adapter_type === 'fynzah' ? 'Новый Fynzah Secret key' : 'Новый webhook secret'}</Label>
                                                <Input type="password" value={settings.webhook_secret} placeholder="Оставьте пустым, чтобы не менять" onChange={(e) => setSettings({ ...settings, webhook_secret: e.target.value })} />
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="space-y-2">
                                    <Label>Webhook URL</Label>
                                    <div className="flex gap-2">
                                        <Input
                                            value={settings.webhook_url}
                                            onChange={(e) => setSettings({ ...settings, webhook_url: e.target.value })}
                                            readOnly={['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(settings.adapter_type)}
                                        />
                                        {['youpay', 'youpay_kgs', 'fynzah', 'swpay_rub', 'bnpay', 'swpay', 'noros', 'sarex', 'gotham'].includes(settings.adapter_type) && settings.webhook_url && (
                                            <Button type="button" variant="outline" onClick={() => navigator.clipboard.writeText(settings.webhook_url)}>
                                                Копировать
                                            </Button>
                                        )}
                                    </div>
                                </div>

                                {showCredentials && ['youpay', 'youpay_kgs'].includes(settings.adapter_type) && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 p-4">
                                        <div className="space-y-2">
                                            <Label>YouPay Merchant ID</Label>
                                            <Input value={settings.external_merchant_id} onChange={(e) => setSettings({ ...settings, external_merchant_id: e.target.value })} />
                                        </div>
                                        <div className="space-y-2">
                                            <Label>YouPay Merchant Login</Label>
                                            <Input value={settings.external_merchant_login} onChange={(e) => setSettings({ ...settings, external_merchant_login: e.target.value })} />
                                        </div>
                                    </div>
                                )}

                                {settings.adapter_type === 'gotham' && (
                                    <div className="space-y-2 rounded-md border p-4">
                                        <Label>Gotham Merchant Name</Label>
                                        <Input value={settings.external_merchant_id} onChange={(e) => setSettings({ ...settings, external_merchant_id: e.target.value })} />
                                        <p className="text-xs text-slate-500">Токен платформы: X-Merchant-Token. Webhook secret: Callback Token.</p>
                                    </div>
                                )}
                                {settings.adapter_type === 'sarex' && (
                                    <div className="space-y-2 rounded-md border p-4">
                                        <Label>Sarex Merchant ID</Label>
                                        <Input value={settings.external_merchant_id} onChange={(e) => setSettings({ ...settings, external_merchant_id: e.target.value })} />
                                        <p className="text-xs text-slate-500">KZT → GEL: KZT_P2P_TG · KZT → TJS: KZT_P2P_TGTJ · только приём платежей</p>
                                    </div>
                                )}
                                {settings.adapter_type === 'bnpay' && (
                                    <div className="space-y-2 rounded-md border p-4">
                                        <Label>Bnpay Shop ID</Label>
                                        <Input value={settings.external_merchant_id} onChange={(e) => setSettings({ ...settings, external_merchant_id: e.target.value })} />
                                    </div>
                                )}
                                {settings.adapter_type === 'fynzah' && (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 rounded-md border border-cyan-200 dark:border-cyan-800 bg-cyan-50 dark:bg-cyan-950 p-4">
                                        {showCredentials && (
                                            <div className="space-y-2">
                                                <Label>Fynzah Merchant ID</Label>
                                                <Input value={settings.external_merchant_id} onChange={(e) => setSettings({ ...settings, external_merchant_id: e.target.value })} />
                                            </div>
                                        )}
                                        <div className="space-y-2">
                                            <Label>Маршруты Fynzah</Label>
                                            <div className="rounded-md border bg-white dark:bg-slate-800 px-3 py-2 text-sm leading-6">
                                                <div><code>qr_vietnam_sber</code> → <code>sber_s</code></div>
                                                <div><code>qr_vietnam_vtb</code> → <code>vtb_s</code></div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {['swpay', 'swpay_rub'].includes(settings.adapter_type) && (
                                    <div className="space-y-4 rounded-md border border-violet-200 bg-violet-50 p-4">
                                        {showCredentials && (
                                            <div className="space-y-2">
                                                <Label>SWPay Key ID</Label>
                                                <Input value={settings.external_key_id} onChange={(e) => setSettings({ ...settings, external_key_id: e.target.value })} />
                                            </div>
                                        )}
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            {settings.adapter_type === 'swpay_rub' && <p className="col-span-full text-sm">Т-Банк → tinkoff · ВТБ → vtb · Сбер → sberbank. Направление закрепляется за профилем SWPay.</p>}
                                            {(settings.adapter_type === 'swpay' ? externalAdapterRequisiteTypes('swpay', settings.fiat_currency) : []).map((type) => (
                                                <div className="space-y-2" key={type}>
                                                    <Label>PaymentMethod: {type}</Label>
                                                    <Input value={(settings.payment_type_mapping as Partial<Record<RequisiteType, string>>)[type] || ''} onChange={(e) => setSettings({ ...settings, payment_type_mapping: { ...settings.payment_type_mapping, [type]: e.target.value } })} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {settings.adapter_type === 'flintpays' && (
                                    <div className="space-y-4 rounded-md border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4">
                                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                            {showCredentials && (
                                                <div className="space-y-2 md:col-span-2">
                                                    <Label>Flint Merchant UUID</Label>
                                                    <Input value={settings.external_merchant_id} onChange={(e) => setSettings({ ...settings, external_merchant_id: e.target.value })} />
                                                </div>
                                            )}
                                            {(['card', 'sbp', 'mobile_kgs'] as const).map((type) => (
                                                <div className="space-y-2" key={type}>
                                                    <Label>payment_type: {type}</Label>
                                                    <Input
                                                        value={settings.payment_type_mapping[type]}
                                                        onChange={(e) => setSettings({
                                                            ...settings,
                                                            payment_type_mapping: { ...settings.payment_type_mapping, [type]: e.target.value },
                                                        })}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                <div className="rounded-md border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950 p-4">
                                    <label className="flex items-center gap-3 text-sm font-medium text-amber-900 dark:text-amber-200">
                                        <Switch
                                            checked={settings.debug_payload_capture}
                                            onCheckedChange={(checked) => setSettings({ ...settings, debug_payload_capture: checked })}
                                        />
                                        Сохранять полный журнал интеграции без срока удаления
                                    </label>
                                    <p className="mt-2 text-xs text-amber-800 dark:text-amber-200">
                                        Настройка применяется только к этой платформе. После выключения новые события журнала не сохраняются.
                                    </p>
                                </div>

                                <RequisiteTypeSelector
                                    value={settings.requisite_types}
                                    onChange={(requisite_types) => setSettings({ ...settings, requisite_types })}
                                    options={externalAdapterRequisiteTypes(settings.adapter_type, settings.fiat_currency)}
                                />
                            </>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Приоритет</Label>
                                <Input type="number" min="0" value={settings.priority} onChange={(e) => setSettings({ ...settings, priority: parseInt(e.target.value || '0', 10) })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Мин. сумма</Label>
                                <Input type="number" min="0" value={settings.min_amount} onChange={(e) => setSettings({ ...settings, min_amount: parseFloat(e.target.value || '0') })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Макс. сумма</Label>
                                <Input type="number" min="1" value={settings.max_amount} onChange={(e) => setSettings({ ...settings, max_amount: parseFloat(e.target.value || '0') })} />
                            </div>
                            <label className="flex items-center gap-2 text-sm">
                                <Switch checked={settings.deposit_enabled} onCheckedChange={(checked) => setSettings({ ...settings, deposit_enabled: checked })} />
                                Deposit
                            </label>
                            <label className="flex items-center gap-2 text-sm">
                                <Switch disabled={['flintpays', 'youpay', 'fynzah', 'swpay_rub', 'bnpay', 'noros', 'sarex', 'gotham'].includes(settings.adapter_type) || (settings.adapter_type === 'swpay' && settings.fiat_currency !== 'KGS')} checked={settings.withdraw_enabled} onCheckedChange={(checked) => setSettings({ ...settings, withdraw_enabled: checked })} />
                                Withdraw
                            </label>
                            <Button onClick={save} disabled={settings.requisite_types.length === 0 || !!commissionError} className="flex gap-2">
                                <Save className="h-4 w-4" /> Сохранить
                            </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-7 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Страховой депозит</Label>
                                <Input type="number" min="0" value={settings.insurance_balance} onChange={(e) => setSettings({ ...settings, insurance_balance: parseFloat(e.target.value || '0') })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Депозит доверия</Label>
                                <Input type="number" min="0" value={settings.trust_deposit} onChange={(e) => setSettings({ ...settings, trust_deposit: parseFloat(e.target.value || '0') })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Ком. ввода (%)</Label>
                                <Input type="number" min="0" max="100" value={settings.deposit_commission_rate} onChange={(e) => setSettings({ ...settings, deposit_commission_rate: parseFloat(e.target.value || '0') })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Ком. вывода (%)</Label>
                                <Input type="number" min="0" max="100" value={settings.withdraw_commission_rate} onChange={(e) => setSettings({ ...settings, withdraw_commission_rate: parseFloat(e.target.value || '0') })} />
                            </div>
                            <div className="space-y-2">
                                <Label>Источник курса</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={settings.rate_source}
                                    onChange={(e) => setSettings({ ...settings, rate_source: e.target.value })}
                                >
                                    {platformRates.map((item) => (
                                        <option key={item.source} value={item.source}>{item.source_label}{item.is_constant ? ' (постоянный)' : ''}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Наценка к курсу (%)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={settings.rate_markup_percent}
                                    onChange={(e) => setSettings({ ...settings, rate_markup_percent: parseFloat(e.target.value || '0') })}
                                />
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Расчетный курс</p>
                                <p className="text-xl font-bold">
                                    {previewRate != null ? `${previewRate.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ${settings.fiat_currency}` : 'Нет данных'}
                                </p>
                                <p className="text-xs text-slate-400">
                                    База: {baseRate != null ? baseRate.toLocaleString('ru-RU', { maximumFractionDigits: 4 }) : '-'} {settings.fiat_currency}
                                    {' '}+ {Number(settings.rate_markup_percent) || 0}%
                                </p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <CommissionTierEditor
                                title="Общая комиссия пополнения по сумме"
                                tiers={settings.deposit_commission_tiers}
                                onChange={(deposit_commission_tiers) => setSettings({ ...settings, deposit_commission_tiers })}
                            />
                            <CommissionTierEditor
                                title="Общая комиссия вывода по сумме"
                                tiers={settings.withdraw_commission_tiers}
                                onChange={(withdraw_commission_tiers) => setSettings({ ...settings, withdraw_commission_tiers })}
                            />
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <MethodCommissionEditors
                                title="Комиссии пополнения по методам"
                                methods={settings.requisite_types}
                                value={settings.deposit_commission_tiers_by_requisite_type}
                                onChange={(deposit_commission_tiers_by_requisite_type) => setSettings({ ...settings, deposit_commission_tiers_by_requisite_type })}
                            />
                            <MethodCommissionEditors
                                title="Комиссии вывода по методам"
                                methods={settings.requisite_types.filter((item: RequisiteType) => !['psb_ecom', 'sbp_qr', 'qr_vietnam_sber', 'qr_vietnam_vtb', 'transgran_card_rub_arm', 'transgran_mobile_rub_arm', 'kzt_transgran_gel', 'kzt_transgran_idr', 'kzt_transgran_tjs', 'kzt_transgran_uzs', 'elqr'].includes(item))}
                                value={settings.withdraw_commission_tiers_by_requisite_type}
                                onChange={(withdraw_commission_tiers_by_requisite_type) => setSettings({ ...settings, withdraw_commission_tiers_by_requisite_type })}
                            />
                        </div>

                    </CardContent>
                </fieldset>
            )}
        </Card>
    );
};

export const TraderCard: React.FC<{
    trader: any,
    onToggle: () => void,
    onUpdate: (settings: any) => void,
    onOpenDashboard: () => void,
    onOpenSettlements: () => void,
    isOpeningDashboard: boolean,
    dashboardError: string | null,
    isToggling: boolean,
    currencyRates: AdminCurrencyRate[],
    readOnly: boolean,
}> = ({ trader, onToggle, onUpdate, onOpenDashboard, onOpenSettlements, isOpeningDashboard, dashboardError, isToggling, currencyRates, readOnly }) => {
    const fiatCurrency: 'RUB' | 'KGS' = trader.fiat_currency || 'RUB';
    const [isExpanded, setIsExpanded] = useState(false);
    const [settings, setSettings] = useState({
        deposit_method: trader.settings?.deposit_method || "wallet",
        insurance_balance: trader.settings?.insurance_balance || 0,
        trust_deposit: trader.settings?.trust_deposit || 0,
        deposit_commission_rate: trader.settings?.deposit_commission_rate || 0,
        withdraw_commission_rate: trader.settings?.withdraw_commission_rate || 0,
        deposit_commission_tiers: normalizeCommissionTiers(trader.settings?.deposit_commission_tiers),
        withdraw_commission_tiers: normalizeCommissionTiers(trader.settings?.withdraw_commission_tiers),
        deposit_commission_tiers_by_requisite_type: normalizeCommissionTierMap(trader.settings?.deposit_commission_tiers_by_requisite_type),
        withdraw_commission_tiers_by_requisite_type: normalizeCommissionTierMap(trader.settings?.withdraw_commission_tiers_by_requisite_type),
        rate_source: trader.settings?.rate_source?.startsWith('FIXED_') ? trader.settings.rate_source : (fiatCurrency === 'KGS' ? 'BYBIT_P2P_API' : (trader.settings?.rate_source ?? 'RAPIRA_P2P_API')),
        rate_markup_percent: trader.settings?.rate_markup_percent ?? 6,
    });
    const commissionError = validateCommissionTiers(settings.deposit_commission_tiers)
        || validateCommissionTiers(settings.withdraw_commission_tiers)
        || validateCommissionTierMap(settings.deposit_commission_tiers_by_requisite_type)
        || validateCommissionTierMap(settings.withdraw_commission_tiers_by_requisite_type);

    const volumeStats = trader.volume_stats || {};
    const traderRates = currencyRates.filter((item) => item.from_currency === fiatCurrency);
    const selectedRate = traderRates.find((item) => item.source === settings.rate_source);
    const baseRate = selectedRate?.base_rate ?? selectedRate?.rate ?? null;
    const previewRate = baseRate != null
        ? baseRate * (1 + (Number(settings.rate_markup_percent) || 0) / 100)
        : null;

    return (
        <div>
            <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <div>
                        <CardTitle className="text-lg">{trader.username}</CardTitle>
                        <CardDescription className="flex items-center gap-2 flex-wrap">
                            {trader.email}
                            <Badge variant="outline">{fiatCurrency} / USDT</Badge>
                            {trader.referral_code && (
                                <Badge variant="outline" className="text-xs bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-800">
                                    Реф: {trader.referral_code}
                                </Badge>
                            )}
                            {trader.created_at && (
                                <span className="flex items-center gap-1 text-xs text-slate-400" title="Дата регистрации">
                                    <CalendarDays className="h-3 w-3" />
                                    {new Date(trader.created_at).toLocaleString('ru-RU')}
                                </span>
                            )}
                        </CardDescription>
                    </div>
                    <div className="flex items-center gap-4">
                        <Button type="button" variant="outline" size="sm"
                            onClick={() => setIsExpanded(expanded => !expanded)} aria-expanded={isExpanded}
                            aria-label={isExpanded ? 'Скрыть информацию о трейдере' : 'Показать информацию о трейдере'}>
                            {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        </Button>
                        <Badge variant={trader.is_active ? "success" : "destructive"}>
                            {trader.is_active ? "Активен" : "Отключен"}
                        </Badge>
                        <Switch checked={trader.is_active} onCheckedChange={onToggle} disabled={readOnly || isToggling} />
                    </div>
                </CardHeader>
                {isExpanded && <fieldset disabled={readOnly} className={readOnly ? 'opacity-90' : undefined}>
                    <CardContent>
                        <div className="flex flex-wrap gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onOpenDashboard}
                                disabled={isOpeningDashboard}
                                className="flex gap-2"
                            >
                                <LayoutDashboard className="h-4 w-4" />
                                Дашборд
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={onOpenSettlements}
                                className="flex gap-2"
                            >
                                <DollarSign className="h-4 w-4" />
                                Взаиморасчеты
                            </Button>
                        </div>
                        {dashboardError && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{dashboardError}</p>}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Баланс (USDT)</p>
                                <p className="text-2xl font-bold">{trader.settings?.balance?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="space-y-2 text-green-600 dark:text-green-300">
                                <p className="text-sm font-medium opacity-80">Заработано за сегодня</p>
                                <p className="text-2xl font-bold">{trader.settings?.today_commission_earned?.toFixed(2) || '0.00'}</p>
                            </div>
                            <div className="space-y-2 text-slate-600 dark:text-slate-300">
                                <p className="text-sm font-medium opacity-80">Всего заработано</p>
                                <p className="text-2xl font-bold">{trader.settings?.total_commission_earned?.toFixed(2) || '0.00'}</p>
                            </div>
                        </div>

                        {/* Статистика объёмов */}
                        <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-3">Объёмы операций</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-green-600 dark:text-green-300 mb-1">
                                        <ArrowDownCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Ввод сегодня</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.deposit_today?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-green-600 dark:text-green-300 mb-1">
                                        <ArrowDownCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Ввод за месяц</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.deposit_month?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-orange-600 dark:text-orange-300 mb-1">
                                        <ArrowUpCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Вывод сегодня</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.withdraw_today?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-orange-600 dark:text-orange-300 mb-1">
                                        <ArrowUpCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Вывод за месяц</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.withdraw_month?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 flex items-center gap-3">
                            <Switch checked={settings.deposit_method === 'shared'}
                                onCheckedChange={(enabled) => setSettings({ ...settings, deposit_method: enabled ? 'shared' : 'wallet' })}
                                disabled={readOnly} />
                            <div>
                                <Label>Пополнение на общий кошелёк без комиссии</Label>
                                <p className="text-xs text-slate-500">Применяется к новым заявкам после сохранения настроек. Случайная сумма, срок оплаты 30 минут.</p>
                            </div>
                        </div>
                        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Страховой депозит (USDT)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={settings.insurance_balance}
                                    onChange={(e) => setSettings({ ...settings, insurance_balance: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Депозит доверия (USDT)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    value={settings.trust_deposit}
                                    onChange={(e) => setSettings({ ...settings, trust_deposit: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ком. пополнения (%)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={settings.deposit_commission_rate}
                                    onChange={(e) => setSettings({ ...settings, deposit_commission_rate: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Ком. вывода (%)</Label>
                                <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={settings.withdraw_commission_rate}
                                    onChange={(e) => setSettings({ ...settings, withdraw_commission_rate: parseFloat(e.target.value) })}
                                />
                            </div>
                            <Button onClick={() => onUpdate({
                                ...settings,
                                deposit_commission_tiers: normalizeCommissionTiers(settings.deposit_commission_tiers),
                                withdraw_commission_tiers: normalizeCommissionTiers(settings.withdraw_commission_tiers),
                                deposit_commission_tiers_by_requisite_type: normalizeCommissionTierMap(settings.deposit_commission_tiers_by_requisite_type),
                                withdraw_commission_tiers_by_requisite_type: normalizeCommissionTierMap(settings.withdraw_commission_tiers_by_requisite_type),
                            })} disabled={!!commissionError} className="flex gap-2">
                                <Save className="h-4 w-4" /> Сохранить
                            </Button>
                        </div>
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <CommissionTierEditor
                                title="Общая комиссия пополнения по сумме"
                                tiers={settings.deposit_commission_tiers}
                                onChange={(deposit_commission_tiers) => setSettings({ ...settings, deposit_commission_tiers })}
                            />
                            <CommissionTierEditor
                                title="Общая комиссия вывода по сумме"
                                tiers={settings.withdraw_commission_tiers}
                                onChange={(withdraw_commission_tiers) => setSettings({ ...settings, withdraw_commission_tiers })}
                            />
                        </div>
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <MethodCommissionEditors
                                title="Комиссии пополнения по методам"
                                methods={traderCommissionMethods(fiatCurrency, 'deposit')}
                                value={settings.deposit_commission_tiers_by_requisite_type}
                                onChange={(deposit_commission_tiers_by_requisite_type) => setSettings({ ...settings, deposit_commission_tiers_by_requisite_type })}
                                methodLabel={(method) => traderCommissionMethodLabel(method, fiatCurrency)}
                            />
                            <MethodCommissionEditors
                                title="Комиссии вывода по методам"
                                methods={traderCommissionMethods(fiatCurrency, 'withdraw')}
                                value={settings.withdraw_commission_tiers_by_requisite_type}
                                onChange={(withdraw_commission_tiers_by_requisite_type) => setSettings({ ...settings, withdraw_commission_tiers_by_requisite_type })}
                                methodLabel={(method) => traderCommissionMethodLabel(method, fiatCurrency)}
                            />
                        </div>

                        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Источник курса</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                                    value={settings.rate_source}
                                    onChange={(e) => setSettings({ ...settings, rate_source: e.target.value })}
                                >
                                    {traderRates.map((item) => (
                                        <option key={item.source} value={item.source}>{item.source_label}{item.is_constant ? ' (постоянный)' : ''}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Наценка к курсу (%)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={settings.rate_markup_percent}
                                    onChange={(e) => setSettings({ ...settings, rate_markup_percent: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-1">
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Расчетный курс</p>
                                <p className="text-xl font-bold">
                                    {previewRate != null ? `${previewRate.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ${fiatCurrency}` : 'Нет данных'}
                                </p>
                            </div>
                            <Button onClick={() => onUpdate(settings)} className="flex gap-2">
                                <Save className="h-4 w-4" /> Сохранить курс
                            </Button>
                        </div>
                    </CardContent>
                </fieldset>}
            </Card>
        </div>
    );
};

const RequisiteTypeSelector: React.FC<{
    value: RequisiteType[];
    onChange: (value: RequisiteType[]) => void;
    options?: RequisiteType[];
    allowEmpty?: boolean;
    emptyMessage?: string;
}> = ({ value, onChange, options: enabledOptions, allowEmpty = false, emptyMessage }) => {
    const allOptions: Array<{ value: RequisiteType; label: string }> = [
        { value: 'card', label: 'Card' },
        { value: 'sbp', label: 'СБП' },
        { value: 'sbp_kgs', label: 'СБП KGS' },
        { value: 'sbp_qr', label: 'СБП QR' },
        { value: 'mobile_kgs', label: 'Mobile' },
        { value: 'mobile_tjs', label: 'Mobile Commerce TJS' },
        { value: 'qr_vietnam_sber', label: 'QR Vietnam — Sber' },
        { value: 'qr_vietnam_vtb', label: 'QR Vietnam — VTB' },
        { value: 'psb_ecom', label: 'PSB E-com' },
        { value: 'rub_transgran_uzs_emission_tbank', label: 'rub_transgran_uzs_emission_tbank' },
        { value: 'rub_transgran_uzs_emission_vtb', label: 'rub_transgran_uzs_emission_vtb' },
        { value: 'rub_transgran_uzs_emission_sber', label: 'rub_transgran_uzs_emission_sber' },
        { value: 'transgran_card_rub_arm', label: 'transgran_card_rub_arm' },
        { value: 'transgran_mobile_rub_arm', label: 'transgran_mobile_rub_arm' },
        { value: 'kzt_transgran_gel', label: 'kzt_transgran_gel' },
        { value: 'kzt_transgran_idr', label: 'kzt_transgran_idr' },
        { value: 'kzt_transgran_tjs', label: 'kzt_transgran_tjs' },
        { value: 'kzt_transgran_uzs', label: 'kzt_transgran_uzs' },
        { value: 'elqr', label: 'ElQR' },
    ];
    const options = enabledOptions === undefined
        ? allOptions
        : allOptions.filter((option) => enabledOptions.includes(option.value));
    const toggle = (type: RequisiteType, checked: boolean) => {
        const visibleValue = enabledOptions === undefined
            ? value
            : value.filter((item) => enabledOptions.includes(item));
        const next = checked
            ? Array.from(new Set([...visibleValue, type]))
            : visibleValue.filter(item => item !== type);
        onChange(next);
    };
    return (
        <div className="space-y-2">
            <Label>Доступные методы</Label>
            <div className="flex flex-wrap gap-4 rounded-md border p-3">
                {options.length > 0 ? options.map(option => (
                    <label key={option.value} className="flex items-center gap-2 text-sm">
                        <input
                            type="checkbox"
                            checked={value.includes(option.value)}
                            onChange={(event) => toggle(option.value, event.target.checked)}
                        />
                        {option.label}
                    </label>
                )) : <span className="text-sm text-slate-500 dark:text-slate-400">Доступных методов пока нет</span>}
            </div>
            {value.length === 0 && (
                <p className={`text-xs ${allowEmpty ? 'text-slate-500 dark:text-slate-400' : 'text-red-600 dark:text-red-300'}`}>
                    {emptyMessage || (allowEmpty ? 'Список методов может быть пустым' : 'Выберите хотя бы один метод')}
                </p>
            )}
        </div>
    );
};

const MerchantCard: React.FC<{
    merchant: any,
    onOpenDashboard: () => void,
    isOpeningDashboard: boolean,
    dashboardError: string | null,
    onToggle: () => void,
    onUpdate: (settings: any) => void,
    onDirectionChange: (field: MerchantDirectionField, enabled: boolean) => Promise<void>,
    onApiVisibilityChange: (hidden: boolean) => Promise<void>,
    isToggling: boolean,
    exchangeRate: number | null,
    currencyRates: AdminCurrencyRate[],
    currencyCapabilities: Record<MerchantFiatCurrency, RequisiteType[]>,
    readOnly: boolean,
}> = ({ merchant, onOpenDashboard, isOpeningDashboard, dashboardError, onToggle, onUpdate, onDirectionChange, onApiVisibilityChange, isToggling, exchangeRate, currencyRates, currencyCapabilities, readOnly }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [routingEnabled, setRoutingEnabled] = useState(Boolean(merchant.routing_template_id) || merchant.routing_policy?.enabled === true);
    useEffect(() => {
        setRoutingEnabled(Boolean(merchant.routing_template_id) || merchant.routing_policy?.enabled === true);
    }, [merchant.routing_template_id, merchant.routing_policy?.enabled]);
    const fiatCurrency = merchant.fiat_currency || (merchant.integration_profile === 'typical_v2_2' ? 'KGS' : 'RUB');
    const isTypical = merchant.integration_profile === 'typical_v2_2';
    const currencyMethods = currencyCapabilities[fiatCurrency as MerchantFiatCurrency] || [];
    const [settings, setSettings] = useState({
        deposit_amount_adjustments: (merchant.deposit_amount_adjustments || {}) as AmountAdjustmentRules,
        auto_unique_deposit_amount: merchant.auto_unique_deposit_amount ?? false,
        reject_deposit_on_amount_cooldown: merchant.reject_deposit_on_amount_cooldown ?? false,
        deposit_commission_rate: merchant.deposit_commission_rate ?? 10,
        withdraw_commission_rate: merchant.withdraw_commission_rate ?? 10,
        deposit_commission_tiers: normalizeCommissionTiers(merchant.deposit_commission_tiers),
        withdraw_commission_tiers: normalizeCommissionTiers(merchant.withdraw_commission_tiers),
        deposit_commission_tiers_by_requisite_type: normalizeCommissionTierMap(merchant.deposit_commission_tiers_by_requisite_type),
        withdraw_commission_tiers_by_requisite_type: normalizeCommissionTierMap(merchant.withdraw_commission_tiers_by_requisite_type),
        deposit_min_amount: (merchant.deposit_min_amount ?? null) as number | null,
        deposit_max_amount: (merchant.deposit_max_amount ?? null) as number | null,
        withdraw_min_amount: (merchant.withdraw_min_amount ?? null) as number | null,
        withdraw_max_amount: (merchant.withdraw_max_amount ?? null) as number | null,
        rate_source: merchant.rate_source ?? (fiatCurrency === 'RUB' ? 'RAPIRA_P2P_API' : 'BYBIT_P2P_API'),
        rate_markup_percent: fiatCurrency === 'KZT' ? 0 : (merchant.rate_markup_percent ?? 6),
        requisite_types: (merchant.requisite_types ?? (isTypical ? [] : ['card', 'sbp'])) as RequisiteType[],
        payment_form_mode: merchant.payment_form_mode ?? 'legacy',
    });

    const merchantRates = currencyRates.filter((item) => item.from_currency === fiatCurrency);
    const volumeStats = merchant.volume_stats || {};
    const selectedRate = merchantRates.find((item) => item.source === settings.rate_source);
    const merchantBaseRate = selectedRate?.base_rate ?? selectedRate?.rate ?? null;
    const merchantPreviewRate = merchantBaseRate != null
        ? merchantBaseRate * (1 + (Number(settings.rate_markup_percent) || 0) / 100)
        : null;
    const rate = fiatCurrency === 'RUB' ? (exchangeRate || merchantPreviewRate || 100) : (merchantPreviewRate || 0);
    const commissionError = validateAmountAdjustments(settings.deposit_amount_adjustments)
        || validateCommissionTiers(settings.deposit_commission_tiers)
        || validateCommissionTiers(settings.withdraw_commission_tiers)
        || validateCommissionTierMap(settings.deposit_commission_tiers_by_requisite_type)
        || validateCommissionTierMap(settings.withdraw_commission_tiers_by_requisite_type)
        || validateOptionalAmountRange(settings.deposit_min_amount, settings.deposit_max_amount, 'пополнения')
        || validateOptionalAmountRange(settings.withdraw_min_amount, settings.withdraw_max_amount, 'вывода')
        || validateMethodCommissionTiersWithinAmountRange(settings.deposit_commission_tiers_by_requisite_type, settings.deposit_min_amount, settings.deposit_max_amount, 'пополнения')
        || validateMethodCommissionTiersWithinAmountRange(settings.withdraw_commission_tiers_by_requisite_type, settings.withdraw_min_amount, settings.withdraw_max_amount, 'вывода');

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
                <div>
                    <CardTitle className="text-lg">{merchant.username}</CardTitle>
                    <CardDescription className="flex items-center gap-2 flex-wrap">
                        {merchant.email}
                        <Badge variant="outline">{fiatCurrency}</Badge>
                        {merchant.company_name && (
                            <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 border-blue-300 dark:border-blue-800">
                                {merchant.company_name}
                            </Badge>
                        )}
                        {merchant.created_at && (
                            <span className="flex items-center gap-1 text-xs text-slate-400" title="Дата регистрации">
                                <CalendarDays className="h-3 w-3" />
                                {new Date(merchant.created_at).toLocaleString('ru-RU')}
                            </span>
                        )}
                    </CardDescription>
                    <p className="text-xs text-slate-400 mt-1">ID: {merchant._id}</p>
                </div>
                <div className="flex items-center gap-4">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setIsExpanded((expanded) => !expanded)}
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? 'Скрыть информацию о мерчанте' : 'Показать информацию о мерчанте'}
                        title={isExpanded ? 'Скрыть информацию' : 'Показать информацию'}
                    >
                        {isExpanded
                            ? <ChevronUp className="h-5 w-5 text-slate-500 dark:text-slate-400" />
                            : <ChevronDown className="h-5 w-5 text-slate-500 dark:text-slate-400" />}
                    </Button>
                    {!readOnly && <Button type="button" variant="outline" size="sm" onClick={onOpenDashboard} disabled={isOpeningDashboard}>
                        <LayoutDashboard className="h-4 w-4 mr-2" />
                        Дашборд
                    </Button>}
                    <Badge variant={merchant.is_active ? "success" : "destructive"}>
                        {merchant.is_active ? "Активен" : "Отключен"}
                    </Badge>
                    <Switch checked={merchant.is_active} onCheckedChange={onToggle} disabled={readOnly || isToggling} />
                </div>
            </CardHeader>
            {dashboardError && <p role="alert" className="px-6 pb-3 text-sm text-red-600 dark:text-red-400">{dashboardError}</p>}
            <MerchantDirectionSwitches depositEnabled={merchant.deposit_enabled ?? true}
                withdrawEnabled={merchant.withdraw_enabled ?? true} readOnly={readOnly}
                onChange={onDirectionChange} />
            <MerchantApiVisibilitySwitch hidden={merchant.hide_api_keys ?? false} readOnly={readOnly} onChange={onApiVisibilityChange} />
            {!readOnly && <MerchantApiKeysDownload merchant={merchant} />}
            {isExpanded && <MerchantAdvancePanel merchantId={merchant._id} canManage={!readOnly} />}
            <MerchantRoutingPanel merchantId={merchant._id} initialEnabled={routingEnabled}
                readOnly={readOnly} onEnabledChange={setRoutingEnabled}
                initialTemplate={merchant.routing_template_id ? { id: merchant.routing_template_id, name: merchant.routing_template_name || merchant.routing_template_id } : null} />
            {isExpanded && (
                <fieldset disabled={readOnly} className={readOnly ? 'opacity-90' : undefined}>
                    <CardContent>
                        {/* Баланс мерчанта: USDT и его fiat-эквивалент */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                            <div className="space-y-2">
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Баланс</p>
                                <p className="text-2xl font-bold">
                                    {((getMerchantAvailableBalance(merchant)) * rate).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} {fiatCurrency}
                                </p>
                                <p className="text-xs text-slate-400">
                                    {(getMerchantAvailableBalance(merchant)).toLocaleString('ru-RU', { maximumFractionDigits: 6 })} USDT
                                </p>
                            </div>
                            <div className="space-y-2 text-green-600 dark:text-green-300">
                                <p className="text-sm font-medium opacity-80">Пополнения</p>
                                <p className="text-2xl font-bold">{((merchant.deposit_balance || 0) * rate).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} {fiatCurrency}</p>
                                <p className="text-xs opacity-60">{(merchant.deposit_balance || 0).toLocaleString('ru-RU', { maximumFractionDigits: 6 })} USDT</p>
                            </div>
                            <div className="space-y-2 text-orange-600 dark:text-orange-300">
                                <p className="text-sm font-medium opacity-80">Выводы</p>
                                <p className="text-2xl font-bold">{((merchant.withdraw_balance || 0) * rate).toLocaleString('ru-RU', { maximumFractionDigits: 2 })} {fiatCurrency}</p>
                                <p className="text-xs opacity-60">{(merchant.withdraw_balance || 0).toLocaleString('ru-RU', { maximumFractionDigits: 6 })} USDT</p>
                            </div>
                        </div>

                        {/* Статистика объёмов */}
                        <div className="mt-4 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg">
                            <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-3">Объёмы операций</p>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-green-600 dark:text-green-300 mb-1">
                                        <ArrowDownCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Ввод сегодня</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.deposit_today?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-green-600 dark:text-green-300 mb-1">
                                        <ArrowDownCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Ввод за месяц</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.deposit_month?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-orange-600 dark:text-orange-300 mb-1">
                                        <ArrowUpCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Вывод сегодня</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.withdraw_today?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                                <div className="text-center">
                                    <div className="flex items-center justify-center gap-1 text-orange-600 dark:text-orange-300 mb-1">
                                        <ArrowUpCircle className="h-4 w-4" />
                                        <span className="text-xs font-medium">Вывод за месяц</span>
                                    </div>
                                    <p className="text-sm font-bold">{volumeStats.withdraw_month?.toLocaleString() || 0} {fiatCurrency}</p>
                                </div>
                            </div>
                        </div>

                        {/* Комиссии */}
                        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700">
                            {routingEnabled && <p className="mb-2 text-sm text-slate-500 dark:text-slate-400">Для изменения методов сначала отключите распределение.</p>}
                            <fieldset disabled={routingEnabled}>
                                <RequisiteTypeSelector
                                    value={settings.requisite_types}
                                    onChange={(requisite_types) => setSettings({ ...settings, requisite_types })}
                                    options={currencyMethods}
                                    allowEmpty={currencyMethods.length === 0}
                                    emptyMessage={currencyMethods.length === 0 ? 'Для этой валюты методы пока не настроены' : undefined}
                                />
                            </fieldset>
                        </div>

                        <div className="mt-4 rounded-md border bg-slate-50 dark:bg-slate-900 p-4">
                            <Label className="text-sm font-semibold">Индивидуальные лимиты ({fiatCurrency})</Label>
                            <p className="mb-3 mt-1 text-xs text-slate-500 dark:text-slate-400">Пустая пара использует глобальные лимиты Merchant API.</p>
                            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                {([
                                    ['deposit_min_amount', 'Пополнение от'],
                                    ['deposit_max_amount', 'Пополнение до'],
                                    ['withdraw_min_amount', 'Вывод от'],
                                    ['withdraw_max_amount', 'Вывод до'],
                                ] as const).map(([field, label]) => (
                                    <div key={field} className="space-y-1">
                                        <Label className="text-xs">{label}</Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            value={settings[field] ?? ''}
                                            onChange={(e) => setSettings({
                                                ...settings,
                                                [field]: e.target.value === '' ? null : parseFloat(e.target.value),
                                            })}
                                        />
                                    </div>
                                ))}
                            </div>
                            {(validateOptionalAmountRange(settings.deposit_min_amount, settings.deposit_max_amount, 'пополнения')
                                || validateOptionalAmountRange(settings.withdraw_min_amount, settings.withdraw_max_amount, 'вывода')) && (
                                    <p className="mt-2 text-xs text-red-600 dark:text-red-300">
                                        {validateOptionalAmountRange(settings.deposit_min_amount, settings.deposit_max_amount, 'пополнения')
                                            || validateOptionalAmountRange(settings.withdraw_min_amount, settings.withdraw_max_amount, 'вывода')}
                                    </p>
                                )}
                        </div>

                        <div className="mt-4 rounded-md border p-4">
                            <Label htmlFor={`payment-form-mode-${merchant._id}`} className="text-sm font-semibold">Форма оплаты</Label>
                            <p className="mb-3 mt-1 text-xs text-slate-500">
                                Режим применяется к новым пополнениям.
                            </p>
                            <select
                                id={`payment-form-mode-${merchant._id}`}
                                className="flex h-10 w-full max-w-md rounded-md border border-input bg-background px-3 py-2 text-sm"
                                value={settings.payment_form_mode}
                                disabled={readOnly}
                                onChange={(event) => setSettings({ ...settings, payment_form_mode: event.target.value })}
                            >
                                <option value="legacy">Текущая форма и действующие эксперименты</option>
                                <option value="standard">Стандартная форма</option>
                                <option value="branded" disabled={!merchant.payment_form_theme_available}>Брендированная форма Melbet</option>
                                <option value="ab_test" disabled={!merchant.payment_form_theme_available}>A/B: стандартная или Melbet (50/50)</option>
                            </select>
                            {!merchant.payment_form_theme_available && (
                                <p className="mt-2 text-xs text-slate-500">Тема Melbet пока недоступна.</p>
                            )}
                        </div>

                        {fiatCurrency === 'KZT' && <MerchantAmountAdjustments
                            methods={settings.requisite_types.filter(method => method.startsWith('kzt_transgran_'))}
                            value={settings.deposit_amount_adjustments}
                            onChange={deposit_amount_adjustments => setSettings({ ...settings, deposit_amount_adjustments })}
                        />}

                        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700 grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Комиссия пополнения (%)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={settings.deposit_commission_rate}
                                    onChange={(e) => setSettings({ ...settings, deposit_commission_rate: parseFloat(e.target.value) })}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Комиссия вывода (%)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    max="100"
                                    value={settings.withdraw_commission_rate}
                                    onChange={(e) => setSettings({ ...settings, withdraw_commission_rate: parseFloat(e.target.value) })}
                                />
                            </div>
                        </div>
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <CommissionTierEditor
                                title="Общая комиссия пополнения по сумме"
                                tiers={settings.deposit_commission_tiers}
                                onChange={(deposit_commission_tiers) => setSettings({ ...settings, deposit_commission_tiers })}
                            />
                            <CommissionTierEditor
                                title="Общая комиссия вывода по сумме"
                                tiers={settings.withdraw_commission_tiers}
                                onChange={(withdraw_commission_tiers) => setSettings({ ...settings, withdraw_commission_tiers })}
                            />
                        </div>
                        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <MethodCommissionEditors
                                title="Комиссии пополнения по методам"
                                methods={settings.requisite_types}
                                value={settings.deposit_commission_tiers_by_requisite_type}
                                onChange={(deposit_commission_tiers_by_requisite_type) => setSettings({ ...settings, deposit_commission_tiers_by_requisite_type })}
                                minAmount={settings.deposit_min_amount}
                                maxAmount={settings.deposit_max_amount}
                                operationLabel="пополнения"
                            />
                            <MethodCommissionEditors
                                title="Комиссии вывода по методам"
                                methods={settings.requisite_types.filter((item: RequisiteType) => !['psb_ecom', 'sbp_qr', 'qr_vietnam_sber', 'qr_vietnam_vtb', 'transgran_card_rub_arm', 'transgran_mobile_rub_arm', 'kzt_transgran_gel', 'kzt_transgran_idr', 'kzt_transgran_tjs', 'kzt_transgran_uzs', 'elqr'].includes(item))}
                                value={settings.withdraw_commission_tiers_by_requisite_type}
                                onChange={(withdraw_commission_tiers_by_requisite_type) => setSettings({ ...settings, withdraw_commission_tiers_by_requisite_type })}
                                minAmount={settings.withdraw_min_amount}
                                maxAmount={settings.withdraw_max_amount}
                                operationLabel="вывода"
                            />
                        </div>

                        <div className="mt-6 pt-6 border-t border-slate-100 dark:border-slate-700 grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
                            <div className="space-y-2">
                                <Label>Источник курса</Label>
                                <select
                                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                                    value={settings.rate_source}
                                    onChange={(e) => setSettings({ ...settings, rate_source: e.target.value })}
                                >
                                    {merchantRates.map((item) => (
                                        <option key={item.source} value={item.source}>
                                            {item.source_label}{item.is_constant ? ' (постоянный)' : ''}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Наценка к курсу (%)</Label>
                                <Input
                                    type="number"
                                    step="0.1"
                                    min="0"
                                    value={settings.rate_markup_percent}
                                    onChange={(e) => setSettings({ ...settings, rate_markup_percent: parseFloat(e.target.value) })}
                                    disabled={fiatCurrency === 'KZT'}
                                />
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <p className="text-sm font-medium text-slate-500 dark:text-slate-400">Расчетный курс мерчанта</p>
                                <p className="text-xl font-bold">
                                    {merchantPreviewRate != null
                                        ? `${merchantPreviewRate.toLocaleString('ru-RU', { maximumFractionDigits: 4 })} ${fiatCurrency}`
                                        : 'Нет данных'}
                                </p>
                                <p className="text-xs text-slate-400">
                                    База: {merchantBaseRate != null ? merchantBaseRate.toLocaleString('ru-RU', { maximumFractionDigits: 4 }) : '-'} {fiatCurrency}
                                    {' '}+ {Number(settings.rate_markup_percent) || 0}%
                                </p>
                            </div>
                            {fiatCurrency === 'KGS' && (
                                <div className="md:col-span-2 space-y-2">
                                    <Label className="flex items-center gap-3">
                                        <Switch checked={settings.auto_unique_deposit_amount}
                                            disabled={readOnly}
                                            onCheckedChange={(checked) => setSettings({ ...settings, auto_unique_deposit_amount: checked })} />
                                        Подбирать свободную сумму (+1 KGS, до +10 KGS)
                                    </Label>
                                    <p className="text-xs text-slate-500">Для live-депозитов API v2: сначала проверяем всех исполнителей на текущей сумме. Расчёты ведутся от выданной суммы. Если свободной суммы нет, API вернёт 404.</p>
                                    <Label className="flex items-center gap-3">
                                        <Switch checked={settings.reject_deposit_on_amount_cooldown}
                                            disabled={readOnly}
                                            onCheckedChange={(checked) => setSettings({ ...settings, reject_deposit_on_amount_cooldown: checked })} />
                                        Не выдавать реквизиты при защитной блокировке суммы
                                    </Label>
                                    <p className="text-xs text-slate-500">При блокировке суммы проверяем другие счета и маршруты. Если доступных нет, API вернёт 409 для повтора с другой суммой.</p>
                                </div>
                            )}
                            <Button onClick={() => onUpdate({
                                ...settings,
                                deposit_amount_adjustments: fiatCurrency === 'KZT' ? settings.deposit_amount_adjustments : undefined,
                                rate_source: settings.rate_source,
                                rate_markup_percent: fiatCurrency === 'KZT' ? 0 : settings.rate_markup_percent,
                                requisite_types: settings.requisite_types.filter((item) => currencyMethods.includes(item)),
                                deposit_commission_tiers: normalizeCommissionTiers(settings.deposit_commission_tiers),
                                withdraw_commission_tiers: normalizeCommissionTiers(settings.withdraw_commission_tiers),
                                deposit_commission_tiers_by_requisite_type: normalizeCommissionTierMap(settings.deposit_commission_tiers_by_requisite_type),
                                withdraw_commission_tiers_by_requisite_type: normalizeCommissionTierMap(settings.withdraw_commission_tiers_by_requisite_type),
                            })} disabled={(settings.requisite_types.length === 0 && currencyMethods.length > 0) || !!commissionError} className="flex gap-2">
                                <Save className="h-4 w-4" /> Сохранить
                            </Button>
                        </div>
                    </CardContent>
                </fieldset>
            )}
        </Card>
    );
};
